import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from 'react-dom';
import { localInvoiceTargets } from './backend/bankStatementDraft';
import { commitLocalBankStatementFinalize } from './backend/localBankStatementFinalize';
import { commitLocalBankStatementManual } from './backend/localBankStatementManual';
import { accountingGroupFor, availableAccountingGroups } from "./accountingDestinations";
import {
  ArchiveIcon,
  CalendarIcon,
  ChatIcon,
  ChartIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CreditCardIcon,
  DocumentIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  EyeOffIcon,
  ExportIcon,
  HomeIcon,
  LogOutIcon,
  MoreIcon,
  NotificationsIcon,
  OfficeIcon,
  PeopleIcon,
  PersonIcon,
  PlusIcon,
  RepeatIcon,
  SavedIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  SwapHorizontalIcon,
  TimeIcon,
  TickCircleIcon,
  WarningSignIcon,
} from "evergreen-ui";
import { ACTIVITY_LOG, BUSINESSES, CLIENTS, INVOICES, LOGIN_ACCOUNTS, SAMPLE_COLLECTION_HISTORY, SAMPLE_WORKSPACE_VERSION, SERVICES, SETTINGS_ITEMS, TEAM_MEMBERS, USER_PROFILE, WORKSPACES, WORK_ITEM_ACTIVITIES, WORK_REMINDERS, WORK_TASKS, upgradeSampleWorkspace } from "./data";
import { exportBackup, importBackup, readLocalData } from "./dataPortability";
import { openLocalSnapshotSession } from "./localSnapshotFreshness";
import { useLocalSnapshotPersistence } from "./features/useLocalSnapshotPersistence";
import LocalSnapshotSaveStatus from "./features/LocalSnapshotSaveStatus";
import { formatCurrency, formatDocumentDate, invoiceNumber, nextInvoiceNumber, parseCurrency, paymentTermsLabel } from "./invoiceUtils";
import { formatServiceRate } from "./serviceCatalog";
import { AvatarImage, Brand, CustomSelect, IconButton, ViewHeading } from "./components/SharedUI";
import { getVisualViewportBounds, placeFloatingPanel } from "./components/floatingPosition";
import InvoiceTable from "./components/InvoiceTable";
import InvoiceLedgerSwitch from "./components/InvoiceLedgerSwitch";
import InvoiceStatusControl from "./components/InvoiceStatusControl";
import LoginPage from "./features/LoginPage";
import LocalWorkspaceOnboarding from "./features/LocalWorkspaceOnboarding";
import ImportedPaymentReviewView from "./features/ImportedPaymentReviewView";
import IssuerPanel from "./features/IssuerPanel";
import WhatsAppComposer from "./features/WhatsAppComposer";
import { NotificationPanel, NotificationsView, OrganizationPanel, SearchPanel, SearchView, UserMenu } from "./features/WorkspaceOverlays";
import { PrivacyProvider, usePrivacy } from "./privacy/PrivacyContext";
import { clientScopeLabel, findClientConflict, resolveClient } from "./clientAccess";
import { documentLabels, formatLocalizedDocumentCurrency, formatLocalizedDocumentDate } from "./documentPreferences";
import { contactForPurpose, primaryContactFor } from "./contactUtils";
import { accessDeniedMessage, canAccessBusiness, canAccessClient, canAccessInvoice, canManageOrganization, canUseAccountingOperation } from "./accessControl";
import { createAuditEntry } from "./auditTrail";
import { createIntakeDiagnostic, createIntakeItem, expandUploadFiles, INVOICE_IMPORT_DOCUMENT_TYPES } from "./documentIntake";
import { mapLocalPurchaseDocument } from './repositories/localPurchaseInvoiceRepository';
import { purchaseDocumentFromIntake } from "./purchases";
import { bankingMutationRepository, prepareAccountBankEvidenceUpdate } from "./repositories/bankingMutationRepository";
import { coordinateAccountMutation } from "./repositories/accountMutationCoordinator";
import { createPrototypeIdentityRepository } from "./repositories/identityRepository";
import { getConfiguredAccountRuntime } from "./repositories/configuredAccountRuntime";
import { intakeMutationRepository } from "./repositories/intakeMutationRepository";
import { intakeProcessingType } from "./backend/accountIntakeProcessing";
import { scopedWorkspaceRepository } from "./repositories/scopedWorkspaceRepository";
import { useAccountPage } from "./repositories/useAccountPage";
import { createIntakeQueueReader } from './repositories/intakeQueue';
import { refreshLoadedAccountPages } from "./repositories/accountPageRefresh";
import { useVisibleAccountRefresh } from "./repositories/useVisibleAccountRefresh";
import { calculateInvoiceTotals, deriveLineTotalFromInvoiceTotal } from "../utils/invoiceTotals.mjs";
import { cloneTaxLines, countryName, getClientTaxProfile, taxProfileLabel } from "./taxRules";
import { downloadInvoiceRegister } from "./workspaceExport";
import { saveAccountFile } from "./accountFileDownload";
import { ALL_BUSINESSES_SCOPE, resolveBusinessScopeId, scopeWorkspaceByBusiness } from "./workspaceBusinessScope";
import { PUBLIC_GUEST_MODE, storageKeyForMode } from "./publicGuestMode";
import { createEmptyLocalWorkspaceData, createLocalWorkspaceData } from "./localWorkspace";
import { readMobileExperience, writeMobileExperience } from "./mobileExperience";
import { normalizeNavigationPreferences, readNavigationPreferences, writeNavigationPreferences } from "./navigationPreferences";
import { createUiTranslator, INTERFACE_LANGUAGE_OPTIONS, normalizeInterfaceLanguage, UiLanguageProvider, useUiLanguage } from "./uiLanguage";
import { albanianInvoiceCountText, dashboardDate, dashboardMonthShort } from "./albanianCopy";
import { normalizeAccountingMapping, validateAccountingMapping } from "./accountingMappings";
import { formatCurrencySummary, formatInvoiceCurrencySummary, invoiceOutstandingAmount, meaningfulCurrencySummary, prioritizeCurrencySummary, summarizeBilledInvoicesByCurrency, summarizeInvoicesByCurrency } from "./financialSummaries";
import { IMPORTED_PAYMENT_REVIEW, invoiceSettlementDate, isImportedPaymentPending } from "./importedPaymentReview";
import { invoiceYear, invoiceYears, latestInvoiceDateForClient, sortClientsByInvoiceRecency, sortInvoices } from "./invoiceList";
import { clearAccountInvitationIntent, readAccountInvitationIntent } from "./accountInvitationIntent";
import { collectAccountExportPages } from "./backend/accountExportPageCollector";
import "./invoice-studio-revamp.css";

const DeclarationsView = React.lazy(() => import(/* webpackChunkName: "declarations-view" */ "./features/DeclarationsView"));
const AdminView = React.lazy(() => import(/* webpackChunkName: "administration-view" */ "./features/AdminView"));
const BusinessPortalView = React.lazy(() => import(/* webpackChunkName: "business-portal" */ "./features/BusinessPortalView"));
const AccountWorkspaceChooser = React.lazy(() => import(/* webpackChunkName: "account-workspace-chooser" */ "./features/AccountWorkspaceChooser"));
const CompanyArchiveView = React.lazy(() => import(/* webpackChunkName: "company-archive-view" */ "./features/CompanyArchiveView"));
const DocumentIntakeView = React.lazy(() => import(/* webpackChunkName: "document-intake-view" */ "./features/DocumentIntakeView"));
const BankingView = React.lazy(() => import(/* webpackChunkName: "banking-view" */ "./features/BankingView"));
const BankStatementDraftReview = React.lazy(() => import(/* webpackChunkName: "bank-statement-review" */ './features/BankStatementDraftReview'));
const BankDocumentRequests = React.lazy(() => import('./features/BankDocumentRequests'));
const PurchasesView = React.lazy(() => import(/* webpackChunkName: "purchases-view" */ "./features/PurchasesView"));
const PurchaseMonthEdi = React.lazy(() => import('./features/PurchaseMonthEdi'));
const PurchaseAccountMonthEdi = React.lazy(() => import('./features/PurchaseAccountMonthEdi'));
const QuickBooksView = React.lazy(() => import(/* webpackChunkName: "quickbooks-view" */ "./features/QuickBooksView"));
const FinancialsView = React.lazy(() => import(/* webpackChunkName: "financials-view" */ "./features/FinancialsView"));
const WorkView = React.lazy(() => import(/* webpackChunkName: "work-view" */ "./features/WorkView"));
const WorkCalendar = React.lazy(() => import(/* webpackChunkName: "work-calendar" */ "./features/WorkCalendar"));
const ReportsView = React.lazy(() => import(/* webpackChunkName: "reports-view" */ "./features/ReportsWorkspace"));
const InvoiceAssistant = React.lazy(() => import(/* webpackChunkName: "invoice-assistant" */ "./features/InvoiceAssistant"));
const AccountBillingRecovery = React.lazy(() => import(/* webpackChunkName: "account-billing-recovery" */ "./features/AccountBillingRecovery"));
const ClientPanel = React.lazy(() => import(/* webpackChunkName: "client-editor" */ "./features/ClientPanel"));
const ServicePanel = React.lazy(() => import(/* webpackChunkName: "service-editor" */ "./features/ServicePanel"));
const BusinessContacts = React.lazy(() => import(/* webpackChunkName: "business-contacts" */ "./features/BusinessContacts"));
const ClientLoadState = React.lazy(() => import(/* webpackChunkName: "client-load-state" */ "./features/ClientLoadState"));
const AccountViewContent = React.lazy(() => import(/* webpackChunkName: "account-profile" */ "./features/AccountView"));
const AccountSettingsView = React.lazy(() => import(/* webpackChunkName: "account-settings" */ './features/AccountSettingsView'));
const AccountView = props => <React.Suspense fallback={<section className="is2-view" role="status"><ViewHeading eyebrow="Your account" title="Account" description="Loading account..." /></section>}><AccountViewContent {...props} /></React.Suspense>;
const InvoiceWorkspaceContent = React.lazy(() => import(/* webpackChunkName: "invoice-workspace" */ "./features/InvoiceWorkspace"));
const InvoiceWorkspace = props => <React.Suspense fallback={<section className="is2-view" role="status"><ViewHeading eyebrow="Invoices" title="Invoice" description="Loading invoice..." /></section>}><InvoiceWorkspaceContent {...props} /></React.Suspense>;
const loadInvoiceMutations = () => import(/* webpackChunkName: "invoice-mutation-plans" */ "./repositories/invoiceMutationRepository").then(module => module.invoiceMutationRepository);

const LazyOverlayFallback = ({ title }) => <div className="is2-modal-backdrop" role="presentation"><section className="is2-lazy-overlay" role="status" aria-live="polite"><span><TimeIcon size={18} /></span><div><strong>{title}</strong><small>Loading the secure editor...</small></div></section></div>;

const declarationScopeKey = (scope) => [scope?.organizationId, scope?.businessId, scope?.type, scope?.period].join(":");
const declarationPayloadSignature = (draft) => JSON.stringify({
  inputs: draft?.inputs,
  result: draft?.result,
  status: draft?.status,
  sourceEvidence: draft?.sourceEvidence,
});
const journalEvidenceBySource = (result) => Object.fromEntries(
  (result?.ok && Array.isArray(result.data?.items) ? result.data.items : [])
    .filter((journal) => journal?.sourceEntityId)
    .map((journal) => [journal.sourceEntityId, journal]),
);

const NAV_ITEMS = [
  { id: "overview", label: "Overview", icon: HomeIcon },
  { id: "work", label: "Work", icon: CalendarIcon },
  { id: "invoices", label: "Sales invoices", tabLabel: "Sales invoices", icon: DocumentIcon },
  { id: "clients", label: "Clients", icon: PeopleIcon },
  { id: "services", label: "Services", icon: SavedIcon },
  { id: "history", label: "History", icon: TimeIcon },
  { id: "reports", label: "Reports", icon: ChartIcon },
];

const ADMIN_NAV_ITEMS = [
  { id: "admin-overview", section: "overview", label: "Admin overview", mobileLabel: "Admin", icon: HomeIcon },
  { id: "admin-businesses", section: "businesses", label: "Managed businesses", mobileLabel: "Businesses", icon: OfficeIcon },
  { id: "admin-team", section: "team", label: "Team & access", mobileLabel: "Team", icon: PeopleIcon },
  { id: "admin-history", section: "history", label: "Admin history", mobileLabel: "History", icon: TimeIcon },
];

const OPERATION_NAV_ITEMS = [
  { id: "archive", label: "Company archive", tabLabel: "Company archive", mobileLabel: "Archive", icon: OfficeIcon },
  { id: "intake", label: "Document intake", tabLabel: "Document intake", mobileLabel: "Intake", icon: ArchiveIcon },
  { id: "purchases", label: "Purchases & receipts", tabLabel: "Purchases & receipts", mobileLabel: "Purchases", icon: DocumentIcon },
  { id: "banking", label: "Banking", tabLabel: "Banking", mobileLabel: "Banking", icon: CreditCardIcon },
  { id: "ledger", label: "Financials", tabLabel: "Financials", mobileLabel: "Financials", icon: ChartIcon },
  { id: "quickbooks", label: "QuickBooks export", tabLabel: "QuickBooks export", mobileLabel: "QuickBooks", icon: ExportIcon },
  { id: "declarations", label: "Declarations", tabLabel: "Declarations", mobileLabel: "Declarations", icon: ChartIcon },
];

const ACCOUNTING_TOOLS_NAV_ITEM = { id: "accounting-tools", label: "Accounting tools", icon: ArchiveIcon };
const WORK_NAV_ITEM = { id: "work", label: "Work", icon: CalendarIcon };
const WORK_NAV_ITEMS = [
  { id: "calendar", label: "Work calendar", icon: CalendarIcon },
  { id: "tasks", label: "Tasks", icon: TickCircleIcon },
  { id: "reminders", label: "Reminders", icon: NotificationsIcon },
  { id: "routines", label: "Routines", icon: RepeatIcon },
];
const WORKSPACE_NAV_ITEM = { id: "workspace-records", label: "Workspace", icon: SavedIcon };
const WORKSPACE_NAV_ITEMS = NAV_ITEMS.filter((item) => ["services", "history", "reports"].includes(item.id));
const EMPTY_PORTAL_WORKSPACE = Object.freeze({
  organization: null,
  businesses: [],
  clients: [],
  services: [],
  invoices: [],
  activeInvoices: [],
  intakeItems: [],
  documentExpectations: [],
  bankTransactions: [],
  quickBooksExports: [],
  communications: [],
  workReminders: [],
  workTasks: [],
  workItemActivities: [],
  activityLog: [],
  portalPublications: [],
  portalDocumentRequests: [],
  portalNotifications: [],
});
const navGroupState = (activeGroup = "") => ({
  work: activeGroup === "work",
  workspace: activeGroup === "workspace",
  accounting: activeGroup === "accounting",
  "destination-accounting": activeGroup === "destination-accounting",
  ...(activeGroup && !["work", "workspace", "accounting"].includes(activeGroup)
    ? { [activeGroup]: true }
    : {}),
});

const INVOICE_FILTERS = [
  { id: "All", mobileLabel: "All" },
  { id: "Draft", mobileLabel: "Draft" },
  { id: "Sent", mobileLabel: "Sent" },
  { id: "Partially paid", mobileLabel: "Partial" },
  { id: "Overdue", mobileLabel: "Overdue" },
  { id: "Paid", mobileLabel: "Paid" },
  { id: "Archived", mobileLabel: "Archived" },
];

const MOBILE_MORE_ITEMS = [
  { id: "clients", label: "Clients", detail: "Contacts and billing defaults", icon: PeopleIcon },
  { id: "services", label: "Services", detail: "Rates and reusable work", icon: SavedIcon },
  { id: "history", label: "History", detail: "Who changed what", icon: TimeIcon },
  { id: "reports", label: "Reports", detail: "Billing performance", icon: ChartIcon },
  { id: "settings", label: "Settings", detail: "Organization and defaults", icon: SettingsIcon },
  { id: "account", label: "Account", detail: "Profile and public mode", icon: PersonIcon },
];

const matchesMobileViewport = () => Boolean(
  typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(max-width: 860px)")?.matches,
);

const sameMonth = (value, reference) => {
  if (!value) return false;
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return !Number.isNaN(date.getTime())
    && date.getFullYear() === reference.getFullYear()
    && date.getMonth() === reference.getMonth();
};

const greeting = (now = new Date(), t = (value) => value) => {
  const hour = now.getHours();
  if (hour < 12) return t("Good morning");
  if (hour < 18) return t("Good afternoon");
  return t("Good evening");
};

const collectionSeries = (invoices, collectionHistory = [], now = new Date(), count = 8, language = "en") => Array.from({ length: count }, (_, index) => {
  const date = new Date(now.getFullYear(), now.getMonth() - (count - index - 1), 1);
  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  const detailedTotal = invoices
    .filter((invoice) => invoice.documentType !== "credit-note" && invoice.status === "Paid" && sameMonth(invoiceSettlementDate(invoice), date))
    .reduce((sum, invoice) => sum + parseCurrency(invoice.amount), 0);
  const importedPeriod = collectionHistory.find((period) => period.month === key);
  return {
    key,
    label: dashboardMonthShort(date, language),
    total: detailedTotal || parseCurrency(importedPeriod?.total),
    source: detailedTotal ? "Invoice records" : importedPeriod?.source,
  };
});

const ProfileAvatar = ({ profile, className = "is2-user-trigger__avatar" }) => {
  const image = profile.avatarUrl || profile.avatar || profile.photo;
  const initials = profile.initials || String(profile.name || "U").split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return <AvatarImage className={className} src={image} fallback={initials} />;
};

const BusinessScopeSelect = ({ businesses, value, onChange, mobile = false, disabled = false }) => {
  const { t } = useUiLanguage();
  const selectedBusiness = businesses.find((business) => business.id === value);
  const selectedLabel = selectedBusiness?.name || t("All issuing businesses");
  return <label className={`is2-business-scope${mobile ? " is-mobile" : ""}${disabled ? " is-disabled" : ""}`}>
    <span className="is2-business-scope__mark"><OfficeIcon size={14} /></span>
    <span><small>{t("Issuing business")}</small><strong>{selectedLabel}</strong></span>
    <ChevronDownIcon size={13} />
    <select data-cy={mobile ? "mobile-business-scope" : "business-scope"} aria-label={t("Scope daily workspace by issuing business")} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value={ALL_BUSINESSES_SCOPE}>{t("All issuing businesses")}</option>
      {businesses.map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}
    </select>
  </label>;
};

const BusinessDirectoryMore = ({ page, mobile = false }) => {
  if (!page || (!page.hasMore && !page.error)) return null;
  return <button type="button" className={`is2-business-directory-more${mobile ? " is-mobile" : ""}${page.error ? " is-error" : ""}`} onClick={page.error ? page.reload : page.loadMore} disabled={page.loading}>
    {page.loading ? "Loading businesses..." : page.error ? "Retry business directory" : `Load more businesses (${page.items.length} shown)`}
  </button>;
};

const MobileMoreView = ({ navigate, navigateWork, navigateAdmin, profile, sessionMode, signOut, switchAccountWorkspace, workspaceMode, switchWorkspaceMode, canManage, openBusinessContacts = null, operationItems = [], businesses = [], businessPage = null, businessScopeId = ALL_BUSINESSES_SCOPE, setBusinessScopeId, mobileExperience = "minimal", setMobileExperience, navigationPreferences = normalizeNavigationPreferences(), routineWorkEnabled = false }) => {
  const { t } = useUiLanguage();
  const advancedItems = workspaceMode === "admin" ? [
    { id: "admin-team", adminSection: "team", label: "Team & access", detail: "Roles and managed-business access", icon: PeopleIcon },
    { id: "admin-history", adminSection: "history", label: "Admin history", detail: "Operational changes and decisions", icon: TimeIcon },
    { id: "settings", label: "Settings", detail: "Organization and defaults", icon: SettingsIcon },
    { id: "account", label: "Account", detail: "Profile and public mode", icon: PersonIcon },
  ] : MOBILE_MORE_ITEMS;
  const workItems = WORK_NAV_ITEMS
    .filter((item) => navigationPreferences[item.id] !== false && (item.id !== "routines" || routineWorkEnabled))
    .map((item) => ({
      ...item,
      workSection: item.id,
      detail: item.id === "tasks"
        ? "Assignments and shared work"
        : item.id === "reminders"
          ? "Deadlines and follow-ups"
          : item.id === "calendar" ? "Scheduled tasks, reminders, and recurring work" : "Monthly templates and period checklists",
    }));
  const itemEnabled = (item) => {
    if (item.adminSection || item.id === "settings" || item.id === "account") return true;
    if (["archive", "intake", "purchases", "banking", "ledger", "quickbooks", "declarations"].includes(item.id)) return navigationPreferences.accounting;
    return navigationPreferences[item.id] !== false;
  };
  const workspaceItems = workspaceMode === "regular" && mobileExperience === "minimal"
    ? MOBILE_MORE_ITEMS.filter((item) => ["services", "reports", "settings"].includes(item.id))
    : advancedItems;
  const groups = workspaceMode === "admin"
    ? [{ id: "admin", label: "Administration", items: advancedItems }]
    : [
      { id: "work", label: "Work", items: workItems },
      { id: "accounting", label: "Accounting tools", items: operationItems },
      { id: "workspace", label: "Workspace", items: workspaceItems },
    ];
  const openItem = (item) => {
    if (item.workSection) navigateWork(item.workSection);
    else if (item.adminSection) navigateAdmin(item.adminSection);
    else navigate(item.id);
  };
  return <div className="is2-view is2-mobile-more-view" data-cy="mobile-more-view">
    <ViewHeading eyebrow="Workspace" title="More" description="Work, accounting tools, and account controls." />
    <button className="is2-mobile-account-card" data-cy="mobile-account" onClick={() => navigate("account")}><ProfileAvatar profile={profile} /><span><strong>{profile.name}</strong><small>{profile.role} - {sessionMode === "local" ? "stored on this device" : "account workspace"}</small></span><ChevronRightIcon size={16} /></button>
    {workspaceMode === "regular" ? <section className="is2-mobile-experience" aria-label="Mobile workspace detail"><span><strong>Mobile view</strong><small>{mobileExperience === "minimal" ? "Focused dashboard with every tool still reachable" : "Full dashboard and reporting detail"}</small></span><div className="is2-segmented"><button className={mobileExperience === "minimal" ? "is-active" : ""} onClick={() => setMobileExperience("minimal")}>Minimal</button><button className={mobileExperience === "advanced" ? "is-active" : ""} onClick={() => setMobileExperience("advanced")}>Advanced</button></div></section> : null}
    {workspaceMode === "regular" && businesses.length > 1 ? <><BusinessScopeSelect businesses={businesses} value={businessScopeId} onChange={setBusinessScopeId} mobile /><BusinessDirectoryMore page={businessPage} mobile /></> : null}
    {workspaceMode === "regular" && openBusinessContacts ? <button className="is2-business-contacts-entry" onClick={openBusinessContacts}><PeopleIcon size={16} />Business contacts</button> : null}
    {canManage ? <button className={`is2-mobile-mode-card is-${workspaceMode}`} onClick={() => switchWorkspaceMode(workspaceMode === "admin" ? "regular" : "admin")} data-cy="mobile-workspace-mode"><span><SwapHorizontalIcon size={18} /></span><span><strong>{t(workspaceMode === "admin" ? "Daily work" : "Administration")}</strong><small>{workspaceMode === "admin" ? "Invoices, clients, and day-to-day work" : "Businesses, team access, and policies"}</small></span><em>{t("Switch")}</em></button> : null}
    <div className="is2-mobile-destination-groups">{groups.map((group) => { const visibleItems = group.items.filter((item) => item.id !== "account" && itemEnabled(item)); return visibleItems.length ? <section className="is2-mobile-destination-group" key={group.id}><p>{t(group.label)}</p><div className="is2-mobile-destination-list">{visibleItems.map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`mobile-more-${item.id}`} onClick={() => openItem(item)}><span><Icon size={18} /></span><span><strong>{t(item.tabLabel || item.label)}</strong><small>{t(item.detail)}</small></span><ChevronRightIcon size={15} /></button>; })}</div></section> : null; })}</div>
    {switchAccountWorkspace ? <button className="is2-mobile-signout" onClick={switchAccountWorkspace}><span><SwapHorizontalIcon size={16} /></span><span><strong>Switch workspace</strong></span><ChevronRightIcon size={15} /></button> : null}
    <button className="is2-mobile-signout" data-cy="mobile-signout" onClick={signOut}><span><LogOutIcon size={16} /></span><span><strong>{t(sessionMode === "local" ? "Exit local workspace" : "Sign out")}</strong><small>Return to the secure start screen</small></span><ChevronRightIcon size={15} /></button>
  </div>;
};

const MetricCurrencyValue = ({ summary, preferredCurrency, privacy }) => {
  const ordered = prioritizeCurrencySummary(meaningfulCurrencySummary(summary), preferredCurrency);
  const [primary, ...secondary] = ordered.length
    ? ordered
    : [{ currency: preferredCurrency || "EUR", amount: 0 }];
  return <div className="is2-metric-currency-value">
    <div className="is2-metric-currency-value__primary">
      <strong>{privacy.amount(formatCurrency(primary.amount, primary.currency))}</strong>
      {secondary.length ? <em>{primary.currency}</em> : null}
    </div>
    {secondary.length ? <div className="is2-metric-currency-value__breakdown" aria-label="Other native currency balances">
      {secondary.map((entry) => <span key={entry.currency}><em>{entry.currency}</em><b>{privacy.amount(formatCurrency(entry.amount, entry.currency))}</b></span>)}
    </div> : null}
  </div>;
};

const overviewCurrencySummary = (overview, key) => Object.entries(overview?.totalsByCurrency || {})
  .map(([currencyCode, values]) => ({ currency: currencyCode, amount: Number(values?.[key] || 0) }))
  .sort((left, right) => left.currency.localeCompare(right.currency));

const MetricRibbon = ({ invoices, scopeLabel, reportingCurrency = "EUR", period, serverOverview = null }) => {
  const privacy = usePrivacy();
  const { language, t } = useUiLanguage();
  const receivableInvoices = invoices.filter((invoice) => invoice.documentType !== "credit-note");
  const totalBilled = serverOverview ? overviewCurrencySummary(serverOverview, "billed") : summarizeBilledInvoicesByCurrency(invoices);
  const settledInvoices = receivableInvoices.filter((invoice) => invoice.status === "Paid");
  const collected = serverOverview ? overviewCurrencySummary(serverOverview, "collected") : summarizeInvoicesByCurrency(settledInvoices);
  const openInvoices = receivableInvoices.filter((invoice) => invoice.status !== "Paid");
  const outstanding = serverOverview ? overviewCurrencySummary(serverOverview, "outstanding") : summarizeInvoicesByCurrency(openInvoices, invoiceOutstandingAmount);
  const overdue = receivableInvoices.filter((invoice) => invoice.status === "Overdue");
  const overdueSummary = serverOverview ? overviewCurrencySummary(serverOverview, "overdue") : summarizeInvoicesByCurrency(overdue, invoiceOutstandingAmount);
  const openCount = serverOverview ? Number(serverOverview.counts?.openInvoices || 0) : openInvoices.length;
  const invoiceCount = serverOverview ? Number(serverOverview.counts?.issuedInvoices || 0) : receivableInvoices.length;
  const settledCount = serverOverview ? Number(serverOverview.counts?.settledInvoices || 0) : settledInvoices.length;
  const overdueCount = serverOverview ? Number(serverOverview.counts?.overdueInvoices || 0) : overdue.length;
  const visibleTotalBilled = meaningfulCurrencySummary(totalBilled);
  const visibleOutstanding = meaningfulCurrencySummary(outstanding);
  return (
  <section className={`is2-metric-ribbon${invoiceCount ? "" : " is-empty"}`} aria-label={`${scopeLabel} summary`}>
    <div className="is2-metric is2-metric--lead">
      <span>{t("Total billed")}</span>
      <MetricCurrencyValue summary={visibleTotalBilled} preferredCurrency={reportingCurrency} privacy={privacy} />
      <small>{t(invoiceCount === 1 ? "Across {{count}} invoice" : "Across {{count}} invoices", { count: privacy.number(invoiceCount) })}</small>
    </div>
    <div className="is2-metric">
      <span>{period === "all" ? t("Collected across all years") : t("Collected in {{year}}", { year: period })}</span>
      <MetricCurrencyValue summary={collected} preferredCurrency={reportingCurrency} privacy={privacy} />
      <small>{language === "sq" ? settledCount === 1 ? "1 faturë e arkëtuar në këtë periudhë" : `${privacy.number(settledCount)} fatura të arkëtuara në këtë periudhë` : <><span className="is2-up">{privacy.number(settledCount)}</span> settled invoice{settledCount === 1 ? "" : "s"} in this period</>}</small>
    </div>
    <div className="is2-metric">
      <span>{t("Outstanding")}</span>
      <MetricCurrencyValue summary={visibleOutstanding} preferredCurrency={reportingCurrency} privacy={privacy} />
      <small>{language === "sq" ? albanianInvoiceCountText("open", openCount, privacy.number(openCount)) : `Across ${privacy.number(openCount)} open invoice${openCount === 1 ? "" : "s"}`}{visibleOutstanding.length > 1 ? ` · ${privacy.number(visibleOutstanding.length)} ${language === "sq" ? "valuta" : "currencies"}` : ""}</small>
    </div>
    <div className="is2-metric">
      <span>{t("Overdue")}</span>
      <MetricCurrencyValue summary={overdueSummary} preferredCurrency={reportingCurrency} privacy={privacy} />
      <small>{language === "sq" ? albanianInvoiceCountText("attention", overdueCount, privacy.number(overdueCount)) : `${privacy.number(overdueCount)} invoice${overdueCount === 1 ? " needs" : "s need"} attention`}</small>
    </div>
  </section>
  );
};

const CashFlow = ({ invoices, collectionHistory, referenceDate = new Date(), period = "all", serverOverview = null }) => {
  const privacy = usePrivacy();
  const { language, t } = useUiLanguage();
  const now = referenceDate;
  const currencies = useMemo(() => [...new Set(serverOverview
    ? [...Object.keys(serverOverview.totalsByCurrency || {}), ...(serverOverview.monthlyCollected || []).map((entry) => entry.currency || "EUR")]
    : invoices.map((invoice) => invoice.currency || "EUR"))].sort(), [invoices, serverOverview]);
  const [currency, setCurrency] = useState(() => currencies.includes("EUR") ? "EUR" : currencies[0] || "EUR");
  useEffect(() => {
    if (!currencies.includes(currency)) setCurrency(currencies.includes("EUR") ? "EUR" : currencies[0] || "EUR");
  }, [currencies, currency]);
  const currencyInvoices = invoices.filter((invoice) => (invoice.currency || "EUR") === currency);
  const currencyHistory = (serverOverview ? serverOverview.monthlyCollected || [] : collectionHistory)
    .filter((entry) => !entry.currency || entry.currency === currency)
    .map((entry) => ({ ...entry, total: entry.total ?? entry.collected, source: entry.source || "Recorded payments" }));
  const series = collectionSeries(serverOverview ? [] : currencyInvoices, currencyHistory, now, 8, language);
  const collected = series[series.length - 1]?.total || 0;
  const expected = serverOverview
    ? Number(serverOverview.totalsByCurrency?.[currency]?.outstanding || 0)
    : currencyInvoices.filter((invoice) => invoice.documentType !== "credit-note" && invoice.status === "Sent").reduce((sum, invoice) => sum + invoiceOutstandingAmount(invoice), 0);
  const compact = collected >= 1000 ? `${(collected / 1000).toFixed(collected % 1000 === 0 ? 0 : 1)}k` : String(collected);
  const maxCollection = Math.max(...series.map((item) => item.total), 1);
  const chartCeiling = Math.max(5000, Math.ceil(maxCollection / 5000) * 5000);
  const guideLabel = (value) => value >= 1000
    ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
    : String(value);
  return (
  <section className="is2-tool is2-cashflow">
    <div className="is2-section-heading">
      <div>
        <p className="is2-eyebrow">{t("Cash in motion")}</p>
        <h2>{period === "all" ? t("Recent collection") : t("{{year}} collection", { year: period })}</h2>
      </div>
      {currencies.length ? <span className="is2-cashflow-heading-actions">{currencies.length > 1 ? <CustomSelect value={currency} onChange={(event) => setCurrency(event.target.value)} aria-label="Cash flow currency">{currencies.map((entry) => <option key={entry} value={entry}>{entry}</option>)}</CustomSelect> : null}<button className="is2-text-button">{t("Last 8 months")} <ChevronRightIcon size={14} /></button></span> : <span className="is2-period-label">{t("No history yet")}</span>}
    </div>
    {currencies.length ? <div className="is2-chart" aria-label="Monthly collection chart">
      <div className="is2-chart__guide is2-chart__guide--top"><span>{privacy.enabled ? "••k" : guideLabel(chartCeiling)}</span></div>
      <div className="is2-chart__guide is2-chart__guide--mid"><span>{privacy.enabled ? "••k" : guideLabel(chartCeiling / 2)}</span></div>
      <div className="is2-chart__bars">
        {series.map((item, index) => (
          <div className="is2-chart__column" key={item.key} title={item.source || "No collection recorded"}>
            <div className={`is2-chart__bar${index === series.length - 1 ? " is-current" : ""}`} style={{ height: `${item.total ? Math.max(8, (item.total / chartCeiling) * 100) : 2}%` }}>
              {index === series.length - 1 ? <span>{privacy.enabled ? privacy.amount(`€${compact}`) : compact}</span> : null}
            </div>
            <small>{item.label}</small>
          </div>
        ))}
      </div>
    </div> : <div className="is2-chart is-empty" aria-label="No collection history"><div className="is2-empty-chart-visual" aria-hidden="true">{[34, 52, 43, 68, 58, 82].map((height) => <i key={height} style={{ height: `${height}%` }} />)}<ChartIcon size={18} /></div><span><strong>Your collection story starts here</strong><small>The first issued invoice will turn this preview into a live cash-flow view.</small></span></div>}
    <footer className="is2-cashflow__footer">
      <span><i className="is2-dot is2-dot--orange" /> {t("Collected")}</span>
      <span><i className="is2-dot is2-dot--sand" /> {t("Expected by month end")}</span>
      <strong>{privacy.amount(formatCurrency(expected, currency))} {language === "sq" ? "të pritshme" : "expected"}</strong>
    </footer>
  </section>
  );
};

const AttentionQueue = ({ openInvoice, invoices }) => {
  const privacy = usePrivacy();
  const { language, t } = useUiLanguage();
  const overdueInvoice = invoices.find((invoice) => invoice.documentType !== "credit-note" && invoice.status === "Overdue");
  const draftInvoice = invoices.find((invoice) => invoice.status === "Draft");
  const dueInvoice = invoices.find((invoice) => invoice.documentType !== "credit-note" && invoice.status === "Sent");
  const actionCount = [overdueInvoice, draftInvoice, dueInvoice].filter(Boolean).length;
  return (
  <section className="is2-tool is2-attention">
    <div className="is2-section-heading">
      <div>
        <p className="is2-eyebrow">{t("Needs attention")}</p>
        <h2>{t("Next moves")}</h2>
      </div>
      <span className="is2-count">{privacy.number(actionCount)}</span>
    </div>
    <div className="is2-attention__list">
      {overdueInvoice ? <button className="is2-attention-row" onClick={() => openInvoice(overdueInvoice)}>
        <span className={`is2-attention-row__icon${overdueInvoice.status === "Paid" ? " is-success" : " is-overdue"}`}>{overdueInvoice.status === "Paid" ? <TickCircleIcon size={16} /> : <WarningSignIcon size={16} />}</span>
        <span><strong>{privacy.invoice(overdueInvoice)} {language === "sq" ? "është e vonuar" : "is overdue"}</strong><small>{privacy.client(overdueInvoice.client)} - {language === "sq" ? "kërkon përcjellje" : "follow-up needed"}</small></span>
        <span className="is2-attention-row__amount">{privacy.amount(formatCurrency(overdueInvoice.amount, overdueInvoice.currency))}</span>
        <ChevronRightIcon size={15} />
      </button> : null}
      {draftInvoice ? <button className="is2-attention-row" onClick={() => openInvoice(draftInvoice)}>
        <span className="is2-attention-row__icon"><DocumentIcon size={16} /></span>
        <span><strong>{privacy.invoice(draftInvoice)} {language === "sq" ? "është ende draft" : "is still a draft"}</strong><small>{privacy.client(draftInvoice.client)} - {language === "sq" ? "përditësuar sot" : "updated today"}</small></span>
        <span className="is2-attention-row__amount">{privacy.amount(formatCurrency(draftInvoice.amount, draftInvoice.currency))}</span>
        <ChevronRightIcon size={15} />
      </button> : null}
      {dueInvoice ? <button className="is2-attention-row" onClick={() => openInvoice(dueInvoice)}>
        <span className="is2-attention-row__icon is-success"><TickCircleIcon size={16} /></span>
        <span><strong>{privacy.invoice(dueInvoice)} {language === "sq" ? "pret pagesën" : "is awaiting payment"}</strong><small>{privacy.client(dueInvoice.client)} - {language === "sq" ? "afati" : "due"} {privacy.date(dueInvoice.due)}</small></span>
        <span className="is2-attention-row__amount">{privacy.amount(formatCurrency(dueInvoice.amount, dueInvoice.currency))}</span>
        <ChevronRightIcon size={15} />
      </button> : null}
      {!actionCount ? <div className="is2-attention-empty is2-attention-empty--ready"><span className="is2-attention-empty__mark"><TickCircleIcon size={20} /></span><span><strong>{t("Clear for now")}</strong><small>{t("Drafts, due dates, and payment follow-ups will appear here when they need you.")}</small></span></div> : null}
    </div>
  </section>
  );
};

const Overview = ({ downloadAccountDocument, navigate, openInvoice, reuseInvoice, notify, invoices, archiveInvoice, restoreInvoice, updateInvoiceStatus, businesses, clients, showIssuer, profile, scopeLabel, collectionHistory, reportingCurrency, loadOverview = null, organizationId = null, businessId = null }) => {
  const privacy = usePrivacy();
  const { language, t } = useUiLanguage();
  const currentYear = String(new Date().getFullYear());
  const [serverOverview, setServerOverview] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [overviewRefresh, setOverviewRefresh] = useState(0);
  const [overviewError, setOverviewError] = useState("");
  const overviewScope = useRef("");
  useVisibleAccountRefresh(Boolean(loadOverview && organizationId), () => setOverviewRefresh((value) => value + 1));
  useEffect(() => {
    if (!loadOverview || !organizationId) {
      setServerOverview(null);
      return undefined;
    }
    let active = true;
    const scope = JSON.stringify([organizationId, businessId, year]);
    if (overviewScope.current !== scope) setServerOverview(null);
    overviewScope.current = scope;
    setOverviewError("");
    loadOverview({ organizationId, businessId, year }).then((result) => {
      if (active && result?.ok) setServerOverview(result.data);
      else if (active) { setServerOverview(null); setOverviewError(result?.error || "The overview could not be refreshed."); }
    }).catch(() => {
      if (active) { setServerOverview(null); setOverviewError("The overview could not be refreshed."); }
    });
    return () => { active = false; };
  }, [businessId, loadOverview, organizationId, overviewRefresh, year]);
  const years = useMemo(() => [...new Set([
    currentYear,
    ...invoiceYears(invoices),
    ...(serverOverview?.availableYears || []).map(String),
  ])].sort().reverse(), [currentYear, invoices, serverOverview]);
  const periodInvoices = year === "all" ? invoices : invoices.filter((invoice) => invoiceYear(invoice) === year);
  const periodCollectionHistory = year === "all" ? collectionHistory : collectionHistory.filter((period) => String(period.month || "").startsWith(`${year}-`));
  const latestPeriodDate = periodInvoices.reduce((latest, invoice) => {
    const candidate = Date.parse(invoice.issuedDate || "") || 0;
    return candidate > latest ? candidate : latest;
  }, 0);
  const chartReferenceDate = year === "all" || year === currentYear
    ? new Date()
    : latestPeriodDate ? new Date(latestPeriodDate) : new Date(Number(year), 11, 1);
  const selectedBusiness = businesses.length === 1 ? businesses[0] : null;
  const overdueCount = serverOverview
    ? Number(serverOverview.counts?.overdueInvoices || 0)
    : periodInvoices.filter((invoice) => invoice.documentType !== "credit-note" && invoice.status === "Overdue").length;
  const now = new Date();
  const description = language === "sq"
    ? privacy.enabled
      ? "Vlerat e klientëve dhe financave janë fshehur gjatë modalitetit publik."
      : overdueCount
        ? selectedBusiness
          ? `${selectedBusiness.name} ka ${overdueCount === 1 ? "1 faturë që kërkon" : `${overdueCount} fatura që kërkojnë`} përcjellje këtë javë.`
          : albanianInvoiceCountText("follow-up", overdueCount)
        : selectedBusiness ? `${selectedBusiness.name} nuk ka përcjellje të vonuara.` : "Arkëtimet aktuale nuk kanë përcjellje të vonuara."
    : privacy.enabled
      ? "Sensitive client and financial values are hidden while public mode is active."
      : overdueCount
        ? selectedBusiness
          ? `${selectedBusiness.name} has ${overdueCount} invoice${overdueCount === 1 ? "" : "s"} that ${overdueCount === 1 ? "needs" : "need"} a follow-up this week.`
          : `${overdueCount} invoice${overdueCount === 1 ? " needs" : "s need"} a follow-up this week.`
        : selectedBusiness ? `${selectedBusiness.name} has no overdue follow-ups.` : "Your current receivables have no overdue follow-ups.";
  if (loadOverview && !serverOverview) return <div className="is2-view is2-view--overview" aria-live="polite">
    <p>{overviewError || "Loading account overview..."}</p>
    {overviewError ? <button type="button" className="is2-button is2-button--secondary" onClick={() => setOverviewRefresh((value) => value + 1)}>Retry overview</button> : null}
  </div>;
  return <div className="is2-view is2-view--overview">
    <ViewHeading
      eyebrow={dashboardDate(now, language)}
      title={`${greeting(now, t)}, ${profile.name.split(" ")[0]}.`}
      description={description}
      actions={<label className="is2-overview-period"><span>{t("Overview period")}</span><CustomSelect data-cy="overview-year" aria-label={t("Overview period")} value={year} onChange={(event) => setYear(event.target.value)}>{years.map((entry) => <option key={entry} value={entry}>{entry}{entry === currentYear ? ` · ${t("Current year")}` : ""}</option>)}<option value="all">{t("All years")}</option></CustomSelect></label>}
    />
    <MetricRibbon invoices={periodInvoices} scopeLabel={scopeLabel} reportingCurrency={reportingCurrency} period={year} serverOverview={serverOverview} />
    <div className="is2-overview-grid">
      <CashFlow invoices={periodInvoices} collectionHistory={periodCollectionHistory} referenceDate={chartReferenceDate} period={year} serverOverview={serverOverview} />
      <AttentionQueue openInvoice={openInvoice} invoices={periodInvoices} />
    </div>
    <section className="is2-section is2-recent">
      <div className="is2-section-heading">
        <div><p className="is2-eyebrow">{t("Latest activity")}</p><h2>{t("Recent invoices")}</h2></div>
        <button className="is2-text-button" onClick={() => navigate("invoices")}>{t("View all")} <ChevronRightIcon size={14} /></button>
      </div>
      {periodInvoices.length ? <InvoiceTable downloadAccountDocument={downloadAccountDocument} rows={sortInvoices(periodInvoices).slice(0, 4)} openInvoice={openInvoice} reuseInvoice={reuseInvoice} notify={notify} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} updateInvoiceStatus={updateInvoiceStatus} businesses={businesses} clients={clients} showIssuer={showIssuer} compact /> : <div className="is2-overview-empty"><DocumentIcon size={21} /><span><strong>No invoices for {year === "all" ? scopeLabel : `${scopeLabel} in ${year}`}</strong><small>Choose another year or create the first invoice for this period.</small></span></div>}
    </section>
  </div>;
};

const Invoices = ({ downloadAccountDocument, openInvoice, reuseInvoice, openCreate, openPurchases, openScope, notify, invoices, archiveInvoice, restoreInvoice, updateInvoiceStatus, reviewImportedPayment, businesses, clients, showIssuer, showMobileScope = false, scopeLabel = "All issuing businesses" }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const [filter, setFilter] = useState("All");
  const years = useMemo(() => invoiceYears(invoices), [invoices]);
  const currentYear = String(new Date().getFullYear());
  const [year, setYear] = useState(() => years.includes(currentYear) ? currentYear : "all");
  const [sort, setSort] = useState("issued-desc");
  const [reviewOpen, setReviewOpen] = useState(false);
  useEffect(() => {
    if (year !== "all" && !years.includes(year)) setYear(years.includes(currentYear) ? currentYear : "all");
  }, [currentYear, year, years]);
  const pendingImportedPayments = invoices.filter(isImportedPaymentPending);
  const yearInvoices = year === "all" ? invoices : invoices.filter((invoice) => invoiceYear(invoice) === year);
  const sortedInvoices = sortInvoices(yearInvoices, sort);
  const activeInvoices = sortedInvoices.filter((invoice) => !invoice.archived);
  const filteredRows = filter === "Archived" ? sortedInvoices.filter((invoice) => invoice.archived) : filter === "All" ? activeInvoices : activeInvoices.filter((invoice) => invoice.status === filter);
  const filterCount = (status) => status === "Archived" ? sortedInvoices.filter((invoice) => invoice.archived).length : status === "All" ? activeInvoices.length : activeInvoices.filter((invoice) => invoice.status === status).length;
  if (reviewOpen) return <ImportedPaymentReviewView invoices={invoices} close={() => setReviewOpen(false)} reviewPayment={reviewImportedPayment} />;
  return (
    <div className="is2-view">
      <ViewHeading
        eyebrow="Documents"
        title="Sales invoices"
        description="Invoices issued to customers, their payment state, and follow-ups."
        actions={<button className="is2-button is2-button--primary is2-button--mobile-icon is2-invoices-create" onClick={openCreate}><PlusIcon size={16} /><span>{t("New invoice")}</span></button>}
      />
      <InvoiceLedgerSwitch value="sales" onChange={(value) => value === "purchases" && openPurchases?.()} />
      {showMobileScope ? <button className="is2-mobile-invoice-scope" data-cy="mobile-invoice-scope" onClick={openScope}><span><OfficeIcon size={14} /></span><span><small>{t("Issuing business")}</small><strong>{t(scopeLabel)}</strong></span><ChevronRightIcon size={14} /></button> : null}
      {pendingImportedPayments.length ? <button className="is2-import-review-callout" data-cy="open-imported-payment-review" onClick={() => setReviewOpen(true)}><span><WarningSignIcon size={16} /></span><span><strong>{privacy.number(pendingImportedPayments.length)} {t("imported payments need confirmation")}</strong><small>{t("Historical invoices are treated as paid until you mark an exception.")}</small></span><ChevronRightIcon size={15} /></button> : null}
      <div className="is2-list-toolbar">
        <div className="is2-segmented is2-invoice-filters" role="tablist" aria-label="Invoice status">
          {INVOICE_FILTERS.map(({ id: status, mobileLabel }) => (
            <button key={status} className={filter === status ? "is-active" : ""} onClick={() => setFilter(status)} role="tab" aria-selected={filter === status} aria-label={`${status}: ${filterCount(status)}`}>
              <span className="is2-filter-label is2-filter-label--desktop">{t(status)}</span><span className="is2-filter-label is2-filter-label--mobile">{t(mobileLabel)}</span><span className="is2-filter-count">{privacy.number(filterCount(status))}</span>
            </button>
          ))}
        </div>
        <div className="is2-list-toolbar__controls">
          <CustomSelect className="is2-invoice-list-select" value={year} onChange={(event) => setYear(event.target.value)} aria-label="Filter invoices by year"><option value="all">All years</option>{years.map((entry) => <option key={entry} value={entry}>{entry}</option>)}</CustomSelect>
          <CustomSelect className="is2-invoice-list-select" value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort invoices"><option value="issued-desc">Newest issued</option><option value="issued-asc">Oldest issued</option><option value="number-desc">Highest invoice number</option><option value="number-asc">Lowest invoice number</option></CustomSelect>
        </div>
      </div>
      <section className="is2-section is2-documents">
        {filteredRows.length ? <InvoiceTable downloadAccountDocument={downloadAccountDocument} rows={filteredRows} openInvoice={openInvoice} reuseInvoice={reuseInvoice} notify={notify} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} updateInvoiceStatus={updateInvoiceStatus} businesses={businesses} clients={clients} showIssuer={showIssuer} /> : <div className="is2-empty is2-invoice-list-empty"><DocumentIcon size={24} /><strong>No {filter.toLowerCase()} invoices</strong><span>Try another filter or create a new invoice.</span><button className="is2-button is2-button--quiet" onClick={openCreate}><PlusIcon size={15} /> New invoice</button></div>}
      </section>
    </div>
  );
};

const clientServerCurrencySummary = (client, field) => formatCurrencySummary(
  prioritizeCurrencySummary(
    meaningfulCurrencySummary(Object.entries(client?.totalsByCurrency || {}).map(([currency, totals]) => ({
      currency,
      amount: Number(totals?.[field] ?? (field === "netBilled" ? totals?.billed : 0)) || 0,
    }))),
    client?.currency,
  ),
  client?.currency,
);

const Clients = ({ openCreate, openInvoice, openClient, exportStatement, invoices, clients, businesses, updateInvoiceStatus, prepareInvoiceReminder, initialClientId = "", selectedClientId = "", onSelectClient, invoicePage = null, detailState = null, loadDetailCollection = null, serverManaged = false, returnToSearch }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const sortedClients = useMemo(() => sortClientsByInvoiceRecency(clients, invoices), [clients, invoices]);
  const currentYear = String(new Date().getFullYear());
  const activeClients = sortedClients.filter((item) => latestInvoiceDateForClient(item, invoices).startsWith(currentYear));
  const previousClients = sortedClients.filter((item) => !latestInvoiceDateForClient(item, invoices).startsWith(currentYear));
  const [showPreviousClients, setShowPreviousClients] = useState(false);
  const [localSelected, setLocalSelected] = useState(initialClientId || sortedClients[0]?.id || "");
  const selected = selectedClientId || localSelected;
  const [clientMenuOpen, setClientMenuOpen] = useState(false);
  const clientMenuRef = useRef(null);
  useEffect(() => {
    if (serverManaged && selectedClientId) return;
    if (sortedClients.some((item) => item.id === selected)) return;
    const nextId = sortedClients[0]?.id || "";
    setLocalSelected(nextId);
    if (nextId && selectedClientId !== nextId) onSelectClient?.(nextId);
  }, [onSelectClient, selected, selectedClientId, serverManaged, sortedClients]);
  useEffect(() => {
    if (!initialClientId || !clients.some((item) => item.id === initialClientId)) return;
    setLocalSelected(initialClientId);
    if (selectedClientId !== initialClientId) onSelectClient?.(initialClientId);
  }, [clients, initialClientId, onSelectClient, selectedClientId]);
  useEffect(() => {
    if (!clientMenuOpen) return undefined;
    const close = (event) => {
      if (!clientMenuRef.current?.contains(event.target)) setClientMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [clientMenuOpen]);
  const client = sortedClients.find((item) => item.id === selected) || (!serverManaged || !selectedClientId ? sortedClients[0] : null);
  if (!client && serverManaged && selectedClientId) return <React.Suspense fallback={null}><ClientLoadState state={detailState} back={returnToSearch} /></React.Suspense>;
  if (!client) return <div className="is2-view"><ViewHeading eyebrow="Relationships" title="Clients" description="Billing contacts and preferences stay inside this organization." /><div className="is2-empty is2-client-list-empty"><PeopleIcon size={24} /><strong>{t("No clients yet")}</strong><span>{t("Add a client when you are ready to create the first invoice.")}</span><button type="button" className="is2-button is2-button--primary" data-cy="empty-add-client" disabled={privacy.enabled} title={privacy.enabled ? "Turn off public mode to add client details" : t("Add client")} onClick={() => openClient(null)}><PlusIcon size={16} /> {t("Add client")}</button></div></div>;
  const clientInvoices = invoices.filter((invoice) => invoice.clientId ? invoice.clientId === client.id : invoice.client === client.name && (!client.issuerId || invoice.issuerId === client.issuerId));
  const clientLifetime = serverManaged ? clientServerCurrencySummary(client, "netBilled") : formatInvoiceCurrencySummary(clientInvoices, undefined, client.currency);
  const clientOutstanding = serverManaged ? clientServerCurrencySummary(client, "outstanding") : formatInvoiceCurrencySummary(clientInvoices, invoiceOutstandingAmount, client.currency);
  const invoiceCount = serverManaged ? client.invoiceCount : clientInvoices.length;
  const scopeLabel = clientScopeLabel(client, businesses);
  const profileIssuer = businesses.find((business) => business.id === client.issuerId) || businesses[0];
  const clientTaxProfile = getClientTaxProfile(client, profileIssuer?.id);
  const primaryContact = primaryContactFor(client);
  const reminderContact = contactForPurpose(client, "receivables");
  const reminderInvoice = clientInvoices.find((invoice) => invoice.status === "Overdue") || clientInvoices.find((invoice) => invoice.status === "Sent");
  const contactPage = detailState?.data?.historyPages?.contacts || {};
  const taxProfilePage = detailState?.data?.historyPages?.taxProfiles || {};
  const contactCollectionPending = Boolean(contactPage.hasMore);
  const taxProfileCollectionPending = Boolean(taxProfilePage.hasMore);
  const incompleteClientSetup = contactCollectionPending || taxProfileCollectionPending;
  const clientReadOnly = serverManaged && client.canEdit !== true;
  const collectionLoading = detailState?.collectionLoading || {};
  const collectionErrors = detailState?.collectionErrors || {};
  return (
    <div className="is2-view">
      <ViewHeading
        eyebrow="Relationships"
        title="Clients"
        description="Billing preferences, activity, and receivables without digging through documents."
        actions={<div className="is2-heading-actions">{returnToSearch ? <button className="is2-button is2-button--quiet" onClick={returnToSearch}><SearchIcon size={15} /> {t("Back to search")}</button> : null}<button className="is2-button is2-button--primary is2-button--mobile-icon" disabled={privacy.enabled} title={privacy.enabled ? "Turn off public mode to add client details" : t("Add client")} onClick={() => openClient(null)}><PlusIcon size={16} /><span>{t("Add client")}</span></button></div>}
      />
      <div className="is2-master-detail">
        <section className="is2-client-list" aria-label="Client list">
          <div className="is2-client-list__header"><strong>{privacy.number(clients.length)} clients</strong><div className="is2-client-list-options" ref={clientMenuRef}><IconButton label="Client options" aria-expanded={clientMenuOpen} onClick={() => setClientMenuOpen((current) => !current)}><MoreIcon size={16} /></IconButton>{clientMenuOpen ? <div role="menu"><button type="button" role="menuitem" onClick={() => { setClientMenuOpen(false); openClient(null); }}><PlusIcon size={14} /> Add client</button>{!privacy.enabled ? <button type="button" role="menuitem" disabled={incompleteClientSetup} title={incompleteClientSetup ? "Load the remaining client setup before editing." : "Edit selected client"} onClick={() => { setClientMenuOpen(false); openClient(client); }}><EditIcon size={14} /> Edit selected client</button> : null}<button type="button" role="menuitem" onClick={() => { setClientMenuOpen(false); exportStatement(client); }}><ExportIcon size={14} /> Export selected statement</button></div> : null}</div></div>
          <div className="is2-client-list__rows">
            {activeClients.map((item) => (
              <button key={item.id || item.name} className={`is2-client-row${selected === item.id ? " is-active" : ""}`} onClick={() => { setLocalSelected(item.id); onSelectClient?.(item.id); }}>
                <span className={`is2-avatar is2-avatar--${item.tone}`}>{privacy.enabled ? privacy.clientInitials(item.name) : item.initials}</span>
                <span><strong>{privacy.client(item.name)}</strong><small>{privacy.enabled ? privacy.email(item.email) : `${t("Active in {{year}}", { year: currentYear })} · ${t(clientScopeLabel(item, businesses))}`}</small></span>
                <ChevronRightIcon size={15} />
              </button>
            ))}
            {previousClients.length ? <button className="is2-client-history-toggle" onClick={() => setShowPreviousClients((current) => !current)}><TimeIcon size={14} /><span><strong>{showPreviousClients ? t("Hide previous clients") : `${t("Show previous clients")} · ${privacy.number(previousClients.length)}`}</strong><small>{t("No invoice activity in {{year}}", { year: currentYear })}</small></span><ChevronDownIcon className={showPreviousClients ? "is-open" : ""} size={14} /></button> : null}
            {showPreviousClients ? previousClients.map((item) => {
              const latest = latestInvoiceDateForClient(item, invoices);
              return <button key={item.id || item.name} className={`is2-client-row is-previous${selected === item.id ? " is-active" : ""}`} onClick={() => { setLocalSelected(item.id); onSelectClient?.(item.id); }}>
                <span className={`is2-avatar is2-avatar--${item.tone}`}>{privacy.enabled ? privacy.clientInitials(item.name) : item.initials}</span>
                <span><strong>{privacy.client(item.name)}</strong><small>{privacy.enabled ? privacy.email(item.email) : latest ? `${t("Last invoiced")} ${formatDocumentDate(latest)}` : t("No invoice history")}</small></span>
                <ChevronRightIcon size={15} />
              </button>;
            }) : null}
          </div>
        </section>
        <section className="is2-client-detail">
          <header className="is2-client-detail__header" aria-busy={Boolean(detailState?.loading)}>
            <div className={`is2-avatar is2-avatar--large is2-avatar--${client.tone}`}>{privacy.enabled ? privacy.clientInitials(client.name) : client.initials}</div>
            <div><p className="is2-eyebrow">{t("Client profile")}</p><h2>{privacy.client(client.name)}</h2><span>{privacy.contact(primaryContact?.name || client.contact)} - {privacy.email(primaryContact?.email || client.email)}</span>{primaryContact?.phone ? <small className="is2-client-contact-phone">{privacy.phone(primaryContact.phone)} · {privacy.text(primaryContact.role || "Primary contact")}</small> : null}<small className="is2-client-scope"><OfficeIcon size={12} /> {scopeLabel}</small></div>
            {!privacy.enabled ? <button className="is2-button is2-button--quiet" disabled={clientReadOnly} title={clientReadOnly ? "This client is read-only in this workspace." : "Edit client"} onClick={() => openClient(client)}><EditIcon size={14} /><span>Edit</span></button> : <span className="is2-public-readonly">Private profile</span>}
          </header>
          <div className="is2-client-stats">
            <div><span>{t("Outstanding")}</span><strong>{privacy.amount(clientOutstanding)}</strong></div>
            <div><span>{t("Lifetime billed")}</span><strong>{privacy.amount(clientLifetime)}</strong></div>
            <div><span>{t("Defaults")}</span><strong>{privacy.enabled ? "Private terms" : `${client.currency} - ${paymentTermsLabel(client.terms)}`}</strong></div>
          </div>
          <div className="is2-client-billing-summary">
            <div><p className="is2-eyebrow">{t("Billing defaults")}</p><strong>{privacy.enabled ? "Private recurring setup" : `${client.currency} · ${paymentTermsLabel(client.terms)}`}</strong><small>{privacy.enabled ? "Terms, tax, and issuer access are hidden." : `${countryName(client.countryCode)} · ${scopeLabel} · ${taxProfileLabel(clientTaxProfile)}${profileIssuer ? ` for ${profileIssuer.name}` : ""}`}</small></div>
            {!privacy.enabled ? <button className="is2-button is2-button--quiet" disabled={clientReadOnly} title={clientReadOnly ? "This client is read-only in this workspace." : t("Manage defaults")} onClick={() => openClient(client, "defaults")}><EditIcon size={14} /> {t("Manage defaults")}</button> : null}
          </div>
          {incompleteClientSetup || collectionErrors.contacts || collectionErrors.taxProfiles ? <div className="is2-client-collection-pages" role={collectionErrors.contacts || collectionErrors.taxProfiles ? "alert" : "status"}>
            <div><strong>{collectionErrors.contacts || collectionErrors.taxProfiles ? "Some client setup could not be loaded" : "Additional client details"}</strong></div>
            <div className="is2-client-collection-pages__actions">
              {contactCollectionPending || collectionErrors.contacts ? <button type="button" disabled={collectionLoading.contacts} onClick={() => loadDetailCollection?.("contacts")}>{collectionLoading.contacts ? "Loading contacts..." : collectionErrors.contacts ? "Retry contacts" : "Load contacts"}</button> : null}
              {taxProfileCollectionPending || collectionErrors.taxProfiles ? <button type="button" disabled={collectionLoading.taxProfiles} onClick={() => loadDetailCollection?.("taxProfiles")}>{collectionLoading.taxProfiles ? "Loading tax defaults..." : collectionErrors.taxProfiles ? "Retry tax defaults" : "Load tax defaults"}</button> : null}
            </div>
          </div> : null}
          <div className="is2-client-invoices">
            <div className="is2-section-heading"><div><p className="is2-eyebrow">{t("Documents")}</p><h3>{t("Client invoices")}</h3></div><span className="is2-count">{privacy.number(invoiceCount)}</span></div>
            <div className="is2-client-invoices__list">
              {clientInvoices.map((invoice) => <div className="is2-client-invoice-row" key={invoice.id}><button onClick={() => openInvoice(invoice)}><span><strong>{privacy.invoice(invoice)}</strong><small>{privacy.work(invoice.work)} - due {privacy.date(invoice.due)}</small></span><strong>{privacy.amount(formatCurrency(invoice.amount, invoice.currency))}</strong><ChevronRightIcon size={14} /></button><InvoiceStatusControl invoice={invoice} onChange={updateInvoiceStatus} compact /></div>)}
              {invoicePage?.loading && !invoicePage.loaded ? <div className="is2-client-invoices__empty" role="status">Loading this client&apos;s invoices...</div> : null}
              {invoicePage?.error ? <div className="is2-client-invoices__paging is-error" role="alert"><span>{invoicePage.error}</span><button type="button" onClick={invoicePage.reload}>Try again</button></div> : null}
              {!invoicePage?.loading && clientInvoices.length === 0 ? <div className="is2-client-invoices__empty">No invoices for this client yet.</div> : null}
              {invoicePage?.hasMore ? <div className="is2-client-invoices__paging"><span>{privacy.number(clientInvoices.length)} of {privacy.number(invoiceCount)} loaded</span><button type="button" disabled={invoicePage.loading} onClick={invoicePage.loadMore}>{invoicePage.loading ? "Loading..." : "Load more"}</button></div> : null}
            </div>
            {detailState?.error ? <div className="is2-client-detail-warning" role="alert"><span>{detailState.error}</span><button type="button" onClick={detailState.reload}>Retry profile</button></div> : null}
          </div>
          <footer className="is2-client-detail__footer"><button className="is2-button is2-button--primary is2-client-action" onClick={() => openCreate({ clientId: client.id, issuerId: client.issuerId || businesses[0]?.id })}><PlusIcon size={16} /><span className="is2-client-action__desktop">{t("Invoice this client")}</span><span className="is2-client-action__mobile">Invoice</span></button>{!privacy.enabled && reminderInvoice && reminderContact?.phone ? <button className="is2-button is2-button--quiet is2-client-action" onClick={() => prepareInvoiceReminder(reminderInvoice)}><ChatIcon size={14} /><span className="is2-client-action__desktop">{t("WhatsApp reminder")}</span><span className="is2-client-action__mobile">Message</span></button> : null}{!privacy.enabled ? <button className="is2-text-button is2-client-action" onClick={() => exportStatement(client)}><ExportIcon size={14} /><span className="is2-client-action__desktop">{t("Export statement")}</span><span className="is2-client-action__mobile">Export</span></button> : <span className="is2-public-readonly"><EyeOffIcon size={14} /> Contact details hidden</span>}</footer>
        </section>
      </div>
    </div>
  );
};

const Services = ({ openCreate, editService, duplicateService, archiveService, services, readOnly = false, query, onQuery, includeArchived, onIncludeArchived }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const [menu, setMenu] = useState(null);
  return (
    <div className="is2-view">
      <ViewHeading
        eyebrow="Reusable work"
        title="Services"
        description="A clean catalog of what you sell, with rates that stay consistent across clients."
        actions={<button className="is2-button is2-button--primary" data-cy="new-service" disabled={readOnly} onClick={() => editService({})}><PlusIcon size={16} /> {t("New service")}</button>}
      />
      <div className="is2-service-filters"><label className="is2-choice-search"><SearchIcon size={16} /><input aria-label="Search services" placeholder="Find a service" value={query} onChange={event => onQuery(event.target.value)} /></label><label><input type="checkbox" checked={includeArchived} onChange={event => onIncludeArchived(event.target.checked)} /> Show archived</label></div>
      <section className="is2-section is2-services">
        <div className="is2-service-header"><span>{t("Service")}</span><span>{t("Default rate")}</span><span>{t("Used")}</span><span /></div>
        {services.map((service, index) => (
          <div className="is2-service-row" key={service.id || service.name}>
            <span className="is2-service-index">0{index + 1}</span>
            <span className="is2-service-main"><strong>{service.name}{service.archived ? " (archived)" : ""}</strong><small>{service.detail}</small></span>
            <span className="is2-service-rate"><strong>{privacy.amount(formatServiceRate(service))}</strong><small>{service.unit === "fixed" ? "per project" : `per ${service.unit}`}</small></span>
            <span className="is2-service-usage">{Number.isFinite(service.used) ? <><strong>{privacy.number(service.used)}</strong><small>invoices</small></> : <small>{service.issuerId ? "One issuer" : "Every issuer"}</small>}</span>
            <IconButton label={`More actions for ${service.name}`} onClick={(event) => { const rect = event.currentTarget.getBoundingClientRect(); const placed = placeFloatingPanel({ anchor: rect, width: 270, height: 256, align: "end", margin: 12, viewport: getVisualViewportBounds() }); setMenu({ service, position: { top: placed.top, left: placed.left, width: placed.width, maxHeight: placed.maxHeight } }); }}><MoreIcon size={15} /></IconButton>
          </div>
        ))}
        {!services.length ? <p className="is2-choice-empty">No services match this view.</p> : null}
        <footer className="is2-service-footer"><span>Start from a service and client defaults do the rest.</span><button className="is2-text-button" onClick={openCreate}>{t("Create invoice")} <ChevronRightIcon size={14} /></button></footer>
      </section>
      {menu ? <><button className="is2-menu-dismiss" aria-label="Close service actions" onClick={() => setMenu(null)} /><div className="is2-inline-menu" role="menu" aria-label={`Actions for ${menu.service.name}`} style={menu.position}>
        <button role="menuitem" disabled={readOnly || menu.service.archived || menu.service.capabilities?.canEdit === false} onClick={() => { setMenu(null); editService(menu.service); }}><EditIcon size={15} /><span><strong>Edit service</strong><small>Change its name, rate, or billing unit</small></span></button>
        <button role="menuitem" disabled={readOnly} onClick={() => { setMenu(null); duplicateService(menu.service); }}><DuplicateIcon size={15} /><span><strong>Duplicate service</strong><small>Create an editable copy</small></span></button>
        <button role="menuitem" disabled={menu.service.archived} onClick={() => { setMenu(null); openCreate({ serviceId: menu.service.id, issuerId: menu.service.issuerId }); }}><DocumentIcon size={15} /><span><strong>Create invoice</strong><small>Use this service as the first line item</small></span></button>
        <button className={menu.service.archived ? "" : "is-danger"} role="menuitem" disabled={readOnly || menu.service.capabilities?.canArchive === false} onClick={() => { setMenu(null); archiveService(menu.service); }}><ArchiveIcon size={15} /><span><strong>{menu.service.archived ? "Restore service" : "Archive service"}</strong><small>{menu.service.archived ? "Make available for new invoices" : "Existing invoices stay unchanged"}</small></span></button>
      </div></> : null}
    </div>
  );
};

const History = ({ entries, openInvoice, invoices }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const filters = [
    { id: "all", label: "All", matches: () => true },
    { id: "invoices", label: "Invoices", matches: (entry) => entry.entityType === "invoice" },
    { id: "operations", label: "Operations", matches: (entry) => ["intake", "bank-transaction", "quickbooks-export"].includes(entry.entityType) },
    { id: "access", label: "Organization", matches: (entry) => ["organization", "business", "client", "team-member", "profile"].includes(entry.entityType) },
    { id: "data", label: "Data", matches: (entry) => entry.entityType === "data" },
  ];
  const [filter, setFilter] = useState("all");
  const activeFilter = filters.find((item) => item.id === filter) || filters[0];
  const visible = entries.filter(activeFilter.matches);
  const formatActivityTime = (value) => new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
  return <div className="is2-view">
    <ViewHeading eyebrow="Accountability" title="Change history" description="A chronological record of who changed invoices, organization details, and local backups." />
    <div className="is2-list-toolbar"><div className="is2-segmented" role="tablist" aria-label="History type">{filters.map((item) => <button key={item.id} className={filter === item.id ? "is-active" : ""} onClick={() => setFilter(item.id)} role="tab" aria-selected={filter === item.id}>{t(item.label)}</button>)}</div></div>
    <section className="is2-section is2-history">
      <header><span>{t("Change")}</span><span>{t("Performed by")}</span><span>{t("When")}</span></header>
      {visible.map((entry) => {
        const document = entry.entityType === "invoice" ? invoices.find((invoice) => invoice.id === entry.entityId) : null;
        const Icon = entry.entityType === "invoice" ? DocumentIcon : entry.entityType === "data" ? SavedIcon : ["intake", "bank-transaction", "quickbooks-export"].includes(entry.entityType) ? CreditCardIcon : SettingsIcon;
        return <button key={entry.id} className={`is2-history-row${document ? " is-clickable" : ""}`} aria-disabled={!document} onClick={() => document && openInvoice(document)}><span className="is2-history-row__change"><span className="is2-history-row__icon"><Icon size={16} /></span><span><strong>{entry.action}</strong><small>{privacy.text(entry.detail)}</small></span></span><span><strong>{entry.actor}</strong><small>{entry.actorRole || "Owner"}</small></span><time>{privacy.date(formatActivityTime(entry.at))}</time>{document ? <ChevronRightIcon size={14} /> : <span />}</button>;
      })}
      {!visible.length ? <div className="is2-empty"><TimeIcon size={24} /><strong>{t("No changes in this category")}</strong><span>{t("Future updates will be recorded automatically.")}</span></div> : null}
    </section>
  </div>;
};

const AccountPageStatus = ({ page, noun = "records", showContinuation = true }) => {
  if (!page) return null;
  if (page.loading && !page.loaded) return <div className="is2-account-page-status" role="status"><span><i /> Refreshing {noun} from the account service...</span></div>;
  if (page.error) return <div className="is2-account-page-status is-error" role="alert"><span>{page.loaded ? `The loaded ${noun} remain visible.` : "The saved workspace view remains visible."} {page.error}</span><button type="button" className="is2-button is2-button--quiet" onClick={page.reload}>Try again</button></div>;
  if (!showContinuation || !page.hasMore) return null;
  return <div className="is2-account-page-status"><span>{page.items.length} {noun} loaded</span><button type="button" className="is2-button is2-button--quiet" disabled={page.loading} onClick={page.loadMore}>{page.loading ? "Loading..." : "Load more"}</button></div>;
};

const AccountPagedView = ({ children, page, noun }) => <>{children}<AccountPageStatus page={page} noun={noun} /></>;

const Settings = ({ organization, businesses, saveOrganization, openOrganization, openIssuer, initialSection, getNextInvoiceNumber, sessionMode, exportData, importData, interfaceLanguage = "en", setInterfaceLanguage, guestSampleWorkspace = false, resetGuestWorkspace }) => {
  const privacy = usePrivacy();
  const { t } = useUiLanguage();
  const [active, setActive] = useState(initialSection || SETTINGS_ITEMS[0][0]);
  const [organizationDraft, setOrganizationDraft] = useState(organization);
  const previousOrganization = useRef(organization);
  const [numberingBusiness, setNumberingBusiness] = useState(businesses[0]?.id || "");
  useEffect(() => {
    const previous = previousOrganization.current;
    previousOrganization.current = organization;
    // A separate logo save must not reset fields already being edited here.
    const brandingOnly = [...new Set([...Object.keys(previous), ...Object.keys(organization)])]
      .every(key => ["logo", "logoObjectKey"].includes(key) || previous[key] === organization[key]);
    setOrganizationDraft(current => brandingOnly ? { ...current, logo: organization.logo, logoObjectKey: organization.logoObjectKey } : organization);
  }, [organization]);
  useEffect(() => { if (initialSection) setActive(initialSection); }, [initialSection]);
  useEffect(() => { if (!businesses.some((business) => business.id === numberingBusiness)) setNumberingBusiness(businesses[0]?.id || ""); }, [businesses, numberingBusiness]);
  const updateOrganization = (key) => (event) => setOrganizationDraft((current) => ({ ...current, [key]: event.target.value }));
  const organizationType = "Business organization";
  const localeLabels = documentLabels(organizationDraft);
  const save = async () => {
    if (["Organization profile", "Language and locale"].includes(active)) {
      await saveOrganization(organizationDraft);
      return;
    }
  };
  return (
    <div className="is2-view is2-settings-view">
      <ViewHeading eyebrow="Account" title="Settings" description="Control your organization, issuing businesses, document defaults, and portable data." />
      <div className="is2-settings-layout">
        <section className="is2-settings-list" aria-label="Settings sections">
          {SETTINGS_ITEMS.map(([title, description]) => (
            <button className={`is2-settings-row${active === title ? " is-active" : ""}`} key={title} onClick={() => setActive(title)}><span><strong>{t(title)}</strong><small>{t(description)}</small></span><ChevronRightIcon size={16} /></button>
          ))}
        </section>
        <section className="is2-settings-panel">
          {active === "Organization profile" ? <>
            <div className="is2-settings-panel__heading"><div><p className="is2-eyebrow">{t("Account identity")}</p><h2>{t("Organization profile")}</h2><span>{t("This is the authenticated account boundary. Billing companies keep separate legal identities and number sequences.")}</span></div>{organization.logo ? <img src={organization.logo} alt={`${organization.name} logo`} /> : <span className="is2-settings-logo-fallback">{organization.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2)}</span>}</div>
            <div className="is2-settings-form is2-settings-form--two"><label>{t("Organization name")}<input value={organizationDraft.name} onChange={updateOrganization("name")} /></label><label>{t("Billing email")}<input type="email" value={organizationDraft.email} onChange={updateOrganization("email")} /></label><label>{t("Account type")}<input value={t(organizationType)} readOnly /></label><label>{t("Default currency")}<CustomSelect value={organizationDraft.currency} onChange={updateOrganization("currency")}><option>EUR</option><option>USD</option><option>GBP</option></CustomSelect></label></div>
            <button className="is2-text-button is2-settings-logo-action" onClick={openOrganization}>{t("Change organization logo")} <ChevronRightIcon size={14} /></button>
          </> : null}
          {active === "Issuing businesses" ? <>
            <div className="is2-settings-panel__heading"><div><p className="is2-eyebrow">{t("Invoice from")}</p><h2>{t("Issuing businesses")}</h2><span>{t("Each legal business keeps its own identity, logo, and yearly invoice sequence.")}</span></div><span className="is2-count">{businesses.length}</span></div>
            <div className="is2-settings-business-list">{businesses.map((business) => <div key={business.id}><span className={`is2-avatar is2-avatar--${business.tone}`}>{business.logo ? <img src={business.logo} alt="" /> : business.initials}</span><span><strong>{business.name}</strong><small>{business.legalName} - {business.email}</small></span><span><small>{t("Next invoice")}</small><strong>{privacy.enabled ? "Private invoice" : getNextInvoiceNumber(business.id)}</strong></span><IconButton label={`Edit ${business.name}`} disabled={privacy.enabled} onClick={() => openIssuer(business)}><EditIcon size={15} /></IconButton></div>)}</div>
            <button className="is2-button is2-button--quiet" disabled={privacy.enabled} onClick={() => openIssuer(null)}><PlusIcon size={15} /> {t("Add issuing business")}</button>
          </> : null}
          {active === "Invoice defaults" ? <>
            <div className="is2-settings-panel__heading"><div><p className="is2-eyebrow">{t("Documents")}</p><h2>{t("Invoice defaults")}</h2><span>{t("Applied automatically and editable on each draft.")}</span></div><DocumentIcon size={24} /></div>
            <div className="is2-settings-form is2-settings-form--two"><label>{t("Issuing business")}<CustomSelect value={numberingBusiness} onChange={(event) => setNumberingBusiness(event.target.value)}>{businesses.map((business) => <option key={business.id} value={business.id}>{business.name}</option>)}</CustomSelect></label><label>{t("Next invoice number")}<input value={privacy.enabled ? "Private invoice" : getNextInvoiceNumber(numberingBusiness)} readOnly /></label><label>{t("Default currency")}<input value={businesses.find(business => business.id === numberingBusiness)?.currency || ''} readOnly /></label></div>
            <button type="button" className="is2-button is2-button--quiet" disabled={privacy.enabled || !numberingBusiness} onClick={() => openIssuer(businesses.find(business => business.id === numberingBusiness))}>{t('Edit issuing business')}</button>
          </> : null}
          {active === "Data and backups" ? <>
            <div className="is2-settings-panel__heading"><div><p className="is2-eyebrow">{t("Continuity")}</p><h2>{t("Data and backups")}</h2><span>{t("Move or restore the complete organization without relying on an online account.")}</span></div><SavedIcon size={24} /></div>
            <div className={`is2-storage-mode is-${sessionMode}`}><SavedIcon size={18} /><span><strong>{t(sessionMode === "local" ? "Stored on this device" : "Account workspace")}</strong><small>{t(sessionMode === "local" ? "Changes persist in this browser. A backup is required before clearing browser data or changing devices." : "Portable exports remain available even when you use an account.")}</small></span></div>
            <div className="is2-settings-actions is2-settings-actions--three"><button disabled={privacy.enabled} onClick={() => exportData("json")}><DocumentIcon size={18} /><span><strong>{t("Export JSON")}</strong><small>{privacy.enabled ? "Turn off public mode to export original data" : t("Complete portable data with embedded images")}</small></span></button><button disabled={privacy.enabled} onClick={() => exportData("zip")}><DownloadIcon size={18} /><span><strong>{t("Export ZIP")}</strong><small>{privacy.enabled ? "Turn off public mode to export original data" : t("Manifest, records, and separate image files")}</small></span></button><label className={privacy.enabled ? "is-disabled" : ""}><ExportIcon size={18} /><span><strong>{t("Import backup")}</strong><small>{privacy.enabled ? "Turn off public mode before restoring data" : t("Restore a validated JSON or ZIP archive")}</small></span><input disabled={privacy.enabled} type="file" accept="application/json,application/zip,.json,.zip" onChange={(event) => { importData(event.target.files?.[0]); event.target.value = ""; }} /></label></div>
            {guestSampleWorkspace ? <div className="is2-sample-reset"><span><strong>Restore the sample workspace</strong><small>Discard changes made to the fictional sample. Your separate personal local workspace is not affected.</small></span><button className="is2-button is2-button--quiet" onClick={resetGuestWorkspace}>Restore sample</button></div> : null}
          </> : null}
          {active === "Language and locale" ? <>
            <div className="is2-settings-panel__heading"><div><p className="is2-eyebrow">{t("Formatting")}</p><h2>{t("Language and locale")}</h2><span>{t("Control generated document language, dates, and amount formatting. The dashboard language is a personal preference.")}</span></div><span className="is2-locale-mark">Aa</span></div>
            <div className="is2-settings-form is2-settings-form--two is2-language-settings"><label>{t("Interface language")}<CustomSelect data-cy="interface-language" value={interfaceLanguage} onChange={(event) => setInterfaceLanguage?.(normalizeInterfaceLanguage(event.target.value))} options={INTERFACE_LANGUAGE_OPTIONS} /><small>{t("Interface language changes the dashboard, navigation, and controls on this device.")}</small></label><label>{t("Document language")}<CustomSelect value={organizationDraft.documentLanguage || "en"} onChange={updateOrganization("documentLanguage")}><option value="en">English</option><option value="sq">Shqip</option><option value="de">Deutsch</option></CustomSelect><small>{t("Document language controls generated invoice and credit-note wording.")}</small></label><label>{t("Regional format")}<CustomSelect value={organizationDraft.regionalFormat || "XK"} onChange={updateOrganization("regionalFormat")}><option value="XK">Kosovo</option><option value="GB">United Kingdom</option><option value="US">United States</option></CustomSelect></label><label>{t("Date style")}<CustomSelect value={organizationDraft.documentDateStyle || "medium"} onChange={updateOrganization("documentDateStyle")}><option value="medium">24 Aug 2026</option><option value="numeric">24/08/2026</option><option value="us">Aug 24, 2026</option></CustomSelect></label><label>{t("Number style")}<CustomSelect value={organizationDraft.documentNumberStyle || "symbol-prefix"} onChange={updateOrganization("documentNumberStyle")}><option value="symbol-prefix">€4,840.00</option><option value="symbol-suffix">4.840,00 €</option></CustomSelect></label></div>
            <div className="is2-locale-preview" aria-live="polite"><span><small>{t("Dashboard preview")}</small><strong>{t("Invoices")}</strong></span><span><small>{t("Document preview")}</small><strong>{localeLabels.invoice}</strong></span><span><small>{localeLabels.issued}</small><strong>{formatLocalizedDocumentDate("2026-08-24", organizationDraft)}</strong></span><span><small>{localeLabels.total}</small><strong>{formatLocalizedDocumentCurrency(4840, "EUR", organizationDraft)}</strong></span></div>
          </> : null}
          {["Organization profile", "Language and locale"].includes(active) ? <footer className="is2-settings-panel__footer"><span>{t("Changes are saved to {{organization}}.", { organization: organization.name })}</span><button className="is2-button is2-button--primary" onClick={save}>{t("Save changes")}</button></footer> : null}
        </section>
      </div>
    </div>
  );
};

const PUBLIC_MODE_KEY = storageKeyForMode("invoice-studio-public-mode", "invoice-studio-public-guest-privacy-v1");
const INTERFACE_LANGUAGE_KEY = storageKeyForMode("invoice-studio-interface-language-v1", "invoice-studio-public-interface-language-v1");
const DEFAULT_LOCAL_SPACE = PUBLIC_GUEST_MODE ? "sample" : "standard";
const prototypeIdentityRepository = createPrototypeIdentityRepository({ workspaces: WORKSPACES, loginAccounts: LOGIN_ACCOUNTS });
const accountRepositoryRuntime = getConfiguredAccountRuntime();

const guestSampleData = () => ({
  workspaces: WORKSPACES,
  businesses: BUSINESSES,
  clients: CLIENTS,
  services: SERVICES,
  invoices: INVOICES,
  collectionHistory: SAMPLE_COLLECTION_HISTORY,
  invoiceStatuses: {},
  archivedInvoiceIds: [],
  activityLog: ACTIVITY_LOG,
  teamMembers: TEAM_MEMBERS,
  intakeItems: [],
  documentExpectations: [],
  bankTransactions: [],
  quickBooksExports: [],
  communications: [],
  workReminders: WORK_REMINDERS,
  workTasks: WORK_TASKS,
  workItemActivities: WORK_ITEM_ACTIVITIES,
  profile: USER_PROFILE,
  sampleWorkspace: true,
  sampleWorkspaceVersion: SAMPLE_WORKSPACE_VERSION,
});

const isoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (value, days) => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return isoDate(date);
};

const localWorkId = (prefix) => `${prefix}-${(typeof window !== "undefined" && window.crypto?.randomUUID?.()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
const SERVER_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const shortDateLabel = (value) => new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit" }).format(new Date(`${value}T12:00:00`));
const paymentTermDays = (terms) => ({ "On receipt": 0, "Net 7": 7, "Net 14": 14, "Net 30": 30 }[terms] ?? 14);

const readPublicModePreference = () => {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(PUBLIC_MODE_KEY);
  return value === null ? null : value === "true";
};

const readInterfaceLanguagePreference = (fallback = "en") => {
  if (typeof window === "undefined") return normalizeInterfaceLanguage(fallback);
  return normalizeInterfaceLanguage(window.localStorage.getItem(INTERFACE_LANGUAGE_KEY) || fallback);
};

const createLocalPersistence = (space, initialData) => openLocalSnapshotSession({ space, initialData, mode: "local", scopeId: localWorkId("local-view") });

const createStartupState = () => {
  const prototypeSession = PUBLIC_GUEST_MODE ? null : prototypeIdentityRepository.readSession();
  const session = prototypeSession?.mode === "local" ? prototypeSession : null;
  const localSpace = session?.localSpace || DEFAULT_LOCAL_SPACE;
  const persistence = session ? createLocalPersistence(localSpace) : null;
  return {
    persistence,
    session,
    data: session?.mode === "local" ? upgradeSampleWorkspace(persistence.snapshot() || readLocalData(localSpace)) : null,
    localSpace,
    authReady: !accountRepositoryRuntime || session?.mode === "local",
  };
};

const InvoiceStudioRevamp = () => {
  const [startup] = useState(createStartupState);
  const localSessionRef = useRef(startup.persistence);
  const onboardingPersistenceRef = useRef(null);
  const [accountInvitation, setAccountInvitation] = useState(() => accountRepositoryRuntime ? readAccountInvitationIntent() : null);
  const [authSession, setAuthSession] = useState(startup.session);
  const [accountWorkspaceSelection, setAccountWorkspaceSelection] = useState(null);
  const accountScope = [authSession?.authority, authSession?.userId, authSession?.organizationId].join(":");
  const accountScopeRef = useRef(accountScope);
  accountScopeRef.current = accountScope;
  const [authReady, setAuthReady] = useState(startup.authReady);
  const [localSpace, setLocalSpace] = useState(startup.localSpace);
  const [localOnboardingOpen, setLocalOnboardingOpen] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [workspaceMode, setWorkspaceMode] = useState("regular");
  const [adminSection, setAdminSection] = useState("overview");
  const [workSection, setWorkSection] = useState("calendar");
  const [workCalendarState, setWorkCalendarState] = useState(null);
  const [workCalendarRefreshKey, setWorkCalendarRefreshKey] = useState(0);
  const [expandedNavGroups, setExpandedNavGroups] = useState(navGroupState);
  const [editorOpen, setEditorOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [issuerEditor, setIssuerEditor] = useState(null);
  const [clientEditor, setClientEditor] = useState(null);
  const [serviceEditor, setServiceEditor] = useState(null);
  const [serviceQuery, setServiceQuery] = useState("");
  const [debouncedServiceQuery, setDebouncedServiceQuery] = useState("");
  const [includeArchivedServices, setIncludeArchivedServices] = useState(false);
  useEffect(() => { const timer = setTimeout(() => setDebouncedServiceQuery(serviceQuery.trim()), 180); return () => clearTimeout(timer); }, [serviceQuery]);
  const [createSeed, setCreateSeed] = useState({});
  const [searchState, setSearchState] = useState({ query: "", scope: "All" });
  const [searchWorkTarget, setSearchWorkTarget] = useState(null);
  const [editorReturnContext, setEditorReturnContext] = useState(null);
  const [recordReturnContext, setRecordReturnContext] = useState(null);
  const [focusedClientId, setFocusedClientId] = useState("");
  const [focusedBusinessId, setFocusedBusinessId] = useState("");
  const [focusedAdminMemberId, setFocusedAdminMemberId] = useState("");
  const [focusedAdminInvitation, setFocusedAdminInvitation] = useState(null);
  const [focusedIntakeItemId, setFocusedIntakeItemId] = useState("");
  const [focusedIntakeMode, setFocusedIntakeMode] = useState("summary");
  const [focusedPurchaseId, setFocusedPurchaseId] = useState("");
  const [focusedPurchaseVendor, setFocusedPurchaseVendor] = useState(null);
  const [bankStatementFilterId, setBankStatementFilterId] = useState("");
  const [bankIntakeFocusId, setBankIntakeFocusId] = useState("");
  const [settingsSection, setSettingsSection] = useState(SETTINGS_ITEMS[0][0]);
  const [settingsEditTarget, setSettingsEditTarget] = useState(null);
  const [businessScopeId, setBusinessScopeId] = useState(ALL_BUSINESSES_SCOPE);
  const [businessContactsOpen, setBusinessContactsOpen] = useState(false);
  const [businessContactTarget, setBusinessContactTarget] = useState(null);
  const [quickBooksBusinessId, setQuickBooksBusinessId] = useState("");
  const [bankingBusinessId, setBankingBusinessId] = useState("");
  const [quickBooksCandidateSourceType, setQuickBooksCandidateSourceType] = useState("sales_invoice");
  const [accountClientDetailState, setAccountClientDetailState] = useState({ id: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
  const [accountClientDetailRefreshKey, setAccountClientDetailRefreshKey] = useState(0);
  const [accountAdminBusinessDetailState, setAccountAdminBusinessDetailState] = useState({ id: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
  const [accountAdminBusinessDetailRefreshKey, setAccountAdminBusinessDetailRefreshKey] = useState(0);
  const [accountAdminMemberDetailState, setAccountAdminMemberDetailState] = useState({ id: "", kind: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
  const [accountAdminMemberDetailRefreshKey, setAccountAdminMemberDetailRefreshKey] = useState(0);
  const [workspaces, setWorkspaces] = useState(startup.data?.workspaces || WORKSPACES);
  const [businesses, setBusinesses] = useState(startup.data?.businesses || BUSINESSES);
  const [clientRecords, setClientRecords] = useState(startup.data?.clients || CLIENTS);
  const [serviceRecords, setServiceRecords] = useState(startup.data?.services || SERVICES);
  const [invoiceRecords, setInvoiceRecords] = useState(startup.data?.invoices || INVOICES);
  const [collectionHistory, setCollectionHistory] = useState(startup.data?.collectionHistory || (startup.data?.sampleWorkspace ? SAMPLE_COLLECTION_HISTORY : []));
  const [resolvedNotifications, setResolvedNotifications] = useState([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState(startup.data?.invoiceStatuses || {});
  const [archivedInvoiceIds, setArchivedInvoiceIds] = useState(startup.data?.archivedInvoiceIds || []);
  const [activityLog, setActivityLog] = useState(startup.data?.activityLog || ACTIVITY_LOG);
  const [teamMembers, setTeamMembers] = useState(startup.data?.teamMembers || TEAM_MEMBERS);
  const [intakeItems, setIntakeItems] = useState(startup.data?.intakeItems || []);
  const [localPurchaseDocuments, setLocalPurchaseDocuments] = useState(startup.data?.purchaseDocuments || []);
  const intakeItemsRef = useRef(intakeItems);
  const [documentExpectations, setDocumentExpectations] = useState(startup.data?.documentExpectations || []);
  const [declarationDrafts, setDeclarationDrafts] = useState(startup.data?.declarationDrafts || []);
  const [payrollProfiles, setPayrollProfiles] = useState(startup.data?.payrollProfiles || []);
  const [accountingSourceSummaries, setAccountingSourceSummaries] = useState(startup.data?.accountingSourceSummaries || []);
  const [accountingMappings, setAccountingMappings] = useState(startup.data?.accountingMappings || []);
  useEffect(() => { intakeItemsRef.current = intakeItems; }, [intakeItems]);
  const [vatBookPeriods, setVatBookPeriods] = useState(startup.data?.vatBookPeriods || []);
  const [fixedAssets, setFixedAssets] = useState(startup.data?.fixedAssets || []);
  const [ledgerAccounts, setLedgerAccounts] = useState(startup.data?.ledgerAccounts || []);
  const [quickBooksImportEvidence, setQuickBooksImportEvidence] = useState(startup.data?.quickBooksImportEvidence || null);
  const [bankTransactions, setBankTransactions] = useState(startup.data?.bankTransactions || []);
  const [portalPublications, setPortalPublications] = useState(startup.data?.portalPublications || []);
  const [portalDocumentRequests, setPortalDocumentRequests] = useState(startup.data?.portalDocumentRequests || []);
  const [portalNotifications, setPortalNotifications] = useState(startup.data?.portalNotifications || []);
  const [portalFinancialSnapshot, setPortalFinancialSnapshot] = useState(startup.data?.portalFinancialSnapshot || null);
  const [portalPagination, setPortalPagination] = useState(startup.data?.portalPagination || {});
  const [portalWorkspaceView, setPortalWorkspaceView] = useState(null);
  const [quickBooksExports, setQuickBooksExports] = useState(startup.data?.quickBooksExports || []);
  const [communicationRecords, setCommunicationRecords] = useState(startup.data?.communications || []);
  const [workReminderRecords, setWorkReminderRecords] = useState(startup.data?.workReminders || WORK_REMINDERS);
  const [workTaskRecords, setWorkTaskRecords] = useState(startup.data?.workTasks || WORK_TASKS);
  const [workItemActivityRecords, setWorkItemActivityRecords] = useState(startup.data?.workItemActivities || WORK_ITEM_ACTIVITIES);
  const workReminderRecordsRef = useRef(startup.data?.workReminders || WORK_REMINDERS);
  const workTaskRecordsRef = useRef(startup.data?.workTasks || WORK_TASKS);
  const declarationDraftsRef = useRef(startup.data?.declarationDrafts || []);
  const accountMutationQueueRef = useRef(Promise.resolve());
  const declarationSaveTimersRef = useRef(new Map());
  const declarationSaveVersionsRef = useRef(new Map());
  const declarationSavePromisesRef = useRef(new Map());
  const operationTabsScrollerRef = useRef(null);
  const [sampleWorkspace, setSampleWorkspace] = useState(Boolean(startup.data?.sampleWorkspace));
  const [profile, setProfile] = useState(() => {
    const storedProfile = startup.data?.profile || USER_PROFILE;
    return { ...USER_PROFILE, ...storedProfile, workspaceId: storedProfile.workspaceId || startup.data?.workspaces?.[0]?.id || USER_PROFILE.workspaceId, publicMode: readPublicModePreference() ?? storedProfile.publicMode ?? false };
  });
  const [interfaceLanguage, setInterfaceLanguage] = useState(() => readInterfaceLanguagePreference(startup.data?.profile?.interfaceLanguage));
  const [navigationPreferences, setNavigationPreferences] = useState(() => readNavigationPreferences(startup.data?.workspaces?.[0]?.id, startup.data?.profile?.id || USER_PROFILE.id));
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editorDraft, setEditorDraft] = useState(null);
  const [editorMode, setEditorMode] = useState("edit");
  const [invoiceEmailIntent, setInvoiceEmailIntent] = useState(null);
  const [whatsappRequest, setWhatsappRequest] = useState(null);
  const [toast, setToast] = useState("");
  const [mobileViewport, setMobileViewport] = useState(matchesMobileViewport);
  const [mobileExperience, setMobileExperience] = useState(() => readMobileExperience() || "minimal");
  const [accountPageAdapters, setAccountPageAdapters] = useState(null);
  const t = useMemo(() => createUiTranslator(interfaceLanguage), [interfaceLanguage]);

  const activeLabel = useMemo(() => {
    if (activeView === "admin") return ADMIN_NAV_ITEMS.find((item) => item.section === adminSection)?.label || "Administration";
    if (activeView === "work") return workSection === "calendar" ? "Work calendar" : workSection === "reminders" ? "Reminders" : workSection === "routines" ? "Routines" : "Tasks";
    return [...NAV_ITEMS, ...OPERATION_NAV_ITEMS].find((item) => item.id === activeView)?.label || ({ account: "Account", settings: "Settings", more: "More", search: "Search", notifications: "Notifications" }[activeView] || "Invoice Studio");
  }, [activeView, adminSection, workSection]);
  const publicMode = Boolean(profile.publicMode);
  const canManage = canManageOrganization(profile);
  const accountWorkspaceActive = Boolean(accountRepositoryRuntime && authSession?.authority === "supabase");
  const enabledWorkNavItems = useMemo(() => WORK_NAV_ITEMS.filter((item) => navigationPreferences[item.id] !== false && (item.id !== "routines" || accountWorkspaceActive)), [accountWorkspaceActive, navigationPreferences]);
  const enabledWorkspaceNavItems = useMemo(() => WORKSPACE_NAV_ITEMS.filter((item) => navigationPreferences[item.id] !== false), [navigationPreferences]);
  const availableOperationItems = useMemo(() => navigationPreferences.accounting ? OPERATION_NAV_ITEMS.filter((item) => canUseAccountingOperation(profile, item.id)) : [], [navigationPreferences.accounting, profile]);
  const availableDestinationGroups = useMemo(() => availableAccountingGroups([
    NAV_ITEMS.find((item) => item.id === "invoices"),
    ...availableOperationItems,
  ]), [availableOperationItems]);
  const regularNavigationItems = useMemo(() => [
    NAV_ITEMS.find((item) => item.id === "overview"),
    ...(enabledWorkNavItems.length > 1
      ? [WORK_NAV_ITEM]
      : enabledWorkNavItems.map((item) => ({ ...item, id: "work", workSection: item.id }))),
    ...(navigationPreferences.clients ? [NAV_ITEMS.find((item) => item.id === "clients")] : []),
    ...availableDestinationGroups.map((group) => group.items.length === 1
      ? group.items[0]
      : {
          id: `destination-${group.id}`,
          label: group.label,
          icon: group.id === "invoices" ? DocumentIcon : group.id === "documents" ? ArchiveIcon : ChartIcon,
          destinationGroup: group.id,
        }),
    ...(enabledWorkspaceNavItems.length > 1 ? [WORKSPACE_NAV_ITEM] : enabledWorkspaceNavItems),
  ], [availableDestinationGroups, enabledWorkNavItems, enabledWorkspaceNavItems, navigationPreferences.clients]);
  const activeOperationItem = availableOperationItems.find((item) => item.id === activeView);
  const activeDestinationGroup = accountingGroupFor(availableDestinationGroups, activeView);
  useEffect(() => {
    const scroller = operationTabsScrollerRef.current;
    if (!scroller || !activeOperationItem) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const active = scroller.querySelector("button.is-active");
      if (!active) return;
      const left = active.offsetLeft - ((scroller.clientWidth - active.offsetWidth) / 2);
      const targetLeft = Math.max(0, left);
      if (typeof scroller.scrollTo === "function") scroller.scrollTo({ left: targetLeft, behavior: "auto" });
      else scroller.scrollLeft = targetLeft;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeOperationItem, activeView, mobileViewport]);
  const portalUser = Boolean(profile?.role === "Business User" && profile.status === "Active" && profile.workspaceId && profile.businessIds?.length);
  const staffScopedWorkspace = useMemo(() => portalUser ? EMPTY_PORTAL_WORKSPACE : scopedWorkspaceRepository.read({
    organizationId: authSession?.organizationId,
    profile,
    records: {
      workspaces,
      businesses,
      clients: clientRecords,
      services: serviceRecords,
      invoices: invoiceRecords,
      invoiceStatuses,
      archivedInvoiceIds,
      intakeItems,
      documentExpectations,
      bankTransactions,
      portalPublications,
      portalDocumentRequests,
      portalNotifications,
      quickBooksExports,
      communications: communicationRecords,
      workReminders: workReminderRecords,
      workTasks: workTaskRecords,
      workItemActivities: workItemActivityRecords,
      activityLog,
      teamMembers,
    },
  }), [activityLog, archivedInvoiceIds, authSession?.organizationId, bankTransactions, businesses, clientRecords, communicationRecords, documentExpectations, intakeItems, invoiceRecords, invoiceStatuses, portalDocumentRequests, portalNotifications, portalPublications, portalUser, profile, quickBooksExports, serviceRecords, teamMembers, workItemActivityRecords, workReminderRecords, workTaskRecords, workspaces]);
  useEffect(() => {
    if (!portalUser) {
      setPortalWorkspaceView(null);
      return undefined;
    }
    let active = true;
    import(/* webpackChunkName: "business-portal-model" */ "./repositories/businessPortalWorkspaceRepository").then(({ businessPortalWorkspaceRepository }) => {
      if (!active) return;
      setPortalWorkspaceView(businessPortalWorkspaceRepository.read({
        organizationId: authSession?.organizationId,
        profile,
        records: {
          workspaces,
          businesses,
          clients: clientRecords,
          services: serviceRecords,
          invoices: invoiceRecords,
          invoiceStatuses,
          archivedInvoiceIds,
          intakeItems,
          documentExpectations,
          bankTransactions,
          portalPublications,
          portalDocumentRequests,
          portalNotifications,
          quickBooksExports,
          communications: communicationRecords,
          workReminders: workReminderRecords,
          workTasks: workTaskRecords,
          workItemActivities: workItemActivityRecords,
          activityLog,
          teamMembers,
        },
      }));
    });
    return () => { active = false; };
  }, [activityLog, archivedInvoiceIds, authSession?.organizationId, bankTransactions, businesses, clientRecords, communicationRecords, documentExpectations, intakeItems, invoiceRecords, invoiceStatuses, portalDocumentRequests, portalNotifications, portalPublications, portalUser, profile, quickBooksExports, serviceRecords, teamMembers, workItemActivityRecords, workReminderRecords, workTaskRecords, workspaces]);
  const scopedWorkspace = portalUser ? portalWorkspaceView || EMPTY_PORTAL_WORKSPACE : staffScopedWorkspace;
  const organization = scopedWorkspace.organization;
  const portalBusiness = portalUser
    ? scopedWorkspace.businesses.find((item) => item.id === portalFinancialSnapshot?.businessId) || scopedWorkspace.businesses[0]
    : null;
  const organizationBusinesses = scopedWorkspace.businesses;
  const organizationClients = scopedWorkspace.clients;
  const organizationServices = scopedWorkspace.services;
  const allInvoices = scopedWorkspace.invoices;
  const organizationInvoices = scopedWorkspace.invoices;
  const accessibleActivityLog = scopedWorkspace.activityLog;
  const effectiveBusinessScopeId = resolveBusinessScopeId(organizationBusinesses, businessScopeId, !accountWorkspaceActive || authSession.businessDirectoryComplete === true);
  const dailyWorkspace = useMemo(() => scopeWorkspaceByBusiness({
    businesses: organizationBusinesses,
    clients: organizationClients,
    services: organizationServices,
    invoices: organizationInvoices,
    intakeItems: scopedWorkspace.intakeItems,
    documentExpectations: scopedWorkspace.documentExpectations,
    bankTransactions: scopedWorkspace.bankTransactions,
    quickBooksExports: scopedWorkspace.quickBooksExports,
    communications: scopedWorkspace.communications,
    workReminders: scopedWorkspace.workReminders,
    workTasks: scopedWorkspace.workTasks,
    workItemActivities: scopedWorkspace.workItemActivities,
    activityLog: accessibleActivityLog,
  }, effectiveBusinessScopeId), [accessibleActivityLog, effectiveBusinessScopeId, organizationBusinesses, organizationClients, organizationInvoices, organizationServices, scopedWorkspace.bankTransactions, scopedWorkspace.communications, scopedWorkspace.documentExpectations, scopedWorkspace.intakeItems, scopedWorkspace.quickBooksExports, scopedWorkspace.workItemActivities, scopedWorkspace.workReminders, scopedWorkspace.workTasks]);
  const dailyBusinesses = dailyWorkspace.businesses;
  const dailyClients = dailyWorkspace.clients;
  const dailyServices = dailyWorkspace.services;
  const dailyInvoices = dailyWorkspace.invoices;
  const dailyCollectionHistory = effectiveBusinessScopeId === ALL_BUSINESSES_SCOPE
    ? collectionHistory
    : collectionHistory.filter((period) => !period.businessId || period.businessId === effectiveBusinessScopeId);
  const dailyIntakeItems = dailyWorkspace.intakeItems;
  const organizationBusinessIds = useMemo(() => new Set(organizationBusinesses.map((business) => business.id)), [organizationBusinesses]);
  const organizationAccountingSources = useMemo(() => accountingSourceSummaries.filter((source) => !source.businessId || organizationBusinessIds.has(source.businessId)), [accountingSourceSummaries, organizationBusinessIds]);
  const dailyBusinessIds = useMemo(() => new Set(dailyBusinesses.map((business) => business.id)), [dailyBusinesses]);
  const dailyDeclarationDrafts = useMemo(() => declarationDrafts.filter((draft) => dailyBusinessIds.has(draft.scope?.businessId)), [dailyBusinessIds, declarationDrafts]);
  const dailyPayrollProfiles = useMemo(() => payrollProfiles.filter((employee) => dailyBusinessIds.has(employee.businessId)), [dailyBusinessIds, payrollProfiles]);
  const dailyAccountingSources = useMemo(() => organizationAccountingSources.filter((source) => !source.businessId || dailyBusinessIds.has(source.businessId)), [dailyBusinessIds, organizationAccountingSources]);
  const dailyBankTransactions = dailyWorkspace.bankTransactions;
  const dailyQuickBooksExports = dailyWorkspace.quickBooksExports;
  const dailyCommunications = dailyWorkspace.communications;
  const dailyWorkReminders = dailyWorkspace.workReminders;
  const dailyWorkTasks = dailyWorkspace.workTasks;
  const dailyWorkActivities = dailyWorkspace.workItemActivities;
  const dailyActivityLog = dailyWorkspace.activityLog;
  const dailyScopeLabel = effectiveBusinessScopeId === ALL_BUSINESSES_SCOPE
    ? "All issuing businesses"
    : dailyBusinesses[0]?.name || "Issuing business";
  const invoices = dailyInvoices.filter((invoice) => !invoice.archived);
  const selectedDocument = organizationInvoices.find((invoice) => invoice.id === selectedInvoice?.id) || (selectedInvoice && selectedInvoice.workspaceId === organization?.id && canAccessInvoice(profile, selectedInvoice) ? selectedInvoice : null);
  const getNextInvoiceNumber = (issuerId, year = new Date().getFullYear()) => nextInvoiceNumber(allInvoices, issuerId, year);
  const showIssuer = effectiveBusinessScopeId === ALL_BUSINESSES_SCOPE && dailyBusinesses.length > 1;
  const sessionMode = authSession?.mode || "account";
  const serverAccount = accountWorkspaceActive;
  const accountReadEnabled = serverAccount && Boolean(organization?.id) && !portalUser;
  const ledgerAccessEnabled = accountReadEnabled && canUseAccountingOperation(profile, "ledger");
  useEffect(() => {
    if (!accountReadEnabled || accountPageAdapters) return undefined;
    let active = true;
    import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters").then((module) => {
      if (active) setAccountPageAdapters(module);
    });
    return () => { active = false; };
  }, [accountPageAdapters, accountReadEnabled]);
  const accountBusinessId = effectiveBusinessScopeId === ALL_BUSINESSES_SCOPE ? null : effectiveBusinessScopeId;
  const selectedBankingBusinessId = accountBusinessId || dailyBusinesses.find(business => business.id === bankingBusinessId)?.id || dailyBusinesses[0]?.id || "";
  const changeBankingBusiness = useCallback(businessId => {
    setBankingBusinessId(businessId); setBankStatementFilterId("");
  }, []);
  const selectedQuickBooksBusinessId = accountBusinessId
    || dailyBusinesses.find((business) => business.id === quickBooksBusinessId)?.id
    || dailyBusinesses[0]?.id
    || "";
  const searchAccountPage = useCallback((filters) => {
    if (!accountReadEnabled || !organization?.id || !accountRepositoryRuntime?.reads?.searchPage) return Promise.resolve({ ok: false, error: "Account search is unavailable." });
    return accountRepositoryRuntime.reads.searchPage({ ...filters, organizationId: organization.id, businessId: accountBusinessId });
  }, [accountBusinessId, accountReadEnabled, organization?.id]);
  const businessPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled,
    list: accountRepositoryRuntime?.reads?.businesses?.list,
    query: { organizationId: organization?.id, limit: 50 },
  });
  const invoiceReadSurface = ["overview", "invoices", "clients", "banking", "history", "reports", "search", "notifications"].includes(activeView)
    || editorOpen || createOpen || searchOpen || notificationsOpen;
  const clientReadSurface = ["clients", "intake", "search", "notifications", "admin"].includes(activeView)
    || editorOpen || createOpen || Boolean(clientEditor);
  const invoicePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && invoiceReadSurface,
    refreshKey: activeView,
    refreshWhenVisible: !editorOpen && !createOpen,
    list: accountRepositoryRuntime?.reads?.invoices?.list,
    query: { organizationId: organization?.id, businessId: activeView === "banking" ? selectedBankingBusinessId : accountBusinessId, limit: 50, includeArchived: true },
  });
  const clientPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && clientReadSurface,
    list: accountRepositoryRuntime?.reads?.clients?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, includeArchived: true },
  });
  const servicePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && (activeView === "services" || createOpen || serviceEditor !== null),
    list: accountRepositoryRuntime?.reads?.services?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 25, query: debouncedServiceQuery, includeArchived: includeArchivedServices },
  });
  const [sharedTaskOrder, setSharedTaskOrder] = useState(false);
  const taskPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "work" && workSection === "tasks",
    list: sharedTaskOrder ? accountRepositoryRuntime?.reads?.tasks?.board : accountRepositoryRuntime?.reads?.tasks?.list,
    preserveOnReload: sharedTaskOrder,
    requireStableOrder: sharedTaskOrder,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, ...(!sharedTaskOrder ? { includeArchived: true } : {}) },
  });
  const reminderPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "work" && workSection === "reminders",
    list: accountRepositoryRuntime?.reads?.reminders?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, includeArchived: true },
  });
  const actionPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && (activeView === "overview" || activeView === "notifications" || notificationsOpen),
    list: accountRepositoryRuntime?.reads?.actions?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, dueDays: 14 },
  });
  const routinePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "work" && workSection === "routines",
    list: accountRepositoryRuntime?.reads?.routines?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, includeArchived: false },
  });
  const routineOccurrencePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "work" && workSection === "routines",
    list: accountRepositoryRuntime?.reads?.routines?.occurrences,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const [intakeQueueFilter, setIntakeQueueFilter] = useState({ scope: '', view: 'pending' });
  const intakeQueueScope = `${accountScope || sessionMode}/${effectiveBusinessScopeId}`;
  const intakeQueueView = intakeQueueFilter.scope === intakeQueueScope ? intakeQueueFilter.view : 'pending';
  const intakeList = accountRepositoryRuntime?.reads?.intake?.list;
  const intakeQueueReader = useMemo(() => intakeList ? createIntakeQueueReader(intakeList) : null, [intakeList]);
  const intakePage = useAccountPage({
    scopeKey: accountScope,
    preserveOnReload: true,
    enabled: accountReadEnabled && ["intake", "banking"].includes(activeView),
    list: intakeQueueReader,
    query: { organizationId: organization?.id, businessId: activeView === "banking" ? selectedBankingBusinessId : accountBusinessId, limit: 50, includeArchived: activeView === 'banking', queueView: activeView === 'intake' ? intakeQueueView : null },
  });
  const purchaseVendorPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && ["intake", "purchases"].includes(activeView),
    list: accountRepositoryRuntime?.reads?.purchaseVendors?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50, includeArchived: true },
  });
  const [purchaseArchiveFilter, setPurchaseArchiveFilter] = useState({ scope: "", enabled: false });
  const purchaseFilterScope = `${accountScope}/${accountBusinessId || "all"}`;
  const includeArchivedPurchases = purchaseArchiveFilter.scope === purchaseFilterScope && purchaseArchiveFilter.enabled;
  const purchaseDocumentPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && ["purchases", "banking"].includes(activeView),
    list: accountRepositoryRuntime?.reads?.purchaseDocuments?.list,
    query: { organizationId: organization?.id, businessId: activeView === "banking" ? selectedBankingBusinessId : accountBusinessId, limit: 50, includeArchived: activeView === "banking" ? false : includeArchivedPurchases },
  });
  const bankStatementPage = useAccountPage({
    scopeKey: accountScope,
    preserveOnReload: true,
    enabled: accountReadEnabled && activeView === "banking",
    list: accountRepositoryRuntime?.reads?.bankStatements?.list,
    query: { organizationId: organization?.id, businessId: selectedBankingBusinessId, limit: 25 },
  });
  const bankTransactionPage = useAccountPage({
    scopeKey: accountScope,
    preserveOnReload: true,
    enabled: accountReadEnabled && activeView === "banking",
    list: accountRepositoryRuntime?.reads?.bankTransactions?.list,
    query: { organizationId: organization?.id, businessId: selectedBankingBusinessId, statementId: bankStatementFilterId || null, limit: 50 },
  });
  const auditPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && (activeView === "history" || (activeView === "admin" && adminSection === "history")),
    list: accountRepositoryRuntime?.reads?.audit?.list,
    query: { organizationId: organization?.id, businessId: workspaceMode === "admin" ? null : accountBusinessId, limit: 50 },
  });
  const quickBooksExportPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "quickbooks",
    list: accountRepositoryRuntime?.reads?.quickBooksExports?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const quickBooksCandidatePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "quickbooks" && Boolean(selectedQuickBooksBusinessId),
    list: accountRepositoryRuntime?.reads?.quickBooksExports?.candidates,
    query: {
      organizationId: organization?.id,
      businessId: selectedQuickBooksBusinessId,
      sourceType: quickBooksCandidateSourceType,
      limit: 50,
    },
  });
  const accountingMappingPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "quickbooks",
    list: accountRepositoryRuntime?.reads?.accountingMappings?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const ledgerReadEnabled = accountReadEnabled && activeView === "ledger" && Boolean(accountBusinessId);
  const ledgerAccountPage = useAccountPage({
    scopeKey: accountScope,
    enabled: ledgerReadEnabled,
    list: accountRepositoryRuntime?.reads?.ledger?.accounts,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const journalEntryPage = useAccountPage({
    scopeKey: accountScope,
    enabled: ledgerReadEnabled,
    list: accountRepositoryRuntime?.reads?.ledger?.journals,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const fixedAssetPage = useAccountPage({
    scopeKey: accountScope,
    enabled: ledgerReadEnabled,
    list: accountRepositoryRuntime?.reads?.ledger?.fixedAssets,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const declarationPage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "declarations",
    list: accountRepositoryRuntime?.reads?.declarations?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const payrollEmployeePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "declarations",
    list: accountRepositoryRuntime?.reads?.payrollEmployees?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, limit: 50 },
  });
  const administrationReadEnabled = accountReadEnabled && canManage && activeView === "admin";
  const adminBusinessPage = useAccountPage({
    scopeKey: accountScope,
    enabled: administrationReadEnabled,
    list: accountRepositoryRuntime?.reads?.administration?.businesses,
    query: { organizationId: organization?.id, limit: 50, includeArchived: false },
  });
  const adminMemberPage = useAccountPage({
    scopeKey: accountScope,
    enabled: administrationReadEnabled,
    list: accountRepositoryRuntime?.reads?.administration?.members,
    query: { organizationId: organization?.id, limit: 50, includeInactive: true },
  });
  const adminInvitationPage = useAccountPage({
    scopeKey: accountScope,
    enabled: administrationReadEnabled,
    list: accountRepositoryRuntime?.reads?.administration?.invitations,
    query: { organizationId: organization?.id, limit: 50, includeInactive: false },
  });
  const [accountAdministrationOverview, setAccountAdministrationOverview] = useState(null);
  const [accountAdministrationOverviewError, setAccountAdministrationOverviewError] = useState("");
  const loadAccountAdministrationOverview = useCallback(async () => {
    if (!administrationReadEnabled || typeof accountRepositoryRuntime?.reads?.administration?.overview !== "function") return null;
    const result = await accountRepositoryRuntime.reads.administration.overview(organization.id);
    if (!result?.ok) {
      setAccountAdministrationOverviewError(result?.error || "Administration totals could not be loaded.");
      return result;
    }
    setAccountAdministrationOverview(result.data);
    setAccountAdministrationOverviewError("");
    return result;
  }, [administrationReadEnabled, organization?.id]);
  useEffect(() => {
    if (!administrationReadEnabled) {
      setAccountAdministrationOverview(null);
      setAccountAdministrationOverviewError("");
      return undefined;
    }
    loadAccountAdministrationOverview();
    return undefined;
  }, [administrationReadEnabled, loadAccountAdministrationOverview]);
  useEffect(() => {
    if (!accountReadEnabled || !accountPageAdapters || !businessPage.loaded) return;
    const mapped = businessPage.items.map((item, index) => ({
      ...accountPageAdapters.mapAccountBusiness(item, {}, index),
      logo: accountRepositoryRuntime?.brandAssets?.getPublicUrl?.(item.logoObjectKey) || "",
    }));
    setBusinesses((current) => [
      ...current.filter((business) => business.workspaceId !== organization.id),
      ...mapped,
    ]);
    if (!canManage) {
      setProfile((current) => current.workspaceId !== organization.id ? current : ({
        ...current,
        businessIds: mapped.map((business) => business.id),
        accountingPermissions: [...new Set(mapped.flatMap((business) => business.operations || []))],
      }));
    }
  }, [accountPageAdapters, accountReadEnabled, businessPage.items, businessPage.loaded, canManage, organization?.id]);
  const accountInvoices = useMemo(() => accountReadEnabled && accountPageAdapters && invoicePage.loaded
    ? accountPageAdapters.mergeAccountPage(invoicePage.items, dailyInvoices, accountPageAdapters.mapAccountInvoice)
    : dailyInvoices, [accountPageAdapters, accountReadEnabled, dailyInvoices, invoicePage.items, invoicePage.loaded]);
  const accountClients = useMemo(() => accountReadEnabled && accountPageAdapters && clientPage.loaded
    ? accountPageAdapters.mergeAccountPage(clientPage.items, dailyClients, accountPageAdapters.mapAccountClient)
    : dailyClients, [accountPageAdapters, accountReadEnabled, clientPage.items, clientPage.loaded, dailyClients]);
  const selectedClientWorkspaceId = useMemo(() => {
    if (serverAccount && SERVER_UUID_PATTERN.test(focusedClientId)) return focusedClientId;
    const explicit = accountClients.find((client) => client.id === focusedClientId && !client.archived);
    if (explicit) return explicit.id;
    return sortClientsByInvoiceRecency(accountClients.filter((client) => !client.archived), accountInvoices)[0]?.id || "";
  }, [accountClients, accountInvoices, focusedClientId, serverAccount]);
  const accountClientWorkspaceId = serverAccount && SERVER_UUID_PATTERN.test(selectedClientWorkspaceId)
    ? selectedClientWorkspaceId
    : "";
  const clientInvoicePage = useAccountPage({
    scopeKey: accountScope,
    enabled: accountReadEnabled && activeView === "clients" && Boolean(accountClientWorkspaceId),
    list: accountRepositoryRuntime?.reads?.invoices?.list,
    query: { organizationId: organization?.id, businessId: accountBusinessId, clientId: accountClientWorkspaceId, limit: 25, includeArchived: false },
  });
  useEffect(() => {
    if (!accountReadEnabled || activeView !== "clients" || !accountClientWorkspaceId || typeof accountRepositoryRuntime?.reads?.clients?.detail !== "function") {
      setAccountClientDetailState({ id: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
      return undefined;
    }
    let active = true;
    setAccountClientDetailState((current) => ({
      id: accountClientWorkspaceId,
      data: current.id === accountClientWorkspaceId ? current.data : null,
      loading: true,
      error: "",
      collectionLoading: {},
      collectionErrors: {},
    }));
    accountRepositoryRuntime.reads.clients.detail(organization.id, accountClientWorkspaceId).then((result) => {
      if (!active) return;
      const client = result?.data?.client;
      if (!result?.ok || client?.id !== accountClientWorkspaceId || client?.organizationId !== organization.id || (accountBusinessId && client.issuerId && client.issuerId !== accountBusinessId)) {
        setAccountClientDetailState({ id: accountClientWorkspaceId, data: null, loading: false, error: result?.error || "This client profile could not be loaded.", collectionLoading: {}, collectionErrors: {} });
        return;
      }
      setAccountClientDetailState({ id: accountClientWorkspaceId, data: result.data, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
    });
    return () => { active = false; };
  }, [accountBusinessId, accountClientDetailRefreshKey, accountClientWorkspaceId, accountReadEnabled, activeView, organization?.id]);
  const loadClientDetailCollection = useCallback(async (kind) => {
    const config = kind === "contacts"
      ? { field: "contacts", read: accountRepositoryRuntime?.reads?.clients?.contacts }
      : kind === "taxProfiles"
        ? { field: "taxProfiles", read: accountRepositoryRuntime?.reads?.clients?.taxProfiles }
        : null;
    if (!config || typeof config.read !== "function" || !accountClientWorkspaceId || !organization?.id) return;
    const page = accountClientDetailState.id === accountClientWorkspaceId
      ? accountClientDetailState.data?.historyPages?.[config.field]
      : null;
    if (!page?.nextCursor || accountClientDetailState.collectionLoading?.[kind]) return;

    setAccountClientDetailState((current) => current.id !== accountClientWorkspaceId ? current : ({
      ...current,
      collectionLoading: { ...current.collectionLoading, [kind]: true },
      collectionErrors: { ...current.collectionErrors, [kind]: "" },
    }));
    const result = await config.read({
      organizationId: organization.id,
      clientId: accountClientWorkspaceId,
      limit: 25,
      cursor: page.nextCursor,
    });
    setAccountClientDetailState((current) => {
      if (current.id !== accountClientWorkspaceId) return current;
      if (current.data?.historyPages?.[config.field]?.nextCursor !== page.nextCursor) return current;
      if (!result?.ok) return {
        ...current,
        collectionLoading: { ...current.collectionLoading, [kind]: false },
        collectionErrors: { ...current.collectionErrors, [kind]: result?.error || "These client records could not be loaded." },
      };
      const existingItems = Array.isArray(current.data?.[config.field]) ? current.data[config.field] : [];
      const incomingItems = Array.isArray(result.data?.items) ? result.data.items : [];
      const mergedItems = [...existingItems, ...incomingItems.filter((item) => !existingItems.some((existing) => existing.id === item.id))];
      return {
        ...current,
        data: {
          ...current.data,
          [config.field]: mergedItems,
          historyPages: {
            ...(current.data?.historyPages || {}),
            [config.field]: {
              hasMore: Boolean(result.data?.hasMore),
              nextCursor: result.data?.nextCursor || null,
            },
          },
        },
        collectionLoading: { ...current.collectionLoading, [kind]: false },
        collectionErrors: { ...current.collectionErrors, [kind]: "" },
      };
    });
  }, [accountClientDetailState.collectionLoading, accountClientDetailState.data, accountClientDetailState.id, accountClientWorkspaceId, organization?.id]);
  const selectedAccountClientDetail = useMemo(() => {
    const fallback = accountClients.find((client) => client.id === selectedClientWorkspaceId);
    if (!accountPageAdapters?.mapAccountClientDetail || accountClientDetailState.id !== selectedClientWorkspaceId || !accountClientDetailState.data) return fallback;
    return accountPageAdapters.mapAccountClientDetail(accountClientDetailState.data, fallback || {}, Math.max(0, accountClients.indexOf(fallback)));
  }, [accountClientDetailState.data, accountClientDetailState.id, accountClients, accountPageAdapters, selectedClientWorkspaceId]);
  const accountClientsForClientView = useMemo(() => selectedAccountClientDetail
    ? [selectedAccountClientDetail, ...accountClients.filter((client) => client.id !== selectedAccountClientDetail.id)]
    : accountClients, [accountClients, selectedAccountClientDetail]);
  const accountClientInvoices = useMemo(() => serverAccount && accountPageAdapters
    ? sortInvoices(clientInvoicePage.items.map((item) => accountPageAdapters.mapAccountInvoice(
      item,
      accountInvoices.find((invoice) => invoice.id === item.id) || {},
    )), "issued-desc")
    : accountInvoices, [accountInvoices, accountPageAdapters, clientInvoicePage.items, serverAccount]);
  const accountServices = useMemo(() => accountReadEnabled && accountPageAdapters && servicePage.loaded
    ? accountPageAdapters.mergeAccountPage(servicePage.items, dailyServices, accountPageAdapters.mapAccountService)
    : dailyServices, [accountPageAdapters, accountReadEnabled, dailyServices, servicePage.items, servicePage.loaded]);
  const accountTasks = useMemo(() => accountReadEnabled && accountPageAdapters && taskPage.loaded
    ? accountPageAdapters.mergeAccountPage(taskPage.items, dailyWorkTasks, accountPageAdapters.mapAccountWorkItem)
    : dailyWorkTasks, [accountPageAdapters, accountReadEnabled, dailyWorkTasks, taskPage.items, taskPage.loaded]);
  const accountReminders = useMemo(() => accountReadEnabled && accountPageAdapters && reminderPage.loaded
    ? accountPageAdapters.mergeAccountPage(reminderPage.items, dailyWorkReminders, accountPageAdapters.mapAccountWorkItem)
    : dailyWorkReminders, [accountPageAdapters, accountReadEnabled, dailyWorkReminders, reminderPage.items, reminderPage.loaded]);
  const accountNotificationActions = useMemo(() => accountReadEnabled && accountPageAdapters && actionPage.loaded
    ? actionPage.items.map(accountPageAdapters.mapAccountNotificationAction)
    : [], [accountPageAdapters, accountReadEnabled, actionPage.items, actionPage.loaded]);
  const accountRoutines = accountReadEnabled && routinePage.loaded ? routinePage.items : [];
  const accountRoutineOccurrences = accountReadEnabled && routineOccurrencePage.loaded ? routineOccurrencePage.items : [];
  const intakeFallbackItems = activeView === "archive" ? scopedWorkspace.intakeItems : dailyIntakeItems;
  const accountIntakeItems = useMemo(() => accountReadEnabled && accountPageAdapters && intakePage.loaded
    ? accountPageAdapters.mergeAccountPage(intakePage.items, intakeFallbackItems, accountPageAdapters.mapAccountIntakeItem)
    : accountReadEnabled && activeView === 'intake' ? [] : intakeFallbackItems, [accountPageAdapters, accountReadEnabled, activeView, intakeFallbackItems, intakePage.items, intakePage.loaded]);
  const accountPurchaseVendors = useMemo(() => accountReadEnabled && accountPageAdapters && purchaseVendorPage.loaded
    ? accountPageAdapters.mergeAccountPage(purchaseVendorPage.items, [], accountPageAdapters.mapAccountPurchaseVendor)
    : [], [accountPageAdapters, accountReadEnabled, purchaseVendorPage.items, purchaseVendorPage.loaded]);
  const accountPurchaseDocuments = useMemo(() => accountReadEnabled && accountPageAdapters && purchaseDocumentPage.loaded
    ? accountPageAdapters.mergeAccountPage(purchaseDocumentPage.items, [], accountPageAdapters.mapAccountPurchaseDocument)
    : [], [accountPageAdapters, accountReadEnabled, purchaseDocumentPage.items, purchaseDocumentPage.loaded]);
  const accountBankStatements = useMemo(() => accountReadEnabled && accountPageAdapters && bankStatementPage.loaded
    ? bankStatementPage.items.map((item) => accountPageAdapters.mapAccountBankStatement(item))
    : [], [accountPageAdapters, accountReadEnabled, bankStatementPage.items, bankStatementPage.loaded]);
  useEffect(() => {
    if (!serverAccount || activeView !== "banking" || !bankIntakeFocusId || !bankStatementPage.loaded || bankStatementPage.loading) return;
    const statement = accountBankStatements.find((item) => item.intakeRecordId === bankIntakeFocusId);
    setBankStatementFilterId(statement?.id || "");
    setBankIntakeFocusId("");
    if (!statement) setToast("The statement is accepted. Showing the loaded banking transactions.");
  }, [accountBankStatements, activeView, bankIntakeFocusId, bankStatementPage.loaded, bankStatementPage.loading, serverAccount]);
  const accountBankTransactions = useMemo(() => accountReadEnabled && accountPageAdapters && bankTransactionPage.loaded
    ? accountPageAdapters.mergeAccountPage(bankTransactionPage.items, dailyBankTransactions, accountPageAdapters.mapAccountBankTransaction)
    : dailyBankTransactions, [accountPageAdapters, accountReadEnabled, bankTransactionPage.items, bankTransactionPage.loaded, dailyBankTransactions]);
  const historyFallbackItems = workspaceMode === "admin" ? accessibleActivityLog : dailyActivityLog;
  const accountAuditEntries = useMemo(() => accountReadEnabled && accountPageAdapters && auditPage.loaded
    ? accountPageAdapters.mergeAccountPage(auditPage.items, historyFallbackItems, accountPageAdapters.mapAccountAuditEvent)
    : historyFallbackItems, [accountPageAdapters, accountReadEnabled, auditPage.items, auditPage.loaded, historyFallbackItems]);
  const communicationFallbackItems = workspaceMode === "admin" ? scopedWorkspace.communications : dailyCommunications;
  const accountCommunications = communicationFallbackItems;
  const accountQuickBooksExports = useMemo(() => accountReadEnabled && accountPageAdapters && quickBooksExportPage.loaded
    ? accountPageAdapters.mergeAccountPage(quickBooksExportPage.items, dailyQuickBooksExports, accountPageAdapters.mapAccountQuickBooksExport)
    : dailyQuickBooksExports, [accountPageAdapters, accountReadEnabled, dailyQuickBooksExports, quickBooksExportPage.items, quickBooksExportPage.loaded]);
  const accountAccountingMappings = useMemo(() => {
    if (!accountReadEnabled || !accountPageAdapters || !accountingMappingPage.loaded) return accountingMappings;
    return accountPageAdapters.mergeAccountMappingPage(accountingMappingPage.items, accountingMappings);
  }, [accountPageAdapters, accountReadEnabled, accountingMappingPage.items, accountingMappingPage.loaded, accountingMappings]);
  const organizationAccountingMappings = useMemo(() => accountAccountingMappings.filter((mapping) => !mapping.businessId || organizationBusinessIds.has(mapping.businessId)), [accountAccountingMappings, organizationBusinessIds]);
  const dailyAccountingMappings = useMemo(() => organizationAccountingMappings.filter((mapping) => !mapping.businessId || dailyBusinessIds.has(mapping.businessId)), [dailyBusinessIds, organizationAccountingMappings]);
  const accountDeclarationDrafts = useMemo(() => {
    if (!accountReadEnabled || !accountPageAdapters || !declarationPage.loaded) return dailyDeclarationDrafts;
    const merged = new Map(declarationPage.items.map((item) => {
      const fallback = dailyDeclarationDrafts.find((draft) => draft.id === item.id);
      const mapped = accountPageAdapters.mapAccountDeclarationDraft(item, fallback);
      return [declarationScopeKey(mapped.scope), mapped];
    }));
    dailyDeclarationDrafts.forEach((draft) => {
      const key = declarationScopeKey(draft.scope);
      const serverDraft = merged.get(key);
      if (!serverDraft || (Date.parse(draft.savedAt || "") || 0) > (Date.parse(serverDraft.savedAt || "") || 0)) merged.set(key, draft);
    });
    return [...merged.values()].sort((left, right) => (Date.parse(right.savedAt || "") || 0) - (Date.parse(left.savedAt || "") || 0));
  }, [accountPageAdapters, accountReadEnabled, dailyDeclarationDrafts, declarationPage.items, declarationPage.loaded]);
  const accountPayrollProfiles = useMemo(() => accountReadEnabled && accountPageAdapters && payrollEmployeePage.loaded
    ? accountPageAdapters.mergeAccountPayrollEmployeePage(payrollEmployeePage.items, dailyPayrollProfiles)
    : dailyPayrollProfiles, [accountPageAdapters, accountReadEnabled, dailyPayrollProfiles, payrollEmployeePage.items, payrollEmployeePage.loaded]);
  const accountAdminBusinesses = useMemo(() => administrationReadEnabled && accountPageAdapters && adminBusinessPage.loaded
    ? accountPageAdapters.mergeAccountPage(adminBusinessPage.items, organizationBusinesses, accountPageAdapters.mapAccountManagedBusiness)
    : organizationBusinesses, [accountPageAdapters, adminBusinessPage.items, adminBusinessPage.loaded, administrationReadEnabled, organizationBusinesses]);
  const accountAdminMembers = useMemo(() => {
    if (!administrationReadEnabled || !accountPageAdapters || !adminMemberPage.loaded) return teamMembers.filter((member) => member.workspaceId === organization?.id);
    const members = accountPageAdapters.mergeAccountPage(adminMemberPage.items, teamMembers, accountPageAdapters.mapAccountOrganizationMember);
    const invitations = adminInvitationPage.loaded
      ? accountPageAdapters.mergeAccountPage(adminInvitationPage.items, teamMembers, accountPageAdapters.mapAccountOrganizationMember)
      : [];
    return [...members, ...invitations];
  }, [accountPageAdapters, adminInvitationPage.items, adminInvitationPage.loaded, adminMemberPage.items, adminMemberPage.loaded, administrationReadEnabled, organization?.id, teamMembers]);
  const selectedAdminBusinessId = useMemo(() => {
    const directory = serverAccount ? accountAdminBusinesses : organizationBusinesses;
    if (serverAccount && SERVER_UUID_PATTERN.test(focusedBusinessId)) return focusedBusinessId;
    return directory.find((business) => business.id === focusedBusinessId)?.id || directory[0]?.id || "";
  }, [accountAdminBusinesses, focusedBusinessId, organizationBusinesses, serverAccount]);
  const selectedAdminMemberId = useMemo(() => {
    const directory = serverAccount ? accountAdminMembers : teamMembers.filter((member) => member.workspaceId === organization?.id);
    if (serverAccount && SERVER_UUID_PATTERN.test(focusedAdminMemberId)) return focusedAdminMemberId;
    return directory.find((member) => member.id === focusedAdminMemberId)?.id || directory[0]?.id || "";
  }, [accountAdminMembers, focusedAdminMemberId, organization?.id, serverAccount, teamMembers]);
  const selectedAdminBusinessWorkspaceId = serverAccount && SERVER_UUID_PATTERN.test(selectedAdminBusinessId)
    ? selectedAdminBusinessId
    : "";
  const selectedAdminMemberDirectoryRecord = accountAdminMembers.find((member) => member.id === selectedAdminMemberId);
  useEffect(() => {
    if (selectedAdminMemberDirectoryRecord?.invitationId) {
      setFocusedAdminInvitation({ organizationId: organization?.id, id: selectedAdminMemberId });
    }
  }, [organization?.id, selectedAdminMemberId, selectedAdminMemberDirectoryRecord?.invitationId]);
  const selectedAdminMemberResourceId = serverAccount && SERVER_UUID_PATTERN.test(selectedAdminMemberDirectoryRecord?.invitationId || selectedAdminMemberId)
    ? selectedAdminMemberDirectoryRecord?.invitationId || selectedAdminMemberId
    : "";
  const selectedAdminMemberKind = selectedAdminMemberDirectoryRecord?.invitationId
    || (focusedAdminInvitation?.organizationId === organization?.id && focusedAdminInvitation?.id === selectedAdminMemberId)
    ? "invitation" : "member";
  useEffect(() => {
    if (!administrationReadEnabled || adminSection !== "businesses" || !selectedAdminBusinessWorkspaceId || typeof accountRepositoryRuntime?.reads?.administration?.businessDetail !== "function") {
      setAccountAdminBusinessDetailState({ id: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
      return undefined;
    }
    let active = true;
    setAccountAdminBusinessDetailState((current) => ({
      id: selectedAdminBusinessWorkspaceId,
      data: current.id === selectedAdminBusinessWorkspaceId ? current.data : null,
      loading: true,
      error: "",
      collectionLoading: {},
      collectionErrors: {},
    }));
    accountRepositoryRuntime.reads.administration.businessDetail(organization.id, selectedAdminBusinessWorkspaceId).then((result) => {
      if (!active) return;
      if (!result?.ok) {
        setAccountAdminBusinessDetailState({ id: selectedAdminBusinessWorkspaceId, data: null, loading: false, error: result?.error || "This managed business could not be loaded.", collectionLoading: {}, collectionErrors: {} });
        return;
      }
      setAccountAdminBusinessDetailState({ id: selectedAdminBusinessWorkspaceId, data: result.data, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
    });
    return () => { active = false; };
  }, [accountAdminBusinessDetailRefreshKey, adminSection, administrationReadEnabled, organization?.id, selectedAdminBusinessWorkspaceId]);
  const loadAdminBusinessDetailCollection = useCallback(async (kind) => {
    const config = kind === "contacts"
      ? { field: "contacts", read: accountRepositoryRuntime?.reads?.administration?.businessContacts }
      : kind === "accessMembers"
        ? { field: "accessMembers", read: accountRepositoryRuntime?.reads?.administration?.businessMembers }
        : null;
    if (!config || typeof config.read !== "function" || !selectedAdminBusinessWorkspaceId || !organization?.id) return;
    const page = accountAdminBusinessDetailState.id === selectedAdminBusinessWorkspaceId
      ? accountAdminBusinessDetailState.data?.historyPages?.[config.field]
      : null;
    if (!page?.nextCursor || accountAdminBusinessDetailState.collectionLoading?.[kind]) return;

    setAccountAdminBusinessDetailState((current) => current.id !== selectedAdminBusinessWorkspaceId ? current : ({
      ...current,
      collectionLoading: { ...current.collectionLoading, [kind]: true },
      collectionErrors: { ...current.collectionErrors, [kind]: "" },
    }));
    const result = await config.read({
      organizationId: organization.id,
      businessId: selectedAdminBusinessWorkspaceId,
      limit: 25,
      cursor: page.nextCursor,
    });
    setAccountAdminBusinessDetailState((current) => {
      if (current.id !== selectedAdminBusinessWorkspaceId) return current;
      if (current.data?.historyPages?.[config.field]?.nextCursor !== page.nextCursor) return current;
      if (!result?.ok) return {
        ...current,
        collectionLoading: { ...current.collectionLoading, [kind]: false },
        collectionErrors: { ...current.collectionErrors, [kind]: result?.error || "These business records could not be loaded." },
      };
      const existingItems = Array.isArray(current.data?.[config.field]) ? current.data[config.field] : [];
      const incomingItems = Array.isArray(result.data?.items) ? result.data.items : [];
      return {
        ...current,
        data: {
          ...current.data,
          [config.field]: [...existingItems, ...incomingItems.filter((item) => !existingItems.some((existing) => existing.id === item.id))],
          historyPages: {
            ...(current.data?.historyPages || {}),
            [config.field]: { hasMore: Boolean(result.data?.hasMore), nextCursor: result.data?.nextCursor || null },
          },
        },
        collectionLoading: { ...current.collectionLoading, [kind]: false },
        collectionErrors: { ...current.collectionErrors, [kind]: "" },
      };
    });
  }, [accountAdminBusinessDetailState.collectionLoading, accountAdminBusinessDetailState.data, accountAdminBusinessDetailState.id, organization?.id, selectedAdminBusinessWorkspaceId]);
  useEffect(() => {
    const read = selectedAdminMemberKind === "invitation"
      ? accountRepositoryRuntime?.reads?.administration?.invitationDetail
      : accountRepositoryRuntime?.reads?.administration?.memberDetail;
    if (!administrationReadEnabled || adminSection !== "team" || !selectedAdminMemberId || !selectedAdminMemberResourceId || typeof read !== "function") {
      setAccountAdminMemberDetailState({ id: "", kind: "", data: null, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
      return undefined;
    }
    let active = true;
    setAccountAdminMemberDetailState((current) => ({
      id: selectedAdminMemberId,
      kind: selectedAdminMemberKind,
      data: current.id === selectedAdminMemberId && current.kind === selectedAdminMemberKind ? current.data : null,
      loading: true,
      error: "",
      collectionLoading: {},
      collectionErrors: {},
    }));
    read(organization.id, selectedAdminMemberResourceId).then((result) => {
      if (!active) return;
      if (!result?.ok) {
        setAccountAdminMemberDetailState({ id: selectedAdminMemberId, kind: selectedAdminMemberKind, data: null, loading: false, error: result?.error || "This team member could not be loaded.", collectionLoading: {}, collectionErrors: {} });
        return;
      }
      setAccountAdminMemberDetailState({ id: selectedAdminMemberId, kind: selectedAdminMemberKind, data: result.data, loading: false, error: "", collectionLoading: {}, collectionErrors: {} });
    });
    return () => { active = false; };
  }, [accountAdminMemberDetailRefreshKey, adminSection, administrationReadEnabled, organization?.id, selectedAdminMemberId, selectedAdminMemberKind, selectedAdminMemberResourceId]);
  const loadAdminMemberDetailCollection = useCallback(async () => {
    const read = selectedAdminMemberKind === "invitation"
      ? accountRepositoryRuntime?.reads?.administration?.invitationAccess
      : accountRepositoryRuntime?.reads?.administration?.memberAccess;
    const page = accountAdminMemberDetailState.id === selectedAdminMemberId
      ? accountAdminMemberDetailState.data?.historyPages?.accessAssignments
      : null;
    if (typeof read !== "function" || !selectedAdminMemberResourceId || !organization?.id || !page?.nextCursor || accountAdminMemberDetailState.collectionLoading?.accessAssignments) return;

    setAccountAdminMemberDetailState((current) => current.id !== selectedAdminMemberId ? current : ({
      ...current,
      collectionLoading: { ...current.collectionLoading, accessAssignments: true },
      collectionErrors: { ...current.collectionErrors, accessAssignments: "" },
    }));
    const result = await read({
      organizationId: organization.id,
      ...(selectedAdminMemberKind === "invitation" ? { invitationId: selectedAdminMemberResourceId } : { memberId: selectedAdminMemberResourceId }),
      limit: 25,
      cursor: page.nextCursor,
    });
    setAccountAdminMemberDetailState((current) => {
      if (current.id !== selectedAdminMemberId || current.kind !== selectedAdminMemberKind) return current;
      if (current.data?.historyPages?.accessAssignments?.nextCursor !== page.nextCursor) return current;
      if (!result?.ok) return {
        ...current,
        collectionLoading: { ...current.collectionLoading, accessAssignments: false },
        collectionErrors: { ...current.collectionErrors, accessAssignments: result?.error || "This access list could not be loaded." },
      };
      const existingItems = Array.isArray(current.data?.accessAssignments) ? current.data.accessAssignments : [];
      const incomingItems = Array.isArray(result.data?.items) ? result.data.items : [];
      return {
        ...current,
        data: {
          ...current.data,
          accessAssignments: [...existingItems, ...incomingItems.filter((item) => !existingItems.some((existing) => existing.businessId === item.businessId))],
          historyPages: {
            ...(current.data?.historyPages || {}),
            accessAssignments: { hasMore: Boolean(result.data?.hasMore), nextCursor: result.data?.nextCursor || null },
          },
        },
        collectionLoading: { ...current.collectionLoading, accessAssignments: false },
        collectionErrors: { ...current.collectionErrors, accessAssignments: "" },
      };
    });
  }, [accountAdminMemberDetailState.collectionLoading, accountAdminMemberDetailState.data, accountAdminMemberDetailState.id, organization?.id, selectedAdminMemberId, selectedAdminMemberKind, selectedAdminMemberResourceId]);
  const selectedAccountAdminBusinessDetail = useMemo(() => {
    const fallback = accountAdminBusinesses.find((business) => business.id === selectedAdminBusinessId);
    if (!accountPageAdapters?.mapAccountManagedBusinessDetail || accountAdminBusinessDetailState.id !== selectedAdminBusinessId || !accountAdminBusinessDetailState.data) return fallback;
    return accountPageAdapters.mapAccountManagedBusinessDetail(accountAdminBusinessDetailState.data, fallback || {}, Math.max(0, accountAdminBusinesses.indexOf(fallback)));
  }, [accountAdminBusinessDetailState.data, accountAdminBusinessDetailState.id, accountAdminBusinesses, accountPageAdapters, selectedAdminBusinessId]);
  const accountAdminBusinessesForView = useMemo(() => selectedAccountAdminBusinessDetail
    ? accountAdminBusinesses.some((business) => business.id === selectedAccountAdminBusinessDetail.id)
      ? accountAdminBusinesses.map((business) => business.id === selectedAccountAdminBusinessDetail.id ? selectedAccountAdminBusinessDetail : business)
      : [...accountAdminBusinesses, selectedAccountAdminBusinessDetail]
    : accountAdminBusinesses, [accountAdminBusinesses, selectedAccountAdminBusinessDetail]);
  const selectedAccountAdminMemberDetail = useMemo(() => {
    const fallback = accountAdminMembers.find((member) => member.id === selectedAdminMemberId);
    if (!accountPageAdapters?.mapAccountOrganizationMemberDetail || accountAdminMemberDetailState.id !== selectedAdminMemberId || !accountAdminMemberDetailState.data) return fallback;
    return accountPageAdapters.mapAccountOrganizationMemberDetail(accountAdminMemberDetailState.data, fallback || {});
  }, [accountAdminMemberDetailState.data, accountAdminMemberDetailState.id, accountAdminMembers, accountPageAdapters, selectedAdminMemberId]);
  const accountAdminMembersForView = useMemo(() => {
    const directory = [...accountAdminMembers];
    (selectedAccountAdminBusinessDetail?.accessMembers || []).forEach((member) => {
      if (!directory.some((candidate) => candidate.id === member.id)) directory.push(member);
    });
    if (!selectedAccountAdminMemberDetail) return directory;
    return directory.some((member) => member.id === selectedAccountAdminMemberDetail.id)
      ? directory.map((member) => member.id === selectedAccountAdminMemberDetail.id ? selectedAccountAdminMemberDetail : member)
      : [...directory, selectedAccountAdminMemberDetail];
  }, [accountAdminMembers, selectedAccountAdminBusinessDetail?.accessMembers, selectedAccountAdminMemberDetail]);
  const bankingEnabled = Boolean(canUseAccountingOperation(profile, "banking") && dailyBusinesses.length);
  const organizationWorkReminders = scopedWorkspace.workReminders;
  const organizationWorkTasks = scopedWorkspace.workTasks;
  const surfaceBusinesses = workspaceMode === "admin" ? organizationBusinesses : dailyBusinesses;
  const surfaceClients = serverAccount ? accountClients : workspaceMode === "admin" ? organizationClients : dailyClients;
  const surfaceInvoices = serverAccount ? accountInvoices : workspaceMode === "admin" ? organizationInvoices : dailyInvoices;
  const surfaceIntakeItems = serverAccount ? accountIntakeItems : workspaceMode === "admin" ? scopedWorkspace.intakeItems : dailyIntakeItems;
  const surfaceServices = serverAccount ? accountServices : workspaceMode === "admin" ? organizationServices : dailyServices;
  const surfaceWorkReminders = serverAccount ? accountReminders : workspaceMode === "admin" ? organizationWorkReminders : dailyWorkReminders;
  const pendingCommunications = useMemo(() => accountCommunications.filter((item) => item.status === "follow-up"), [accountCommunications]);
  const hasNotificationActions = useMemo(() => serverAccount
    ? accountNotificationActions.length > 0
    : surfaceInvoices.some((invoice) => invoice.documentType !== "credit-note" && ["Sent", "Overdue"].includes(invoice.status))
      || pendingCommunications.length > 0
      || surfaceWorkReminders.some((reminder) => !reminder.archived && reminder.status === "open"), [accountNotificationActions.length, pendingCommunications, serverAccount, surfaceInvoices, surfaceWorkReminders]);
  const persistableIntakeItems = useMemo(() => intakeItems.map((item) => {
    const persisted = { ...item };
    delete persisted.previewUrl;
    return persisted;
  }), [intakeItems]);
  const localSnapshot = useMemo(() => ({ workspaces, businesses, clients: clientRecords, services: serviceRecords, invoices: invoiceRecords, collectionHistory, invoiceStatuses, archivedInvoiceIds, activityLog, teamMembers, intakeItems: persistableIntakeItems, purchaseDocuments: localPurchaseDocuments, documentExpectations, declarationDrafts, payrollProfiles, accountingSourceSummaries, accountingMappings, vatBookPeriods, fixedAssets, ledgerAccounts, quickBooksImportEvidence, bankTransactions, portalPublications, portalDocumentRequests, portalFinancialSnapshot, quickBooksExports, communications: communicationRecords, workReminders: workReminderRecords, workTasks: workTaskRecords, workItemActivities: workItemActivityRecords, profile, sampleWorkspace, sampleWorkspaceVersion: sampleWorkspace ? SAMPLE_WORKSPACE_VERSION : undefined }), [accountingMappings, accountingSourceSummaries, activityLog, archivedInvoiceIds, bankTransactions, businesses, clientRecords, collectionHistory, communicationRecords, declarationDrafts, documentExpectations, fixedAssets, invoiceRecords, invoiceStatuses, ledgerAccounts, payrollProfiles, localPurchaseDocuments, persistableIntakeItems, portalDocumentRequests, portalFinancialSnapshot, portalPublications, profile, quickBooksExports, quickBooksImportEvidence, sampleWorkspace, serviceRecords, teamMembers, vatBookPeriods, workItemActivityRecords, workReminderRecords, workTaskRecords, workspaces]);
  const localPurchases = useMemo(() => localPurchaseDocuments.filter(document => document.organizationId === organization?.id && dailyBusinessIds.has(document.businessId)).map(mapLocalPurchaseDocument), [localPurchaseDocuments, organization?.id, dailyBusinessIds]);
  const localSnapshotRef = useRef(localSnapshot);
  localSnapshotRef.current = localSnapshot;
  const localPersistence = useLocalSnapshotPersistence({ sessionRef: localSessionRef, snapshot: localSnapshot, sessionMode, localSpace });
  workTaskRecordsRef.current = workTaskRecords;
  workReminderRecordsRef.current = workReminderRecords;
  declarationDraftsRef.current = declarationDrafts;

  useEffect(() => () => {
    declarationSaveTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    declarationSaveTimersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 860px)");
    if (!query) return undefined;
    const sync = (event) => setMobileViewport(event.matches);
    setMobileViewport(Boolean(query.matches));
    if (typeof query.addEventListener === "function") query.addEventListener("change", sync);
    else query.addListener?.(sync);
    return () => {
      if (typeof query.removeEventListener === "function") query.removeEventListener("change", sync);
      else query.removeListener?.(sync);
    };
  }, []);

  useEffect(() => {
    writeMobileExperience(mobileExperience);
  }, [mobileExperience]);

  useEffect(() => {
    if (!organization?.id || !profile?.id) return;
    setNavigationPreferences(readNavigationPreferences(organization.id, profile.id));
  }, [organization?.id, profile?.id]);

  useEffect(() => {
    if (!enabledWorkNavItems.length && activeView === "work") setActiveView("overview");
    if (enabledWorkNavItems.length && !enabledWorkNavItems.some((item) => item.id === workSection)) setWorkSection(enabledWorkNavItems[0].id);
  }, [activeView, enabledWorkNavItems, workSection]);


  useEffect(() => {
    window.localStorage.setItem(PUBLIC_MODE_KEY, String(publicMode));
  }, [publicMode]);

  useEffect(() => {
    window.localStorage.setItem(INTERFACE_LANGUAGE_KEY, interfaceLanguage);
    setProfile((current) => current.interfaceLanguage === interfaceLanguage ? current : { ...current, interfaceLanguage });
  }, [interfaceLanguage]);

  useEffect(() => {
    if (businessScopeId !== ALL_BUSINESSES_SCOPE && !organizationBusinesses.some((business) => business.id === businessScopeId)) {
      setBusinessScopeId(ALL_BUSINESSES_SCOPE);
    }
  }, [businessScopeId, organizationBusinesses]);

  useEffect(() => {
    if (workspaceMode !== "admin" || canManage) return;
    setWorkspaceMode("regular");
    setActiveView("overview");
  }, [canManage, workspaceMode]);

  const navigate = (view) => {
    if (OPERATION_NAV_ITEMS.some((item) => item.id === view) && !canUseAccountingOperation(profile, view)) {
      setToast(accessDeniedMessage);
      return;
    }
    setEditorOpen(false);
    setEditorReturnContext(null);
    setRecordReturnContext(null);
    setFocusedClientId("");
    setFocusedBusinessId("");
    setFocusedIntakeItemId("");
    setFocusedPurchaseId("");
    setSearchWorkTarget(null);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setUserOpen(false);
    const activeGroup = view === "work"
      ? "work"
      : WORKSPACE_NAV_ITEMS.some((item) => item.id === view)
        ? "workspace"
        : accountingGroupFor(availableDestinationGroups, view)
          ? `destination-${accountingGroupFor(availableDestinationGroups, view).id}`
          : "";
    setExpandedNavGroups(navGroupState(activeGroup));
    setActiveView(view);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const navigateWork = (section = "tasks") => {
    const nextSection = enabledWorkNavItems.some((item) => item.id === section) ? section : enabledWorkNavItems[0]?.id;
    if (!nextSection) return;
    setWorkSection(nextSection);
    navigate("work");
  };
  const toggleNavGroup = (group) => setExpandedNavGroups((current) => current[group] ? { ...navGroupState(), [group]: false } : navGroupState(group));
  const switchWorkspaceMode = (mode) => {
    if (mode === "admin" && !canManage) return;
    setWorkspaceMode(mode);
    setAdminSection("overview");
    navigate(mode === "admin" ? "admin" : "overview");
  };
  const navigateAdmin = (section) => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return;
    }
    setWorkspaceMode("admin");
    setAdminSection(section);
    navigate("admin");
  };
  const changeBusinessScope = (nextScopeId) => {
    if (nextScopeId !== ALL_BUSINESSES_SCOPE && !organizationBusinesses.some((business) => business.id === nextScopeId)) return;
    setBusinessScopeId(nextScopeId);
    setBankStatementFilterId("");
    navigate(activeView);
    setSearchState({ query: "", scope: "All" });
  };
  const isMobileViewport = matchesMobileViewport;
  const changeMobileExperience = (nextMode) => {
    if (!["minimal", "advanced"].includes(nextMode)) return;
    setMobileExperience(nextMode);
    if (nextMode === "minimal" && workspaceMode === "regular" && !["overview", "invoices", "clients", "services", "settings", "account", "more"].includes(activeView)) navigate("overview");
  };
  const saveNavigationSettings = (nextPreferences) => {
    let saved;
    try {
      saved = writeNavigationPreferences(organization.id, profile.id, nextPreferences);
    } catch {
      // Keep the account draft available for retry without announcing a saved preference.
      return false;
    }
    setNavigationPreferences(saved);
    setExpandedNavGroups({ work: false, workspace: false, accounting: false });
    setToast("Navigation preferences saved");
    return true;
  };
  const openSearchControl = () => {
    setNotificationsOpen(false);
    setUserOpen(false);
    if (isMobileViewport()) {
      setSearchOpen(false);
      navigate("search");
      return;
    }
    setSearchOpen((value) => !value);
  };
  const openNotificationsControl = () => {
    setSearchOpen(false);
    setUserOpen(false);
    if (isMobileViewport()) {
      setNotificationsOpen(false);
      navigate("notifications");
      return;
    }
    setNotificationsOpen((value) => !value);
  };
  const openAccountControl = () => {
    setSearchOpen(false);
    setNotificationsOpen(false);
    if (isMobileViewport()) {
      setUserOpen(false);
      navigate("account");
      return;
    }
    setUserOpen((value) => !value);
  };
  const openClientFromSearch = (client) => {
    const returnContext = { workspaceMode, adminSection };
    navigate("clients");
    setWorkspaceMode("regular");
    setFocusedClientId(client.id);
    setRecordReturnContext(returnContext);
  };
  const openWorkFromSearch = (record, kind) => {
    navigate("work"); setWorkspaceMode("regular"); setWorkSection(kind);
    setSearchWorkTarget({ ...record, workItemType: kind === "tasks" ? "task" : "reminder" });
  };
  const openBusinessFromSearch = (business) => {
    if (!canManage) {
      if (serverAccount) { setBusinessContactTarget(business.id); setBusinessContactsOpen(true); }
      return;
    }
    const returnContext = { workspaceMode, adminSection };
    navigate("admin");
    setWorkspaceMode("admin");
    setAdminSection("businesses");
    setFocusedBusinessId(business.id);
    setRecordReturnContext(returnContext);
  };
  const openDocumentFromSearch = (document) => {
    navigate("intake");
    setWorkspaceMode("regular");
    setBusinessScopeId(organizationBusinesses.some(business => business.id === document.businessId) ? document.businessId : ALL_BUSINESSES_SCOPE);
    setFocusedIntakeItemId(document.id);
  };
  const openServiceFromSearch = (service) => {
    navigate("services");
    setWorkspaceMode("regular");
    void openServiceEditor(service, false, true);
  };
  const openPurchaseRecord = (purchaseDocumentId) => {
    navigate("purchases");
    setWorkspaceMode("regular");
    setFocusedPurchaseId(purchaseDocumentId);
  };
  const returnToSearch = () => {
    const returnContext = recordReturnContext;
    navigate("search");
    setWorkspaceMode(returnContext?.workspaceMode || "regular");
    setAdminSection(returnContext?.adminSection || "overview");
  };
  const createActivity = ({ actorProfile = profile, workspaceId, ...entry }) => createAuditEntry({ ...entry, profile: actorProfile, workspaceId: workspaceId || organization?.id || authSession?.organizationId || "", sessionMode });
  const recordActivity = (entry) => setActivityLog((current) => [createActivity(entry), ...current]);
  const applyData = useCallback((data) => {
    setWorkspaces(data.workspaces?.length ? data.workspaces : WORKSPACES);
    setBusinesses(data.businesses || []);
    setClientRecords(data.clients || []);
    setServiceRecords(data.services || []);
    setInvoiceRecords(data.invoices || []);
    setCollectionHistory(data.collectionHistory || (data.sampleWorkspace ? SAMPLE_COLLECTION_HISTORY : []));
    setInvoiceStatuses(data.invoiceStatuses || {});
    setArchivedInvoiceIds(data.archivedInvoiceIds || []);
    setActivityLog(data.activityLog || []);
    setTeamMembers(data.teamMembers || TEAM_MEMBERS);
    setIntakeItems(data.intakeItems || []);
    setLocalPurchaseDocuments(data.purchaseDocuments || []);
    setDocumentExpectations(data.documentExpectations || []);
    setDeclarationDrafts(data.declarationDrafts || []);
    setPayrollProfiles(data.payrollProfiles || []);
    setAccountingSourceSummaries(data.accountingSourceSummaries || []);
    setAccountingMappings(data.accountingMappings || []);
    setVatBookPeriods(data.vatBookPeriods || []);
    setFixedAssets(data.fixedAssets || []);
    setLedgerAccounts(data.ledgerAccounts || []);
    setQuickBooksImportEvidence(data.quickBooksImportEvidence || null);
    setBankTransactions(data.bankTransactions || []);
    setPortalPublications(data.portalPublications || []);
    setPortalDocumentRequests(data.portalDocumentRequests || []);
    setPortalNotifications(data.portalNotifications || []);
    setPortalFinancialSnapshot(data.portalFinancialSnapshot || null);
    setPortalPagination(data.portalPagination || {});
    setQuickBooksExports(data.quickBooksExports || []);
    setCommunicationRecords(data.communications || []);
    workReminderRecordsRef.current = data.workReminders || [];
    workTaskRecordsRef.current = data.workTasks || [];
    setWorkReminderRecords(workReminderRecordsRef.current);
    setWorkTaskRecords(workTaskRecordsRef.current);
    setWorkItemActivityRecords(data.workItemActivities || []);
    setSampleWorkspace(Boolean(data.sampleWorkspace));
    setProfile({ ...USER_PROFILE, ...(data.profile || {}), workspaceId: data.profile?.workspaceId || data.workspaces?.[0]?.id || USER_PROFILE.workspaceId, publicMode: readPublicModePreference() ?? data.profile?.publicMode ?? false });
    setSelectedInvoice(null);
  }, []);
  const applyLocalCommit = data => unstable_batchedUpdates(() => {
    localSnapshotRef.current = data;
    intakeItemsRef.current = data.intakeItems || [];
    applyData(data);
  });
  const installLocalView = (owner, data, session) => unstable_batchedUpdates(() => {
    localPersistence.install(owner);
    applyLocalCommit(data);
    setLocalSpace(owner.status().space);
    setAuthSession(session);
  });
  const prepareLocalDeparture = async () => {
    if (await localPersistence.leave()) return true;
    setToast("Keep or export your unsaved changes before switching workspaces.");
    return false;
  };
  const applyAccountShell = useCallback((data) => {
    if (!data?.workspaces?.length || !data?.profile?.id) throw new Error("The account service returned an invalid workspace shell.");
    setWorkspaces(data.workspaces);
    setBusinesses(data.businesses || []);
    setTeamMembers(data.teamMembers || [data.profile]);
    setProfile((current) => ({
      ...current,
      ...data.profile,
      workspaceId: data.profile.workspaceId || data.workspaces[0].id,
      publicMode: current.publicMode,
    }));
  }, []);
  const commitDeclarationDraft = useCallback((draft) => {
    if (!serverAccount || typeof accountRepositoryRuntime?.declarations?.saveDraft !== "function") {
      return Promise.resolve({ ok: false, code: "not_account_mode", error: "An account workspace is required to save this declaration." });
    }
    const key = declarationScopeKey(draft?.scope);
    const previous = declarationSavePromisesRef.current.get(key) || Promise.resolve();
    const pending = previous.catch(() => null).then(async () => {
      const currentBeforeSave = declarationDraftsRef.current.find((candidate) => declarationScopeKey(candidate.scope) === key);
      const saveCandidate = {
        ...draft,
        id: currentBeforeSave?.id || draft.id,
        revision: currentBeforeSave?.revision || draft.revision,
      };
      try {
        const result = await accountRepositoryRuntime.declarations.saveDraft(saveCandidate, organization?.currency || "EUR");
        if (!result?.ok) return result;
        const mapped = accountPageAdapters?.mapAccountDeclarationDraft
          ? accountPageAdapters.mapAccountDeclarationDraft(result.data, saveCandidate)
          : {
            ...saveCandidate,
            id: result.data?.id || saveCandidate.id,
            revision: result.data?.revision || saveCandidate.revision,
            savedAt: result.data?.savedAt || result.data?.updatedAt || saveCandidate.savedAt,
            updatedAt: result.data?.updatedAt || saveCandidate.updatedAt,
          };
        const latest = declarationDraftsRef.current;
        const savedIndex = latest.findIndex((candidate) => declarationScopeKey(candidate.scope) === key);
        const currentAfterSave = savedIndex >= 0 ? latest[savedIndex] : null;
        const saved = currentAfterSave && declarationPayloadSignature(currentAfterSave) !== declarationPayloadSignature(saveCandidate)
          ? {
            ...currentAfterSave,
            id: mapped.id,
            revision: mapped.revision,
            savedAt: mapped.savedAt,
            updatedAt: mapped.updatedAt,
          }
          : mapped;
        const savedDrafts = savedIndex < 0
          ? [...latest, saved]
          : latest.map((item, itemIndex) => itemIndex === savedIndex ? saved : item);
        declarationDraftsRef.current = savedDrafts;
        setDeclarationDrafts(savedDrafts);
        return { ...result, draft: saved };
      } catch (error) {
        return { ok: false, code: "declaration_save_failed", error: error?.message || "The declaration draft could not be saved to this account." };
      }
    });
    declarationSavePromisesRef.current.set(key, pending);
    pending.then(() => {
      if (declarationSavePromisesRef.current.get(key) === pending) declarationSavePromisesRef.current.delete(key);
    });
    return pending;
  }, [accountPageAdapters, organization?.currency, serverAccount]);
  const saveDeclarationDraft = useCallback((draft) => {
    if (!draft?.scope?.organizationId || !draft?.scope?.businessId || !draft?.scope?.type || !draft?.scope?.period) return;
    const key = declarationScopeKey(draft.scope);
    const current = declarationDraftsRef.current;
    const index = current.findIndex((candidate) => declarationScopeKey(candidate.scope) === key);
    const existing = index >= 0 ? current[index] : null;
    const next = {
      ...existing,
      ...draft,
      sourceEvidence: draft.sourceEvidence || existing?.sourceEvidence || [],
      status: draft.status || existing?.status || "draft",
    };
    if (existing && JSON.stringify({ inputs: existing.inputs, result: existing.result, status: existing.status, sourceEvidence: existing.sourceEvidence })
      === JSON.stringify({ inputs: next.inputs, result: next.result, status: next.status, sourceEvidence: next.sourceEvidence })) return;
    const updated = index < 0 ? [...current, next] : current.map((item, itemIndex) => itemIndex === index ? next : item);
    declarationDraftsRef.current = updated;
    setDeclarationDrafts(updated);

    if (!serverAccount || typeof accountRepositoryRuntime?.declarations?.saveDraft !== "function") return;
    const previousTimer = declarationSaveTimersRef.current.get(key);
    if (previousTimer) window.clearTimeout(previousTimer);
    const version = (declarationSaveVersionsRef.current.get(key) || 0) + 1;
    declarationSaveVersionsRef.current.set(key, version);
    const timer = window.setTimeout(async () => {
      declarationSaveTimersRef.current.delete(key);
      const result = await commitDeclarationDraft(next);
      if (declarationSaveVersionsRef.current.get(key) !== version) return;
      if (!result?.ok) {
        setToast(result?.error || "The declaration draft could not be saved to this account.");
      }
    }, 650);
    declarationSaveTimersRef.current.set(key, timer);
  }, [commitDeclarationDraft, serverAccount]);
  const saveDeclarationDraftNow = useCallback(async (draft) => {
    const key = declarationScopeKey(draft?.scope);
    const timer = declarationSaveTimersRef.current.get(key);
    if (timer) window.clearTimeout(timer);
    declarationSaveTimersRef.current.delete(key);
    declarationSaveVersionsRef.current.set(key, (declarationSaveVersionsRef.current.get(key) || 0) + 1);
    return commitDeclarationDraft(draft);
  }, [commitDeclarationDraft]);
  const exportDeclarationDraft = useCallback(async (draft, { exportVariant = "internal" } = {}) => {
    if (!serverAccount || typeof accountRepositoryRuntime?.reads?.declarations?.file !== "function") {
      return { ok: false, error: "An account workspace is required for an authoritative declaration export." };
    }
    const key = declarationScopeKey(draft?.scope);
    const timer = declarationSaveTimersRef.current.get(key);
    if (timer) {
      window.clearTimeout(timer);
      declarationSaveTimersRef.current.delete(key);
    }
    declarationSaveVersionsRef.current.set(key, (declarationSaveVersionsRef.current.get(key) || 0) + 1);
    const latest = declarationDraftsRef.current.find((candidate) => declarationScopeKey(candidate.scope) === key);
    await declarationSavePromisesRef.current.get(key);
    const current = declarationDraftsRef.current.find((candidate) => declarationScopeKey(candidate.scope) === key) || draft;
    const unchanged = current.id && current.revision && JSON.stringify(current.inputs) === JSON.stringify(draft.inputs)
      && JSON.stringify(current.sourceEvidence || []) === JSON.stringify(draft.sourceEvidence || []);
    const saved = unchanged ? { ok: true, draft: current } : await commitDeclarationDraft({ ...latest, ...draft });
    if (!saved?.ok || !saved.draft?.id || !saved.draft?.revision) {
      return { ok: false, error: saved?.error || "The declaration revision could not be saved before export." };
    }
    const file = await accountRepositoryRuntime.reads.declarations.file({
      organizationId: draft.scope.organizationId,
      declarationId: saved.draft.id,
      revision: saved.draft.revision,
      type: draft.scope.type,
      exportVariant,
    });
    if (!file?.ok) return file;
    return { ok: true, filename: saveAccountFile(file.data), draft: saved.draft };
  }, [commitDeclarationDraft, serverAccount]);
  const loadDeclarationDraft = useCallback(async (scope) => {
    if (!serverAccount || typeof accountRepositoryRuntime?.reads?.declarations?.scope !== "function") return null;
    const result = await accountRepositoryRuntime.reads.declarations.scope(scope);
    if (!result?.ok) throw new Error(result?.error || "The declaration period could not be loaded.");
    if (!result.data) return null;
    return accountPageAdapters?.mapAccountDeclarationDraft
      ? accountPageAdapters.mapAccountDeclarationDraft(result.data)
      : result.data;
  }, [accountPageAdapters, serverAccount]);
  const loadPayrollPreparation = useCallback(async (scope) => {
    const result = await accountRepositoryRuntime.reads.declarations.payrollPreparation(scope, organization?.currency || "EUR");
    if (!result?.ok) throw new Error(result?.error || "The payroll sources could not be loaded.");
    return result.data.mode === "existing" ? { ...result.data, current: accountPageAdapters.mapAccountDeclarationDraft(result.data.current) } : result.data;
  }, [accountPageAdapters, organization?.currency]);
  const reviewPayrollDeclaration = useCallback((draft) => import(/* webpackChunkName: "account-declaration-commands" */ "./declarations/accountPayrollFlow")
.then(({ reviewPayrollDeclarationFlow }) => reviewPayrollDeclarationFlow({ draft, publicMode, connected: serverAccount, runtime: accountRepositoryRuntime, adapters: accountPageAdapters, currency: organization?.currency, scopeKey: declarationScopeKey, saveTimers: declarationSaveTimersRef.current, savePromises: declarationSavePromisesRef.current, saveVersions: declarationSaveVersionsRef.current, draftsRef: declarationDraftsRef, setDrafts: setDeclarationDrafts, notify: setToast })), [accountPageAdapters, organization?.currency, publicMode, serverAccount]);
  const postPayrollDeclaration = useCallback((request) => import(/* webpackChunkName: "account-declaration-commands" */ "./declarations/accountPayrollFlow")
    .then(({ postPayrollDeclarationFlow }) => postPayrollDeclarationFlow(request, publicMode, serverAccount, accountRepositoryRuntime, setToast)), [publicMode, serverAccount]);
  const [payrollLedgerEpoch, setPayrollLedgerEpoch] = useState(0);
  const [payrollLedgerTarget, setPayrollLedgerTarget] = useState(null);
  const [portalPublicationTarget, setPortalPublicationTarget] = useState(null);
  const loadPayrollLedgerEvidence = useCallback((declarationId) => import(/* webpackChunkName: "account-declaration-commands" */ "./declarations/accountPayrollFlow")
    .then(({ loadPayrollLedgerEvidenceFlow }) => loadPayrollLedgerEvidenceFlow(declarationId, organization?.id, serverAccount, accountRepositoryRuntime)), [organization?.id, serverAccount]);
  const savePayrollEmployeeProfile = useCallback(async (employee, businessId) => {
    if (publicMode) {
      setToast("Turn off public mode before changing the employee roster");
      return null;
    }
    const existing = accountPayrollProfiles.find((profile) => profile.id === employee?.profileId || profile.id === employee?.id);
    const coordinator = await import(/* webpackChunkName: "payroll-profile-persistence" */ "./declarations/payrollProfilePersistence");
    const result = await coordinator.persistPayrollEmployeeProfile({
      employee,
      businessId,
      organization,
      existing,
      serverAccount,
      saveRemote: accountRepositoryRuntime?.payrollEmployees?.save,
      mapRemote: accountPageAdapters?.mapAccountPayrollEmployee,
    });
    if (!result.ok) {
      setToast(result.error);
      return null;
    }
    setPayrollProfiles((current) => coordinator.mergePayrollProfile(current, result.profile, result.previousId));
    setToast(`${result.profile.name} saved to the employee roster`);
    return result.profile;
  }, [accountPageAdapters, accountPayrollProfiles, organization, publicMode, serverAccount]);
  const loadAccountingLineAssignments = useCallback((query) => {
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.reads?.accountingLineAssignments?.list !== "function") {
      return Promise.resolve({ ok: false, code: "accounting_line_review_unavailable", error: "Line-level accounting review is not available in this workspace." });
    }
    return accountRepositoryRuntime.reads.accountingLineAssignments.list({
      organizationId: organization.id,
      ...query,
    });
  }, [organization?.id, serverAccount]);
  const saveAccountingLineAssignment = useCallback(async (assignment) => {
    if (publicMode) return { ok: false, code: "public_mode", error: "Turn off public mode before changing accounting mappings." };
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.accountingLineAssignments?.save !== "function") {
      return { ok: false, code: "accounting_line_review_unavailable", error: "Line-level accounting review is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.accountingLineAssignments.save(assignment, organization.id);
    if (result?.ok) await refreshLoadedAccountPages([quickBooksCandidatePage]);
    setToast(result?.ok ? "Source line mapping saved" : result?.error || "The source line mapping could not be saved");
    return result;
  }, [organization?.id, publicMode, quickBooksCandidatePage, serverAccount]);
  const loadLineAccountingRules = useCallback((query) => {
    if (!serverAccount || !organization?.id) return Promise.resolve({ ok: false, error: "Accounting rules are unavailable." });
    return accountRepositoryRuntime.reads.accountingMappings.list({ ...query, organizationId: organization.id });
  }, [organization?.id, serverAccount]);
  const clearAccountingLineAssignment = useCallback(async (assignment) => {
    if (publicMode) return { ok: false, code: "public_mode", error: "Turn off public mode before changing accounting mappings." };
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.accountingLineAssignments?.clear !== "function") {
      return { ok: false, code: "accounting_line_review_unavailable", error: "Line-level accounting review is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.accountingLineAssignments.clear(assignment, organization.id);
    if (result?.ok) await refreshLoadedAccountPages([quickBooksCandidatePage]);
    setToast(result?.ok ? "Source line mapping cleared" : result?.error || "The source line mapping could not be cleared");
    return result;
  }, [organization?.id, publicMode, quickBooksCandidatePage, serverAccount]);
  const saveAccountingMapping = async (candidate) => {
    if (publicMode) {
      setToast("Turn off public mode before changing accounting rules");
      return false;
    }
    const validation = validateAccountingMapping({ ...candidate, workspaceId: organization.id });
    if (!validation.valid) {
      setToast(validation.errors[0]);
      return false;
    }
    let saved = normalizeAccountingMapping({
      ...validation.rule,
      workspaceId: organization.id,
      reviewedBy: validation.rule.status === "confirmed" ? profile.name : validation.rule.reviewedBy,
      reviewedAt: validation.rule.status === "confirmed" ? new Date().toISOString() : validation.rule.reviewedAt,
    });
    if (serverAccount) {
      let result;
      try {
        result = await accountRepositoryRuntime.accountingMappings.save(saved, organization.id);
      } catch {
        setToast("The accounting rule could not reach the account service.");
        return false;
      }
      if (!result?.ok) {
        setToast(result?.error || "The accounting rule could not be saved to this account.");
        return false;
      }
      saved = accountPageAdapters?.mapAccountAccountingMapping
        ? accountPageAdapters.mapAccountAccountingMapping(result.data, saved)
        : normalizeAccountingMapping({ ...saved, ...result.data, workspaceId: organization.id });
    }
    setAccountingMappings((current) => current.some((mapping) => mapping.id === saved.id)
      ? current.map((mapping) => mapping.id === saved.id ? saved : mapping)
      : [...current, saved]);
    if (serverAccount) await refreshLoadedAccountPages([accountingMappingPage, quickBooksCandidatePage]);
    if (!serverAccount) recordActivity({ action: saved.status === "confirmed" ? "Accounting rule confirmed" : "Accounting rule updated", detail: `${saved.name} saved as ${saved.status}`, entityType: "accounting-mapping", entityId: saved.id, businessId: saved.businessId });
    setToast(saved.status === "confirmed" ? "Accounting rule confirmed" : "Accounting rule saved for review");
    return saved;
  };
  const refreshActiveAccountPages = () => {
    setWorkCalendarRefreshKey(value => value + 1);
    return refreshLoadedAccountPages([
    businessPage,
    invoicePage,
    clientPage,
    clientInvoicePage,
    servicePage,
    taskPage,
    reminderPage,
    actionPage,
    routinePage,
    routineOccurrencePage,
    intakePage,
    purchaseVendorPage,
    purchaseDocumentPage,
    bankStatementPage,
    bankTransactionPage,
    auditPage,
    quickBooksExportPage,
    quickBooksCandidatePage,
    accountingMappingPage,
    ledgerAccountPage,
    journalEntryPage,
    fixedAssetPage,
    declarationPage,
    payrollEmployeePage,
    adminBusinessPage,
    adminMemberPage,
    adminInvitationPage,
    ]);
  };
  const runAccountCommand = async (command, { successMessage = "Change saved", onSuccess, onError } = {}) => {
    if (!serverAccount) return { ok: false, code: "not_account_mode", error: "An account workspace is not active." };
    const isCurrent = () => accountScopeRef.current === accountScope;
    const execute = () => coordinateAccountMutation({
      command,
      reloadWorkspace: () => accountRepositoryRuntime.workspace.read(authSession),
      applyWorkspace: applyAccountShell,
      isCurrent,
    });
    const queued = accountMutationQueueRef.current.then(execute, execute);
    accountMutationQueueRef.current = queued.then(() => undefined, () => undefined);
    const result = await queued;
    if (!isCurrent()) return { ok: false, code: "account_changed", error: "The active account changed." };
    if (!result.ok) {
      const message = result.committed
        ? `The change was saved, but the workspace could not refresh. Reload before making another change. ${result.error}`
        : result.error;
      if (onError) onError(message); else setToast(message);
      return result;
    }
    await Promise.allSettled([
      refreshActiveAccountPages(),
      administrationReadEnabled ? loadAccountAdministrationOverview() : Promise.resolve(),
    ]);
    if (!isCurrent()) return { ok: false, code: "account_changed", error: "The active account changed." };
    if (activeView === "clients" && accountClientWorkspaceId) setAccountClientDetailRefreshKey((current) => current + 1);
    if (activeView === "admin" && adminSection === "businesses" && selectedAdminBusinessWorkspaceId) setAccountAdminBusinessDetailRefreshKey((current) => current + 1);
    if (activeView === "admin" && adminSection === "team" && selectedAdminMemberResourceId) setAccountAdminMemberDetailRefreshKey((current) => current + 1);
    if (onSuccess) await onSuccess(result);
    setToast(successMessage);
    return result;
  };
  const loadTeamMemberProfile = useCallback((memberId, signal) =>
    accountRepositoryRuntime.reads.administration.memberProfile(organization.id, memberId, signal), [organization?.id]);
  const loadOrganizationSettings = useCallback(signal => accountRepositoryRuntime.organizationSettings.getReview(organization.id, signal), [organization?.id]);
  const loadBusinessContactReview = useCallback((businessId, contactId, signal) =>
    accountRepositoryRuntime.reads.administration.businessContactReview(organization.id, businessId, contactId, signal), [organization?.id]);
  const businessContactManagement = serverAccount && canManage && organization?.id ? {
    organizationId: organization.id, scopeKey: accountScope, readOnly: publicMode,
    loadReview: loadBusinessContactReview,
    listRecords: accountRepositoryRuntime.reads.administration.businessContactRecords,
    recoveryController: accountRepositoryRuntime.billingRecovery,
    saveChange: (source, input, action) => {
      if (publicMode || source.organizationId !== organization.id) return Promise.resolve({ ok: false, error: 'This account cannot change business contacts.' });
      return runAccountCommand(() => accountRepositoryRuntime.commands.saveBusinessContact(source, input, action), {
        successMessage: action === 'status' ? input.active ? 'Contact restored' : 'Contact deactivated' : 'Contact saved',
      });
    },
  } : null;
  const loadTeamBusinessAccess = useCallback((memberId, businessId, signal) =>
    accountRepositoryRuntime.reads.administration.memberBusinessAccess(organization.id, memberId, businessId, signal), [organization?.id]);
  const loadInvitationProfile = useCallback((invitationId, signal) =>
    accountRepositoryRuntime.reads.administration.invitationProfile(organization.id, invitationId, signal), [organization?.id]);
  const loadInvitationAccess = useCallback((invitationId, businessId, signal) =>
    accountRepositoryRuntime.reads.administration.invitationBusinessAccess(organization.id, invitationId, businessId, signal), [organization?.id]);
  const openAccountInvitation = invitationId => {
    setFocusedAdminInvitation({ organizationId: organization.id, id: invitationId });
    setFocusedAdminMemberId(invitationId);
    setAccountAdminMemberDetailRefreshKey(value => value + 1);
    navigateAdmin('team');
  };
  const saveInvitationChange = (section, source, body, invitationBusinesses) => {
    if (!serverAccount || !canManage || publicMode || (source && source.organizationId !== organization.id)) return Promise.resolve({ ok: false, error: 'This account cannot manage invitations.' });
    return runAccountCommand(() => section === 'create'
      ? accountRepositoryRuntime.commands.createReviewedInvitation(organization.id, body, invitationBusinesses)
      : accountRepositoryRuntime.commands.changeReviewedInvitation(section, source, body), {
      successMessage: section === 'create' || section === 'resend' ? 'Invitation email queued' : section === 'cancel' ? 'Invitation cancelled' : 'Invitation saved',
      onSuccess: result => openAccountInvitation(result.data.invitationId),
    });
  };
  const saveTeamSection = (section, source, body) => {
    if (!serverAccount || !canManage || publicMode || source.organizationId !== organization.id) return Promise.resolve({ ok: false, error: 'This account cannot change team access.' });
    const command = { profile: 'saveMemberProfile', role: 'saveMemberRole', access: 'saveMemberBusinessAccess' }[section];
    if (!command) return Promise.resolve({ ok: false, error: 'This member section is unavailable.' });
    return runAccountCommand(() => accountRepositoryRuntime.commands[command](source, body), { successMessage: section === 'profile' ? 'Member profile saved' : 'Member access saved' });
  };
  const teamManagement = serverAccount && canManage && organization?.id && profile?.id ? {
    organizationId: organization.id, scopeKey: accountScope, actor: { id: profile.id, role: profile.role.toLowerCase() }, readOnly: publicMode,
    loadProfile: loadTeamMemberProfile, loadAccess: loadTeamBusinessAccess,
    listBusinesses: accountRepositoryRuntime.reads.administration.businesses,
    saveChange: saveTeamSection, recoveryController: accountRepositoryRuntime.billingRecovery,
    invitations: { organizationId: organization.id, actor: { id: profile.id, role: profile.role.toLowerCase() }, readOnly: publicMode,
      loadProfile: loadInvitationProfile, loadAccess: loadInvitationAccess, loadManager: loadTeamMemberProfile,
      listBusinesses: accountRepositoryRuntime.reads.administration.businesses, listMembers: accountRepositoryRuntime.reads.administration.members,
      saveChange: saveInvitationChange, recoveryController: accountRepositoryRuntime.billingRecovery },
  } : null;
  const loadBusinessPortalMembers = useCallback((options) => {
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.reads?.businessPortal?.members !== "function") {
      return Promise.resolve({ ok: false, code: "portal_management_unavailable", error: "Business portal access management is unavailable." });
    }
    return accountRepositoryRuntime.reads.businessPortal.members({
      organizationId: organization.id,
      ...options,
    });
  }, [organization?.id, serverAccount]);
  const revokeBusinessPortalMember = (member) => runAccountCommand(
    accountRepositoryRuntime.portalCommands.revokeMembership(member),
    { successMessage: "Business portal access revoked" },
  );
  const savePortalPublication = publication => runAccountCommand(
    () => accountRepositoryRuntime.portalCommands.savePublication(publication),
    { successMessage: 'Publication saved' },
  );
  const loadBusinessPortalSubjects = useCallback((options) => {
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.reads?.businessPortal?.shareableSubjects !== "function") {
      return Promise.resolve({ ok: false, code: "portal_publishing_unavailable", error: "Business portal publishing is unavailable." });
    }
    return accountRepositoryRuntime.reads.businessPortal.shareableSubjects({
      organizationId: organization.id,
      ...options,
    });
  }, [organization?.id, serverAccount]);
  const publishBusinessPortalArtifact = async ({ business, subject, file }) => {
    if (!serverAccount || !organization?.id || !business?.id || !subject?.id || typeof accountRepositoryRuntime?.portalArtifacts?.publish !== "function") {
      return { ok: false, code: "portal_publishing_unavailable", error: "Business portal publishing is unavailable." };
    }
    let expectedRevision = subject.revision;
    if (!Number.isSafeInteger(expectedRevision)) {
      try {
        const latest = subject.subjectType === 'publication'
          ? await accountRepositoryRuntime.portalCommands.getPublication({ organizationId: organization.id, businessId: business.id, id: subject.id })
          : await accountRepositoryRuntime.reads.invoices.detail(organization.id, subject.id);
        if (!latest.ok) return latest;
        const { reviewedArtifactRevision } = await import(/* webpackChunkName: "account-business-portal-commands" */ './backend/accountPortalArtifacts');
        expectedRevision = reviewedArtifactRevision(subject, subject.subjectType === 'publication' ? latest.data : latest.data.invoice, { organizationId: organization.id, businessId: business.id });
      } catch {
        return { ok: false, code: 'resource_revision_conflict', error: 'This record changed. Refresh the portal records and review it before attaching a file.' };
      }
    }
    const result = await accountRepositoryRuntime.portalArtifacts.publish({
      organizationId: organization.id,
      businessId: business.id,
      subjectType: subject.subjectType,
      subjectId: subject.id,
      expectedRevision,
      file,
      filename: file.name,
    });
    if (result.ok) setToast(result.status === 'available' ? 'Verified portal file available' : result.status === 'pending_verification' ? 'Portal file received; safety checks pending' : 'Upload status confirmed. Review its attachment history.');
    else if (result.committed) setToast("The file reached private storage, but its verification state is uncertain. Refresh before trying again.");
    else setToast(result.error || "The portal file could not be uploaded");
    return result;
  };
  const getAccountInvoiceDocument = async (invoice, signal, format = 'pdf') => {
    if (!serverAccount || !organization?.id || !invoice?.id || !invoice?.issuerId || !Number.isSafeInteger(invoice.revision)) throw new Error("Reload this invoice before downloading it.");
    return accountRepositoryRuntime.reads.invoices.document({ organizationId: organization.id, businessId: invoice.issuerId, invoiceId: invoice.id, expectedRevision: invoice.revision }, signal, format);
  };
  const loadAccountInvoiceDetail = async (invoiceId, fallback = {}, requireFresh = false) => {
    if (!serverAccount || !invoiceId) return fallback;
    const isCurrent = () => accountScopeRef.current === accountScope;
    const result = await accountRepositoryRuntime.reads.invoices.detail(organization.id, invoiceId);
    if (!isCurrent()) return null;
    if (!result?.ok) return requireFresh ? null : fallback;
    if (result.data?.invoice?.id !== invoiceId || result.data.invoice.organizationId !== organization.id) return null;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    if (!isCurrent()) return null;
    const mapped = adapters.mapAccountInvoiceDetail(result.data, fallback);
    if (!ledgerAccessEnabled) return mapped;
    const paymentIds = mapped.payments?.map((payment) => payment.id).filter(Boolean) || [];
    const [sourceEvidence, paymentEvidence] = await Promise.all([
      accountRepositoryRuntime.reads.ledger.sourceEvidence({ organizationId: organization.id, sourceType: "sales_invoice", sourceIds: [invoiceId] }),
      paymentIds.length
        ? accountRepositoryRuntime.reads.ledger.sourceEvidence({ organizationId: organization.id, sourceType: "sales_payment", sourceIds: paymentIds })
        : Promise.resolve({ ok: true, data: { items: [] } }),
    ]);
    const sourceJournals = journalEvidenceBySource(sourceEvidence);
    if (!isCurrent()) return null;
    return {
      ...mapped,
      ledgerJournal: sourceJournals[invoiceId] || null,
      ledgerPaymentJournals: journalEvidenceBySource(paymentEvidence),
    };
  };
  const loadAccountPurchaseDetail = useCallback(async (purchaseDocumentId, fallback = {}) => {
    if (!serverAccount || !purchaseDocumentId) return fallback;
    const result = await accountRepositoryRuntime.reads.purchaseDocuments.detail(organization.id, purchaseDocumentId);
    if (!result?.ok) throw new Error(result?.error || "The purchase could not be loaded.");
    if (result.data?.document?.id !== purchaseDocumentId || result.data.document.organizationId !== organization.id) throw new Error("The purchase detail did not match this workspace.");
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    if (accountScopeRef.current !== accountScope) throw new Error("The active account changed. Reopen the purchase in its original workspace.");
    const mapped = adapters.mapAccountPurchaseDocumentDetail(result.data, fallback);
    if (!ledgerAccessEnabled) return mapped;
    const paymentIds = mapped.payments?.map((payment) => payment.id).filter(Boolean) || [];
    const [sourceEvidence, paymentEvidence] = await Promise.all([
      accountRepositoryRuntime.reads.ledger.sourceEvidence({ organizationId: organization.id, sourceType: "purchase_document", sourceIds: [purchaseDocumentId] }),
      paymentIds.length
        ? accountRepositoryRuntime.reads.ledger.sourceEvidence({ organizationId: organization.id, sourceType: "purchase_payment", sourceIds: paymentIds })
        : Promise.resolve({ ok: true, data: { items: [] } }),
    ]);
    const sourceJournals = journalEvidenceBySource(sourceEvidence);
    return {
      ...mapped,
      ledgerJournal: sourceJournals[purchaseDocumentId] || null,
      ledgerPaymentJournals: journalEvidenceBySource(paymentEvidence),
    };
  }, [accountPageAdapters, accountScope, ledgerAccessEnabled, organization?.id, serverAccount]);
  const loadAccountPurchaseHistory = useCallback(async (purchaseDocumentId, kind, cursor) => {
    if (!serverAccount || !purchaseDocumentId) return { ok: false, error: "An account workspace is not active." };
    const result = await accountRepositoryRuntime.reads.purchaseDocuments.history({
      organizationId: organization.id,
      documentId: purchaseDocumentId,
      kind,
      limit: 25,
      cursor,
    });
    if (accountScopeRef.current !== accountScope) return { ok: false, error: "The active account changed. Reopen the purchase in its original workspace." };
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    const page = adapters.mapAccountPurchaseHistoryPage(kind, result.data, { organizationId: organization.id, documentId: purchaseDocumentId });
    if (!ledgerAccessEnabled || kind !== "payments" || !page.items?.length) return { ok: true, data: page };
    const evidence = await accountRepositoryRuntime.reads.ledger.sourceEvidence({
      organizationId: organization.id,
      sourceType: "purchase_payment",
      sourceIds: page.items.map((payment) => payment.id).filter(Boolean),
    });
    return { ok: true, data: { ...page, ledgerPaymentJournals: journalEvidenceBySource(evidence) } };
  }, [accountPageAdapters, accountScope, ledgerAccessEnabled, organization?.id, serverAccount]);
  const loadAccountIntakeDetail = useCallback(async (intakeRecordId, fallback = {}) => {
    if (!serverAccount || !intakeRecordId) return fallback;
    const result = await accountRepositoryRuntime.reads.intake.detail(organization.id, intakeRecordId, true);
    if (accountScopeRef.current !== accountScope) throw new Error("The active account changed. Reopen the document in its original workspace.");
    if (!result?.ok) throw new Error(result?.error || "The document could not be loaded.");
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return adapters.mapAccountIntakeDetail(result.data, fallback);
  }, [accountPageAdapters, accountScope, organization?.id, serverAccount]);
  const loadAccountIntakeHistory = useCallback(async (intakeRecordId, kind, cursor) => {
    if (!serverAccount || !intakeRecordId) return { ok: false, error: "An account workspace is not active." };
    const result = await accountRepositoryRuntime.reads.intake.history({
      organizationId: organization.id,
      intakeRecordId,
      kind,
      limit: 25,
      cursor,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return { ok: true, data: adapters.mapAccountIntakeHistoryPage(kind, result.data) };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountIntakeEvidence = useCallback(async (intakeRecordId, extractionRunId, cursor) => {
    if (!serverAccount || !intakeRecordId || !extractionRunId) return { ok: false, error: "An extraction run is not active." };
    const result = await accountRepositoryRuntime.reads.intake.evidence({
      organizationId: organization.id,
      intakeRecordId,
      extractionRunId,
      limit: 25,
      cursor,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return { ok: true, data: adapters.mapAccountIntakeEvidencePage(result.data) };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountIntakeSource = useCallback(async (intakeRecordId, purpose = "preview") => {
    if (!serverAccount || !intakeRecordId) return { ok: false, code: "not_account_mode", error: "An account workspace is not active." };
    return accountRepositoryRuntime.reads.intake.sourceAccess(organization.id, intakeRecordId, purpose);
  }, [organization?.id, serverAccount]);
  const loadAccountMonthlyCloseChecklist = useCallback(async (businessId, period) => {
    if (!serverAccount || !organization?.id || !businessId || !period) return null;
    const result = await accountRepositoryRuntime.reads.intake.monthlyClose(organization.id, businessId, period);
    if (!result?.ok) throw new Error(result?.error || "The monthly checklist could not be loaded.");
    if (!result.data) return null;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return adapters.mapAccountMonthlyCloseChecklist(result.data);
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountBankTransactionDetail = useCallback(async (bankTransactionId, fallback = {}) => {
    if (!serverAccount || !bankTransactionId) throw new Error("An account workspace is not active.");
    const [loader, adapters] = await Promise.all([
      import(/* webpackChunkName: "account-bank-review" */ "./repositories/accountBankingReviewLoader"),
      accountPageAdapters || import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters"),
    ]);
    return loader.loadAccountBankingReview({ organizationId: organization.id, transactionId: bankTransactionId, fallback,
      reads: accountRepositoryRuntime.reads, ledgerEnabled: ledgerAccessEnabled,
      mapDetail: adapters.mapAccountBankTransactionDetail, isCurrent: () => accountScopeRef.current === accountScope });
  }, [accountPageAdapters, accountScope, ledgerAccessEnabled, organization?.id, serverAccount]);
  const loadAccountBankTransactionHistory = useCallback(async (bankTransactionId, kind, cursor) => {
    if (!serverAccount || !bankTransactionId) return { ok: false, error: "An account workspace is not active." };
    const result = await accountRepositoryRuntime.reads.bankTransactions.history({
      organizationId: organization.id,
      transactionId: bankTransactionId,
      kind,
      limit: 25,
      cursor,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return { ok: true, data: adapters.mapAccountBankHistoryPage(kind, result.data) };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountBankTransactionReview = useCallback(async (bankTransactionId, businessId, beforeRevision) => {
    if (!serverAccount || !bankTransactionId || !businessId) return { ok: false, error: "An account workspace is not active." };
    return accountRepositoryRuntime.reads.bankTransactions.review({ organizationId: organization.id, businessId,
      transactionId: bankTransactionId, beforeRevision, limit: 20 });
  }, [organization?.id, serverAccount]);
  const loadBankLedgerAccounts = useCallback(({ businessId, role, limit = 50, cursor = null, query = "" }) => {
    if (!serverAccount || !ledgerAccessEnabled || !organization?.id || !businessId) {
      return Promise.resolve({ ok: false, code: "ledger_unavailable", error: "Financials access is required for this business." });
    }
    return accountRepositoryRuntime.reads.bankAccounts.ledgerOptions({
      organizationId: organization.id,
      businessId,
      role,
      limit,
      cursor,
      query,
    });
  }, [ledgerAccessEnabled, organization?.id, serverAccount]);
  const loadBankEvidenceOwners = useCallback(({ businessId, limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || !businessId) return Promise.resolve({ ok: false, error: "The Banking team directory is unavailable." });
    return accountRepositoryRuntime.reads.bankEvidenceOwners.list({ organizationId: organization.id, businessId, limit, cursor, query });
  }, [organization?.id, serverAccount]);
  const loadInvoicePaymentReview = (input) => accountRepositoryRuntime.ledger.reviewPayment({ ...input, organizationId: organization.id, kind: 'invoice' });
  const loadPurchasePaymentReview = (input) => accountRepositoryRuntime.ledger.reviewPayment({ ...input, organizationId: organization.id, kind: 'purchase' });
  const loadPaymentCashAccounts = (input) => accountRepositoryRuntime.ledger.paymentCashAccounts({ ...input, organizationId: organization.id });
  const postSalesInvoiceLedger = async ({ sourceId, exchangeRate, revision, expectedBaseCurrency }) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before posting accounting records." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.ledger.postSalesInvoice({ organizationId: organization.id, invoiceId: sourceId, exchangeRate, revision, expectedBaseCurrency }),
      {
        successMessage: "Invoice posted to Financials",
        onSuccess: async () => {
          const updated = await loadAccountInvoiceDetail(sourceId, selectedInvoice || {});
          if (updated?.id) setSelectedInvoice((current) => current?.id === sourceId ? updated : current);
        },
      },
    );
  };
  const postInvoicePaymentLedger = async ({ paymentId, exchangeRate, paymentPosting }) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before posting accounting records." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.ledger.postInvoicePayment({ organizationId: organization.id, paymentId, exchangeRate, paymentPosting }),
      {
        successMessage: "Invoice payment posted to Financials",
        onSuccess: async () => {
          if (!selectedInvoice?.id) return;
          const updated = await loadAccountInvoiceDetail(selectedInvoice.id, selectedInvoice);
          if (updated?.id) setSelectedInvoice(updated);
        },
      },
    );
  };
  const postPurchaseDocumentLedger = async ({ sourceId, exchangeRate, revision, expectedBaseCurrency }) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before posting accounting records." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.ledger.postPurchaseDocument({ organizationId: organization.id, purchaseDocumentId: sourceId, exchangeRate, revision, expectedBaseCurrency }),
      { successMessage: "Purchase posted to Financials" },
    );
  };
  const postPurchasePaymentLedger = async ({ paymentId, exchangeRate, paymentPosting }) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before posting accounting records." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.ledger.postPurchasePayment({ organizationId: organization.id, paymentId, exchangeRate, paymentPosting }),
      { successMessage: "Vendor payment posted to Financials" },
    );
  };
  const postBankTransactionLedger = async ({ transactionId, exchangeRate, cashAccountId, counterAccountId, expectedRevision, expectedCashAccountRevision, expectedCounterAccountRevision, expectedBaseCurrency, bankTransaction, cashAccount, counterAccount }) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before posting accounting records." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.ledger.postBankTransaction({
        organizationId: organization.id,
        bankTransactionId: transactionId,
        exchangeRate,
        cashAccountId,
        counterAccountId,
        expectedRevision,
        expectedCashAccountRevision,
        expectedCounterAccountRevision,
        expectedBaseCurrency,
        bankTransaction,
        cashAccount,
        counterAccount,
      }),
      { successMessage: "Bank transaction posted to Financials" },
    );
  };
  const saveBankLedgerAccountMapping = async (review) => {
    if (publicMode || !ledgerAccessEnabled) {
      const error = publicMode ? "Turn off public mode before changing accounting defaults." : "Financials access is required for this business.";
      setToast(error);
      return { ok: false, error };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.banking.saveLedgerMapping({
        ...review,
        organizationId: organization.id,
      }),
      { successMessage: "Bank account default saved" },
    );
  };
  const resetSurface = useCallback(() => {
    setEditorOpen(false);
    setCreateOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setOrganizationOpen(false);
    setUserOpen(false);
    setIssuerEditor(null);
    setClientEditor(null);
    setServiceEditor(null);
    setWhatsappRequest(null);
    setSearchState({ query: "", scope: "All" });
    setEditorReturnContext(null);
    setRecordReturnContext(null);
    setFocusedClientId("");
    setFocusedBusinessId("");
    setFocusedIntakeItemId("");
    setFocusedPurchaseId("");
    setBusinessScopeId(ALL_BUSINESSES_SCOPE);
    setExpandedNavGroups({ work: false, workspace: false, accounting: false });
    setWorkspaceMode("regular");
    setAdminSection("overview");
    setActiveView("overview");
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (authReady || startup.authReady || !accountRepositoryRuntime) return undefined;
    let active = true;
    accountRepositoryRuntime.restore(accountInvitation).then((result) => {
      if (!active) return;
      if (result.ok && result.requiresWorkspaceSelection) {
        setAccountWorkspaceSelection(result.workspaceIdentity);
        setAuthSession(null);
        if (accountInvitation) {
          clearAccountInvitationIntent();
          setAccountInvitation(null);
        }
      } else if (result.ok && result.session && result.data) {
        applyData(result.data);
        setAuthSession(result.session);
        if (accountInvitation) clearAccountInvitationIntent();
      } else if (!result.ok) {
        setToast(result.error || "Your account session could not be restored.");
      }
      setAuthReady(true);
    });
    return () => { active = false; };
  }, [accountInvitation, applyData, authReady, startup.authReady]);

  const openSelectedAccountWorkspace = (result) => {
    applyData(result.data);
    setAuthSession(result.session);
    setSelectedInvoice(result.data.invoices.find((invoice) => invoice.status === "Draft") || result.data.invoices[0] || null);
    setAccountWorkspaceSelection(null);
    resetSurface();
  };
  const switchAccountWorkspace = serverAccount && accountRepositoryRuntime?.workspaceDirectoryEnabled ? async () => {
    resetSurface();
    setSelectedInvoice(null);
    setAuthSession(null);
    setAccountWorkspaceSelection({ loading: true });
    try {
      const result = await accountRepositoryRuntime.restore();
      setAccountWorkspaceSelection(result.requiresWorkspaceSelection ? result.workspaceIdentity : null);
      if (!result.ok) setToast(result.error);
    } catch {
      setAccountWorkspaceSelection(null);
      setToast("Workspace access could not be refreshed. Sign in again.");
    }
  } : null;
  const signIn = async ({ email, password, remember }) => {
    if (accountRepositoryRuntime) {
      const result = await accountRepositoryRuntime.signIn({ email, password, remember }, accountInvitation);
      if (!result.ok) return result;
      if (result.requiresWorkspaceSelection) {
        setAccountWorkspaceSelection(result.workspaceIdentity);
        setAuthSession(null);
        resetSurface();
      } else openSelectedAccountWorkspace(result);
      if (accountInvitation) {
        clearAccountInvitationIntent();
        setAccountInvitation(null);
      }
      return { ok: true };
    }

    return { ok: false, error: "Account sign-in is not configured in this environment. Continue locally to use the offline workspace." };
  };
  const recoverAccount = async (email) => {
    const { requestAccountRecovery } = await import(/* webpackChunkName: "password-recovery-client" */ "./backend/passwordRecoveryClient");
    return requestAccountRecovery(email, Boolean(accountRepositoryRuntime));
  };
  const usePrototypeRole = async (role = "regular") => {
    if (!await prepareLocalDeparture()) return;
    const portalPreview = role === "portal";
    const portalSample = portalPreview ? await import(/* webpackChunkName: "business-portal-sample" */ "./businessPortalSampleData") : null;
    const nextSpace = portalPreview ? "portal-sample" : PUBLIC_GUEST_MODE ? "sample" : "standard";
    const selectedProfile = portalPreview ? portalSample.BUSINESS_PORTAL_PROFILE : role === "admin" ? USER_PROFILE
      : TEAM_MEMBERS.find(member => member.role === "Employee" && member.status === "Active") || TEAM_MEMBERS.find(member => member.role !== "Owner") || USER_PROFILE;
    const owner = createLocalPersistence(nextSpace, portalPreview ? portalSample.createBusinessPortalSampleData() : guestSampleData());
    const stored = owner.snapshot();
    if (!stored || owner.status().phase === "blocked") { setToast("The saved local workspace needs recovery before it can be changed."); return; }
    const nextData = upgradeSampleWorkspace(stored);
    const nextProfile = stored && !stored.sampleWorkspace
      ? { ...selectedProfile, ...stored.profile, workspaceId: stored.profile?.workspaceId || stored.workspaces?.[0]?.id || selectedProfile.workspaceId, publicMode: false }
      : { ...selectedProfile, publicMode: false };
    const data = { ...nextData, profile: nextProfile };
    const result = prototypeIdentityRepository.startLocalSession(data.workspaces[0].id, nextSpace);
    if (!result.ok) { owner.retire(); setToast(result.error); return; }
    installLocalView(owner, data, result.session);
    resetSurface();
  };
  const useLocal = () => usePrototypeRole("regular");
  const usePersonalLocal = async () => {
    if (!await prepareLocalDeparture()) return;
    const owner = createLocalPersistence("custom", createEmptyLocalWorkspaceData());
    if (owner.status().phase === "pending") { onboardingPersistenceRef.current = owner; setLocalOnboardingOpen("custom"); return; }
    const stored = owner.snapshot();
    if (!stored?.workspaces?.[0]?.id || owner.status().phase === "blocked") { setToast("The saved local workspace needs recovery. It will not be overwritten."); return; }
    const result = prototypeIdentityRepository.startLocalSession(stored.workspaces[0].id, "custom");
    if (!result.ok) { owner.retire(); setToast(result.error); return; }
    installLocalView(owner, stored, result.session);
    resetSurface();
  };
  const openEmptyLocalData = async (nextData, message = "Empty local workspace ready", hydratedOwner = null) => {
    if (!await prepareLocalDeparture()) return false;
    const pending = onboardingPersistenceRef.current?.status().space === "empty" ? onboardingPersistenceRef.current : null;
    const owner = hydratedOwner || pending || createLocalPersistence("empty", nextData);
    if ((!hydratedOwner && !pending && owner.status().phase === "saved") || owner.status().phase === "blocked") {
      setToast("An existing empty workspace must be reopened before replacing its setup."); return false;
    }
    owner.replace(nextData);
    const persisted = await owner.save();
    if (!persisted.ok || owner.status().phase !== "saved") { setToast("Browser storage could not safely save this workspace. Existing records were preserved."); return false; }
    const data = owner.snapshot(), organizationId = data.workspaces?.[0]?.id;
    const result = prototypeIdentityRepository.startLocalSession(organizationId, "empty");
    if (!result.ok) { owner.retire(); setToast(result.error); return false; }
    writeNavigationPreferences(organizationId, data.profile?.id, data.profile?.navigationPreferences);
    installLocalView(owner, data, result.session);
    onboardingPersistenceRef.current = null;
    setLocalOnboardingOpen(false); resetSurface(); setToast(message);
    return true;
  };
  const useEmptyLocal = async () => {
    if (!await prepareLocalDeparture()) return;
    const owner = createLocalPersistence("empty", createEmptyLocalWorkspaceData());
    const stored = owner.snapshot();
    if (!stored || owner.status().phase === "blocked") { setToast("The saved empty workspace needs recovery. It will not be overwritten."); return; }
    const hasRecords = [stored.businesses, stored.clients, stored.invoices, stored.workTasks, stored.workReminders].some(records => records?.length);
    if (owner.status().phase === "pending" || (!stored.onboardingCompleted && !hasRecords)) {
      onboardingPersistenceRef.current = owner; setLocalOnboardingOpen("empty"); return;
    }
    await openEmptyLocalData(stored, "Empty local workspace opened", owner);
  };
  const completeEmptyLocalOnboarding = async (details = {}) => {
    const ownerName = String(details.ownerName || "Local owner").trim() || "Local owner";
    const businessName = String(details.businessName || "").trim();
    const nextData = businessName ? createLocalWorkspaceData({ ...details, ownerName, businessName, legalName: details.legalName || businessName, navigationPreferences: details.navigationPreferences })
      : createEmptyLocalWorkspaceData({ ...details, ownerName });
    nextData.onboardingCompleted = true;
    return await openEmptyLocalData(nextData, "Your empty workspace is ready") ? { ok: true } : { ok: false, error: "This browser could not safely open the empty workspace." };
  };
  const completeLocalOnboarding = async details => {
    if (!await prepareLocalDeparture()) return { ok: false, error: "Keep your unsaved changes before switching workspaces." };
    const nextData = createLocalWorkspaceData(details);
    const pending = onboardingPersistenceRef.current?.status().space === "custom" ? onboardingPersistenceRef.current : null;
    const owner = pending || createLocalPersistence("custom", nextData);
    if ((!pending && owner.status().phase === "saved") || owner.status().phase === "blocked") return { ok: false, error: "An existing workspace must be reopened, not overwritten by setup." };
    owner.replace(nextData);
    const persisted = await owner.save();
    if (!persisted.ok || owner.status().phase !== "saved") return { ok: false, error: "Browser storage could not safely save the new workspace." };
    const data = owner.snapshot(), result = prototypeIdentityRepository.startLocalSession(data.workspaces[0].id, "custom");
    if (!result.ok) { owner.retire(); return result; }
    installLocalView(owner, data, result.session);
    onboardingPersistenceRef.current = null;
    setLocalOnboardingOpen(false); resetSurface(); setToast("Local workspace created");
    return { ok: true };
  };
  const resetGuestWorkspace = () => {
    if (!PUBLIC_GUEST_MODE || sessionMode !== "local") return;
    applyData(guestSampleData());
    resetSurface();
    setToast("Guest sample records restored");
  };
  const signOut = async () => {
    if (!await prepareLocalDeparture()) return { ok: false, error: "Unsaved local changes need recovery." };
    const result = authSession?.authority === "supabase" || accountWorkspaceSelection
      ? await accountRepositoryRuntime.signOut()
      : prototypeIdentityRepository.signOut();
    if (!result.ok) {
      setToast(result.error);
      return result;
    }
    resetSurface();
    setSelectedInvoice(null);
    localPersistence.retire();
    setAuthSession(null);
    setAccountWorkspaceSelection(null);
    return result;
  };
  const moveWorkTask = async (task, anchor, placement) => {
    if (!serverAccount || publicMode || !sharedTaskOrder || taskPage.loading || taskPage.error) return false;
    const result = await runAccountCommand(() => accountRepositoryRuntime.work.moveTask(task, anchor, placement), { successMessage: "Task order saved" });
    return result.ok;
  };
  const saveWorkTask = async (task) => {
    if (publicMode) {
      setToast("Turn off public mode before changing work items");
      return false;
    }
    const prepared = { ...task, organizationId: organization.id, workspaceId: organization.id };
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.work.saveTask(prepared),
        { successMessage: task.id ? "Task updated" : "Task added" },
      );
      return result.ok;
    }
    const now = new Date().toISOString();
    const currentRecords = workTaskRecordsRef.current;
    const existing = currentRecords.find((item) => item.id === task.id);
    const saved = {
      ...prepared,
      id: existing?.id || localWorkId("local-task"),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      completedAt: prepared.status === "done" ? existing?.completedAt || now : "",
      archived: false,
    };
    const nextRecords = existing ? currentRecords.map((item) => item.id === existing.id ? saved : item) : [saved, ...currentRecords];
    workTaskRecordsRef.current = nextRecords;
    setWorkTaskRecords(nextRecords);
    setWorkItemActivityRecords((current) => [{
      id: localWorkId("local-work-activity"),
      workspaceId: organization.id,
      businessId: saved.businessId,
      taskId: saved.id,
      reminderId: "",
      actorId: profile.id,
      actor: profile.name,
      action: !existing ? "created" : existing.status !== saved.status ? "status_changed" : existing.assigneeUserId !== saved.assigneeUserId ? "assigned" : "updated",
      note: "",
      safeChanges: { status: saved.status, priority: saved.priority, assigneeUserId: saved.assigneeUserId || "" },
      occurredAt: now,
    }, ...current]);
    recordActivity({ action: existing ? "Work task updated" : "Work task created", detail: existing ? "Updated a daily work item" : "Added a daily work item", entityType: "work-task", entityId: saved.id, businessId: saved.businessId });
    setToast(existing ? "Task updated" : "Task added");
    return true;
  };
  const saveWorkReminder = async (reminder) => {
    if (publicMode) {
      setToast("Turn off public mode before changing work items");
      return false;
    }
    const prepared = { ...reminder, organizationId: organization.id, workspaceId: organization.id };
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.work.saveReminder(prepared),
        { successMessage: reminder.id ? "Reminder updated" : "Reminder added" },
      );
      return result.ok;
    }
    const now = new Date().toISOString();
    const currentRecords = workReminderRecordsRef.current;
    const existing = currentRecords.find((item) => item.id === reminder.id);
    const saved = {
      ...prepared,
      id: existing?.id || localWorkId("local-reminder"),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      completedAt: prepared.status === "completed" ? existing?.completedAt || now : "",
      archived: false,
    };
    const nextRecords = existing ? currentRecords.map((item) => item.id === existing.id ? saved : item) : [saved, ...currentRecords];
    workReminderRecordsRef.current = nextRecords;
    setWorkReminderRecords(nextRecords);
    setWorkItemActivityRecords((current) => [{
      id: localWorkId("local-work-activity"),
      workspaceId: organization.id,
      businessId: saved.businessId,
      taskId: "",
      reminderId: saved.id,
      actorId: profile.id,
      actor: profile.name,
      action: !existing ? "created" : existing.status !== saved.status ? "status_changed" : existing.assigneeUserId !== saved.assigneeUserId ? "assigned" : "updated",
      note: "",
      safeChanges: { status: saved.status, priority: saved.priority, assigneeUserId: saved.assigneeUserId || "" },
      occurredAt: now,
    }, ...current]);
    recordActivity({ action: existing ? "Work reminder updated" : "Work reminder created", detail: existing ? "Updated an issuing-business reminder" : "Added an issuing-business reminder", entityType: "work-reminder", entityId: saved.id, businessId: saved.businessId });
    setToast(existing ? "Reminder updated" : "Reminder added");
    return true;
  };
  const loadWorkAssignees = useCallback(async ({ businessId, limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || !businessId || typeof accountRepositoryRuntime?.reads?.workAssignees?.list !== "function") {
      return { ok: false, code: "work_assignees_unavailable", error: "The assignee directory is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.workAssignees.list({
      organizationId: organization.id,
      businessId,
      limit,
      cursor,
      query,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...result.data,
        items: (result.data?.items || []).map(adapters.mapAccountWorkAssignee),
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadRoutineAssignees = useCallback(async ({ scope, businessIds = [], limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.reads?.routineAssignees?.list !== "function") {
      return { ok: false, code: "routine_assignees_unavailable", error: "The recurring-work owner directory is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.routineAssignees.list({
      organizationId: organization.id,
      scope,
      businessIds,
      limit,
      cursor,
      query,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...result.data,
        items: (result.data?.items || []).map(adapters.mapAccountRoutineAssignee),
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountBusinesses = useCallback(async ({ limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || typeof accountRepositoryRuntime?.reads?.businesses?.list !== "function") {
      return { ok: false, code: "businesses_unavailable", error: "The issuing-business directory is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.businesses.list({
      organizationId: organization.id,
      limit,
      cursor,
      query,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...result.data,
        items: (result.data?.items || []).map((item, index) => ({
          ...adapters.mapAccountBusiness(item, {}, index),
          logo: accountRepositoryRuntime?.brandAssets?.getPublicUrl?.(item.logoObjectKey) || "",
        })),
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadAccountBusiness = useCallback(async ({ businessId }) => {
    if (!serverAccount || !organization?.id || !businessId || typeof accountRepositoryRuntime?.reads?.businesses?.get !== "function") {
      return { ok: false, code: "business_unavailable", error: "This issuing business is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.businesses.get({
      organizationId: organization.id,
      businessId,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...adapters.mapAccountBusiness(result.data),
        logo: accountRepositoryRuntime?.brandAssets?.getPublicUrl?.(result.data?.logoObjectKey) || "",
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadInvoiceClientOptions = useCallback(async ({ businessId, limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || !businessId || typeof accountRepositoryRuntime?.reads?.clients?.invoiceOptions !== "function") {
      return { ok: false, code: "invoice_client_options_unavailable", error: "The invoice client directory is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.clients.invoiceOptions({
      organizationId: organization.id,
      businessId,
      limit,
      cursor,
      query,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...result.data,
        items: (result.data?.items || []).map((item, index) => adapters.mapAccountInvoiceClientOption(item, {}, index)),
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadInvoiceClientOption = useCallback(async ({ businessId, clientId }) => {
    if (!serverAccount || !organization?.id || !businessId || !clientId || typeof accountRepositoryRuntime?.reads?.clients?.invoiceOption !== "function") {
      return { ok: false, code: "invoice_client_unavailable", error: "This client is not available to the selected issuing business." };
    }
    const result = await accountRepositoryRuntime.reads.clients.invoiceOption({
      organizationId: organization.id,
      businessId,
      clientId,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return { ...result, data: adapters.mapAccountInvoiceClientOption(result.data) };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadInvoiceServices = useCallback(async ({ businessId, limit = 25, cursor = null, query = "" }) => {
    if (!serverAccount || !organization?.id || !businessId || typeof accountRepositoryRuntime?.reads?.services?.list !== "function") {
      return { ok: false, code: "invoice_services_unavailable", error: "The invoice service directory is not available in this workspace." };
    }
    const result = await accountRepositoryRuntime.reads.services.list({
      organizationId: organization.id,
      businessId,
      limit,
      cursor,
      query,
      includeArchived: false,
    });
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    return {
      ...result,
      data: {
        ...result.data,
        items: (result.data?.items || []).map((item) => adapters.mapAccountService(item)),
      },
    };
  }, [accountPageAdapters, organization?.id, serverAccount]);
  const loadWorkTaskDetail = async (taskId) => {
    if (!serverAccount || !taskId) return null;
    const result = await accountRepositoryRuntime.reads.tasks.detail(organization.id, taskId);
    if (!result?.ok) {
      setToast(result?.error || "The task details could not be loaded.");
      return null;
    }
    return result.data;
  };
  const loadWorkReminderDetail = async (reminderId) => {
    if (!serverAccount || !reminderId) return null;
    const result = await accountRepositoryRuntime.reads.reminders.detail(organization.id, reminderId);
    if (!result?.ok) {
      setToast(result?.error || "The reminder details could not be loaded.");
      return null;
    }
    return result.data;
  };
  const loadWorkActivityHistory = async (item, cursor) => {
    if (!serverAccount || !item?.id || !item?.workItemType || !cursor) return null;
    return accountRepositoryRuntime.reads.workActivities.list({
      organizationId: organization.id,
      kind: item.workItemType === "task" ? "tasks" : "reminders",
      itemId: item.id,
      limit: 25,
      cursor,
    });
  };
  const loadWorkRoutine = async (routineId) => {
    if (!serverAccount || !routineId) return null;
    const result = await accountRepositoryRuntime.reads.routines.detail(organization.id, routineId);
    if (!result?.ok) {
      setToast(result?.error || "The recurring routine could not be loaded.");
      return null;
    }
    return result.data;
  };
  const loadWorkRoutineCollection = async (routineId, kind, cursor, limit = 25) => {
    if (!serverAccount || !routineId || !cursor) return null;
    return accountRepositoryRuntime.reads.routines.collection({
      organizationId: organization.id,
      routineId,
      kind,
      limit,
      cursor,
    });
  };
  const saveWorkRoutine = async (routine) => {
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.work.saveRoutine({ ...routine, organizationId: organization.id }),
      { successMessage: routine.id ? "Recurring routine updated" : "Recurring routine created" },
    );
    if (!result.ok) throw new Error(result.error || "The routine change could not be confirmed.");
    return result.data;
  };
  const changeWorkRoutineStatus = async (routine, status) => {
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.work.setRoutineStatus({ organizationId: organization.id, routineId: routine.id, status, expectedRevision: routine.revision, title: routine.title }),
      { successMessage: status === "active" ? "Recurring routine resumed" : "Recurring routine paused" },
    );
    if (!result.ok) throw new Error(result.error || "The routine status could not be confirmed.");
    return result.data;
  };
  const generateWorkRoutinePeriod = async (routine, period, backfill) => {
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.work.generateRoutinePeriod({ organizationId: organization.id, routineId: routine.id, period, backfill, title: routine.title }),
      { successMessage: backfill ? "Routine backfill generated" : "Routine period generated" },
    );
    if (!result.ok) throw new Error(result.error || "The routine period could not be confirmed.");
    return result.data;
  };
  const skipWorkRoutinePeriod = async (routine, period, reason) => {
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.work.skipRoutinePeriod({ organizationId: organization.id, routineId: routine.id, period, reason, title: routine.title }),
      { successMessage: "Routine period skipped" },
    );
    if (!result.ok) throw new Error(result.error || "The skipped period could not be confirmed.");
    return result.data;
  };
  const changeWorkChecklistItem = async (check, completed, taskId, businessId) => {
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.work.setChecklistItemStatus({
        organizationId: organization.id,
        checklistItemId: check.id,
        completed,
        expectedRevision: check.revision,
        taskId,
        businessId,
        title: check.title,
        position: check.position,
        required: check.required,
      }),
      { successMessage: completed ? "Checklist item completed" : "Checklist item reopened" },
    );
    if (!result.ok) throw new Error(result.error || "The checklist change could not be confirmed.");
    const detail = await loadWorkTaskDetail(taskId);
    if (!detail) throw new Error("The checklist change was saved. Refresh the task to see its latest state.");
    return detail;
  };
  const addWorkItemNote = async (item, note) => {
    if (publicMode) {
      setToast("Turn off public mode before adding internal notes");
      return false;
    }
    const task = item.workItemType === "task" || organizationWorkTasks.some((record) => record.id === item.id);
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.work.addNote({ organizationId: organization.id, businessId: item.businessId, title: item.title, taskId: task ? item.id : null, reminderId: task ? null : item.id, note }),
        { successMessage: "Note added" },
      );
      return result.ok;
    }
    setWorkItemActivityRecords((current) => [{
      id: localWorkId("local-work-activity"),
      workspaceId: organization.id,
      businessId: item.businessId,
      taskId: task ? item.id : "",
      reminderId: task ? "" : item.id,
      actorId: profile.id,
      actor: profile.name,
      action: "note_added",
      note,
      safeChanges: {},
      occurredAt: new Date().toISOString(),
    }, ...current]);
    setToast("Note added");
    return true;
  };
  const syncReviewedLogo = (latest, url) => {
    if (!serverAccount || latest.organizationId !== organization.id) return;
    const branding = { logo: url, logoObjectKey: latest.logoObjectKey };
    if (latest.businessId === null) setWorkspaces(current => current.map(item => item.id === latest.organizationId ? { ...item, ...branding } : item));
    else setBusinesses(current => current.map(item => item.id === latest.businessId ? { ...item, ...branding } : item));
  };
  const saveOrganization = async (nextOrganization) => {
    if (!canManage) {
      setToast("Only an owner or administrator can change organization settings");
      return false;
    }
    const organizationRecord = { ...nextOrganization };
    delete organizationRecord.logoFile;
    if (serverAccount) {
      setToast('Reopen Settings to review and save each organization section.');
      return false;
    }
    setWorkspaces((current) => current.map((item) => item.id === organizationRecord.id ? { ...item, ...organizationRecord } : item));
    recordActivity({ action: "Organization updated", detail: `Saved profile details for ${organizationRecord.name}`, entityType: "organization", entityId: organizationRecord.id });
    return true;
  };
  const organizationSettingsManagement = serverAccount && canManage ? {
    organizationId: organization.id, scopeKey: accountScope, readOnly: publicMode,
    loadReview: loadOrganizationSettings, recoveryController: accountRepositoryRuntime.billingRecovery,
    brandRepository: accountRepositoryRuntime.brandUploads, onLogoUpdated: syncReviewedLogo,
    saveChange: (section, fields, revision) => publicMode
      ? Promise.resolve({ ok: false, error: 'Turn off public mode before editing organization settings.' })
      : runAccountCommand(() => accountRepositoryRuntime.organizationSettings.saveSection(organization.id, section, fields, revision), {
        successMessage: section === 'identity' ? 'Organization details saved' : 'Document defaults saved',
      }),
  } : null;
  const saveProfile = async (nextProfile) => {
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.updateMyProfile(organization.id, nextProfile),
        { successMessage: "Personal profile saved" },
      );
      return result.ok;
    }
    setProfile(nextProfile);
    setTeamMembers((current) => current.map((member) => member.id === nextProfile.id ? { ...member, name: nextProfile.name, email: nextProfile.email, initials: nextProfile.initials } : member));
    setActivityLog((current) => [createActivity({ action: "Personal profile updated", detail: `Saved preferences for ${nextProfile.name}`, actorProfile: nextProfile, entityType: "profile", entityId: nextProfile.id }), ...current]);
    return true;
  };
  const setPublicMode = (enabled) => {
    setProfile((current) => ({ ...current, publicMode: enabled }));
    setEditorOpen(false);
    setCreateOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setActivityLog((current) => [createActivity({ action: enabled ? "Public mode enabled" : "Public mode disabled", detail: enabled ? "Sensitive client and financial values were masked on screen" : "Original workspace values are visible again", entityType: "profile", entityId: profile.id }), ...current]);
    setToast(enabled ? "Public mode enabled. Sensitive values are hidden." : "Public mode disabled. Original values are visible.");
  };
  const loadBusinessEditor = async (id) => {
    const { loadBusinessEditingSnapshot } = await import(/* webpackChunkName: "business-editing-snapshot" */ "./repositories/businessEditingSnapshot");
    return loadBusinessEditingSnapshot(accountRepositoryRuntime.reads.administration, organization.id, id);
  };
  const openIssuerEditor = async (business = null, context = "issuing") => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return;
    }
    setOrganizationOpen(false);
    if (serverAccount && business?.id) {
      const pending = { business, context, loading: true };
      setIssuerEditor(pending);
      try {
        const complete = await loadBusinessEditor(business.id);
        setIssuerEditor(current => current === pending ? { business: complete, context } : current);
      } catch (error) {
        setIssuerEditor(current => current === pending ? null : current);
        setToast(error.message || "Business details could not be loaded.");
      }
      return;
    }
    setIssuerEditor({ business, context });
  };
  const openClientEditor = async (client = null, mode = "profile") => {
    if (client && !canAccessClient(profile, client)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (client?.id) setFocusedClientId(client.id);
    if (serverAccount && client?.id) {
      const pending = { client, mode, loading: true };
      setClientEditor(pending);
      try {
        const { loadClientEditingSnapshot } = await import(/* webpackChunkName: "client-editing-snapshot" */ "./repositories/clientEditingSnapshot");
        const complete = await loadClientEditingSnapshot(accountRepositoryRuntime.reads.clients, organization.id, client.id);
        setClientEditor(current => current === pending ? { client: complete, mode } : current);
      } catch (error) {
        setClientEditor(current => current === pending ? null : current);
        setToast(error.message || "Client details could not be loaded.");
      }
      return;
    }
    const defaultIssuerId = effectiveBusinessScopeId !== ALL_BUSINESSES_SCOPE
      ? effectiveBusinessScopeId
      : organizationBusinesses[0]?.id || "";
    const preparedClient = client?.id || client?.sharedAcrossIssuers || client?.issuerId
      ? client
      : { ...(client || {}), issuerId: defaultIssuerId, sharedAcrossIssuers: false };
    setClientEditor({ client: preparedClient, mode });
  };
  const saveBusiness = async (business, section = "record") => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return false;
    }
    const { logoFile, ...businessRecord } = business;
    const duplicate = organizationBusinesses.find((item) => item.id !== businessRecord.id && item.name.trim().toLowerCase() === businessRecord.name.trim().toLowerCase());
    if (duplicate) {
      setToast("An issuing business with this name already exists");
      return false;
    }
    const editing = Boolean(businessRecord.id);
    if (serverAccount) {
      if (section === "engagement") return runAccountCommand(
        () => accountRepositoryRuntime.commands.saveBusinessEngagement(businessRecord, organization.id),
        { successMessage: `${businessRecord.name} engagement updated` },
      );
      if (logoFile) {
        setToast("Use Manage logo to verify and apply the business image.");
        return false;
      }
      if (String(businessRecord.logo || "").startsWith("data:") && businessRecord.logo !== organizationBusinesses.find((item) => item.id === businessRecord.id)?.logo) {
        setToast("Use Manage logo to verify the business image.");
        return false;
      }
      return runAccountCommand(
        () => accountRepositoryRuntime.commands.saveBusinessRecord(businessRecord, organization.id),
        { successMessage: editing ? `${businessRecord.name} updated` : `${businessRecord.name} added as an issuer` },
      );
    }
    const baseId = businessRecord.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "business";
    const id = businessRecord.id || (businesses.some((item) => item.id === baseId) ? `${baseId}-${Date.now().toString(36)}` : baseId);
    const prepared = { ...businessRecord, id, workspaceId: organization.id, legalName: businessRecord.legalName || businessRecord.name, logo: businessRecord.logo || "" };
    setBusinesses((current) => editing ? current.map((item) => item.id === id ? prepared : item) : [...current, prepared]);
    recordActivity({ action: editing ? "Issuing business updated" : "Issuing business added", detail: `${editing ? "Updated" : "Added"} ${businessRecord.name}`, entityType: "business", entityId: id });
    setToast(editing ? `${businessRecord.name} updated` : `${businessRecord.name} added as an issuer`);
    return true;
  };
  const removeBusiness = async (businessId) => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return false;
    }
    const business = (serverAccount ? [...accountAdminBusinesses, ...organizationBusinesses] : organizationBusinesses).find((item) => item.id === businessId);
    if (!business) return false;
    if (serverAccount && !Number.isInteger(business.revision)) {
      setToast("Open this business in Administration before archiving it, so its current version can be reviewed.");
      return false;
    }
    const linkedInvoices = invoiceRecords.some((invoice) => invoice.issuerId === businessId);
    const linkedClients = clientRecords.some((client) => client.issuerId === businessId || client.taxProfilesByIssuer?.[businessId]);
    if (linkedInvoices || linkedClients) {
      setToast(`${business.name} has billing records and cannot be removed. Archive or reassign them first.`);
      return false;
    }
    if (organizationBusinesses.length === 1) {
      setToast("Keep at least one issuing business in the organization");
      return false;
    }
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.archiveBusiness(organization.id, businessId, true, business.revision),
        { successMessage: `${business.name} archived` },
      );
      return result.ok;
    }
    setBusinesses((current) => current.filter((item) => item.id !== businessId));
    setTeamMembers((current) => current.map((member) => member.businessIds?.includes("*") ? member : { ...member, businessIds: (member.businessIds || []).filter((id) => id !== businessId) }));
    recordActivity({ action: "Issuing business removed", detail: `Removed ${business.name}`, entityType: "business", entityId: businessId });
    setToast(`${business.name} removed`);
    return true;
  };
  const saveTeamMember = async (member) => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return false;
    }
    const memberDirectory = serverAccount ? accountAdminMembers : teamMembers;
    const duplicate = memberDirectory.find((item) => item.id !== member.id && item.email.trim().toLowerCase() === member.email.trim().toLowerCase());
    if (duplicate) {
      setToast("A teammate with this email already exists");
      return false;
    }
    if (member.id === profile.id && member.status === "Suspended") {
      setToast("You cannot suspend your own active session");
      return false;
    }
    const editing = Boolean(member.id);
    if (serverAccount) {
      setToast('Use the reviewed invitation, profile, role, or business access editor.');
      return false;
    }
    const baseId = member.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "teammate";
    const id = member.id || `${baseId}-${Date.now().toString(36)}`;
    const prepared = { ...member, id, workspaceId: organization.id };
    setTeamMembers((current) => editing ? current.map((item) => item.id === id ? prepared : item) : [...current, prepared]);
    if (id === profile.id) setProfile((current) => ({ ...current, ...prepared }));
    recordActivity({ action: editing ? "Team access updated" : "Teammate invited", detail: `${editing ? "Updated" : "Invited"} ${member.name} as ${member.role}`, entityType: "team-member", entityId: id });
    setToast(editing ? `${member.name}'s access updated` : `Invitation prepared for ${member.name}`);
    return true;
  };
  const removeTeamMember = async (memberId) => {
    if (!canManage) {
      setToast(accessDeniedMessage);
      return false;
    }
    const member = (serverAccount ? accountAdminMembers : teamMembers).find((item) => item.id === memberId);
    if (!member || member.role === "Owner") return false;
    if (serverAccount) {
      setToast(member.invitationId ? 'Open the invitation to review cancellation.' : 'Open Role and status to review suspending this member.');
      return false;
    }
    setTeamMembers((current) => current.filter((item) => item.id !== memberId));
    recordActivity({ action: "Workspace access removed", detail: `Removed ${member.name} from the organization`, entityType: "team-member", entityId: memberId });
    setToast(`${member.name}'s access removed`);
    return true;
  };
  const saveClient = async (client) => {
    if (client.issuerId && !canAccessBusiness(profile, client.issuerId)) {
      setToast(accessDeniedMessage);
      return false;
    }
    const sharedAcrossIssuers = Boolean(client.sharedAcrossIssuers);
    const preparedScope = {
      ...client,
      issuerId: sharedAcrossIssuers ? "" : client.issuerId || organizationBusinesses[0]?.id || "",
      sharedAcrossIssuers,
    };
    const conflict = findClientConflict(organizationClients, preparedScope);
    if (conflict) {
      setToast(preparedScope.issuerId ? "This client already exists for that issuer" : "A shared client with this identity would overlap an existing record");
      return false;
    }
    const editing = Boolean(client.id);
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.saveClientRecord(preparedScope, organization.id),
        { successMessage: editing ? `${client.name} updated` : `${client.name} added` },
      );
      return result;
    }
    const baseId = client.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "client";
    const id = client.id || `${organization.id}-${baseId}-${Date.now().toString(36)}`;
    const initials = client.name.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
    const prepared = { ...preparedScope, id, workspaceId: organization.id, initials, outstanding: client.outstanding || `${client.currency} 0`, lifetime: client.lifetime || `${client.currency} 0` };
    setClientRecords((current) => editing ? current.map((item) => item.id === id ? prepared : item) : [...current, prepared]);
    recordActivity({ action: editing ? "Client updated" : "Client added", detail: `${editing ? "Updated" : "Added"} ${client.name} as ${client.issuerId ? "an issuer-specific" : "a shared"} client`, entityType: "client", entityId: id });
    setToast(editing ? `${client.name} updated` : `${client.name} added`);
    return true;
  };
  const saveServiceRecord = async (service) => {
    if (publicMode) {
      setToast("Turn off public mode before changing services");
      return false;
    }
    if (service.issuerId && !canAccessBusiness(profile, service.issuerId)) {
      setToast(accessDeniedMessage);
      return false;
    }
    const editing = Boolean(service.id);
    const duplicate = organizationServices.find((item) => !item.archived && item.id !== service.id && item.name.trim().toLowerCase() === service.name.trim().toLowerCase() && (item.issuerId || "") === (service.issuerId || ""));
    if (duplicate) {
      setToast("A service with this name already exists in that scope");
      return false;
    }
    const prepared = {
      ...service,
      workspaceId: organization.id,
      issuerId: service.issuerId || "",
      rate: String(service.rate).trim(),
      currency: service.currency || "EUR",
      unit: service.unit || "fixed",
      archived: false,
    };
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.saveService(prepared, organization.id),
        { successMessage: editing ? `${service.name} updated` : `${service.name} added` },
      );
      return result;
    }
    const baseId = service.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "service";
    const id = service.id || `${organization.id}-${baseId}-${Date.now().toString(36)}`;
    const saved = { ...prepared, id, used: service.used || 0 };
    setServiceRecords((current) => editing ? current.map((item) => item.id === id ? saved : item) : [saved, ...current]);
    recordActivity({ action: editing ? "Service updated" : "Service added", detail: `${editing ? "Updated" : "Added"} ${saved.name}`, entityType: "service", entityId: id, businessId: saved.issuerId });
    setToast(editing ? `${saved.name} updated` : `${saved.name} added`);
    return true;
  };
  const archiveServiceRecord = async (service) => {
    if (publicMode) {
      setToast("Turn off public mode before changing services");
      return false;
    }
    const archived = !service.archived;
    if (serverAccount) {
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.archiveService(service.id, archived, organization.id, service.revision),
        { successMessage: `${service.name} ${archived ? "archived" : "restored"}` },
      );
      if (!result.ok && result.code === "resource_revision_conflict") void servicePage.refresh();
      return result.ok;
    }
    setServiceRecords((current) => current.map((item) => item.id === service.id ? { ...item, archived } : item));
    recordActivity({ action: archived ? "Service archived" : "Service restored", detail: service.name, entityType: "service", entityId: service.id, businessId: service.issuerId });
    setToast(`${service.name} ${archived ? "archived" : "restored"}`);
    return true;
  };
  const loadAccountService = useCallback(async ({ serviceId }) => {
    const scope = accountScopeRef.current;
    const result = await accountRepositoryRuntime.reads.services.detail(organization.id, serviceId);
    if (scope !== accountScopeRef.current) return { ok: false, error: "The active account changed." };
    if (!result?.ok) return result;
    const adapters = accountPageAdapters || await import(/* webpackChunkName: "account-page-adapters" */ "./repositories/accountPageAdapters");
    if (scope !== accountScopeRef.current) return { ok: false, error: "The active account changed." };
    return { ok: true, data: adapters.mapAccountService({ ...result.data.service, capabilities: result.data.capabilities }) };
  }, [accountPageAdapters, organization?.id]);
  const openServiceEditor = async (service = {}, duplicate = false, allowReadOnly = false) => {
    let current = service;
    if (serverAccount && service.id) {
      const result = await loadAccountService({ serviceId: service.id });
      if (!result?.ok) { setToast(result?.error || "The service could not be loaded."); return; }
      current = result.data;
      if (!duplicate && !allowReadOnly && !current.capabilities?.canEdit) { setToast("This service is read-only. Restore it first or ask an administrator to update it."); return; }
    }
    if (duplicate) current = { ...current, id: "", revision: undefined, archived: false, name: `${current.name} copy`.slice(0, 160), used: 0, capabilities: undefined };
    if (!current.id && !canManage) current = { ...current, issuerId: accountBusinessId || dailyBusinesses[0]?.id || "" };
    setServiceEditor(current);
  };
  const openEditor = async (invoiceOrDraft, options = {}) => {
    const openingScope = accountScopeRef.current;
    if (invoiceOrDraft?.issuerId && !canAccessBusiness(profile, invoiceOrDraft.issuerId)) {
      setToast(accessDeniedMessage);
      return;
    }
    const resolvedInvoice = invoiceOrDraft?.id && serverAccount
      ? await loadAccountInvoiceDetail(invoiceOrDraft.id, invoiceOrDraft, true)
      : invoiceOrDraft;
    if (serverAccount && (openingScope !== accountScopeRef.current || (invoiceOrDraft?.id && !resolvedInvoice))) {
      if (openingScope === accountScopeRef.current) setToast("This invoice could not be loaded. Refresh and try again.");
      return;
    }
    const emailRequested = options.intent === "email" && resolvedInvoice?.id && !publicMode && !resolvedInvoice.archived && resolvedInvoice.status?.toLowerCase() !== "void";
    setInvoiceEmailIntent(emailRequested ? { invoiceId: resolvedInvoice.id, issuerId: resolvedInvoice.issuerId, workspaceId: organization.id } : null);
    setEditorReturnContext({ activeView, workspaceMode, adminSection });
    setCreateOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setWorkspaceMode("regular");
    setActiveView("invoices");
    if (resolvedInvoice?.id) {
      setSelectedInvoice(resolvedInvoice);
      setEditorDraft(null);
      setEditorMode(publicMode || emailRequested ? "view" : resolvedInvoice.archived ? "view" : resolvedInvoice.status === "Draft" ? "edit" : "view");
    } else if (resolvedInvoice?.client) {
      const issuerId = resolvedInvoice.issuerId || organizationBusinesses[0]?.id;
      const client = resolvedInvoice.clientRecord?.id === resolvedInvoice.clientId
        ? resolvedInvoice.clientRecord
        : resolveClient(accountClients, { clientId: resolvedInvoice.clientId, clientName: resolvedInvoice.client, issuerId });
      const issuedDate = isoDate(new Date());
      const number = resolvedInvoice.invoiceNumber || getNextInvoiceNumber(issuerId, Number(issuedDate.slice(0, 4)));
      const paymentTerms = resolvedInvoice.paymentTerms || client?.terms || "Net 14";
      const dueDate = addDays(issuedDate, paymentTermDays(paymentTerms));
      const clientTaxProfile = getClientTaxProfile(client, issuerId);
      const taxLines = cloneTaxLines(resolvedInvoice.taxLines ?? (clientTaxProfile.useAsDefault ? clientTaxProfile.lines : []));
      const taxNote = resolvedInvoice.taxNote ?? (clientTaxProfile.useAsDefault ? clientTaxProfile.invoiceNote : "");
      const taxReviewed = resolvedInvoice.taxReviewed ?? Boolean(clientTaxProfile.confirmed && clientTaxProfile.useAsDefault);
      const items = resolvedInvoice.items || [{ id: `line-${Date.now()}`, description: resolvedInvoice.description, quantity: resolvedInvoice.quantity, rate: resolvedInvoice.rate }];
      const amount = calculateInvoiceTotals(items, { taxLines }).total;
      const taxSource = resolvedInvoice.taxSource || (clientTaxProfile.useAsDefault ? "client-default" : "invoice");
      setSelectedInvoice({ id: `draft-${issuerId}-${number.replace("/", "-")}`, invoiceNumber: number, workspaceId: organization.id, issuerId, clientId: client?.id || resolvedInvoice.clientId, client: client?.name || resolvedInvoice.client, work: resolvedInvoice.description, issued: shortDateLabel(issuedDate), issuedDate, due: shortDateLabel(dueDate), dueDate, amount, currency: resolvedInvoice.currency || client?.currency || "EUR", status: "Draft", paymentTerms, notes: resolvedInvoice.notes ?? client?.invoiceNoteDefault, footerNotes: resolvedInvoice.footerNotes ?? client?.footerNotesDefault, items, taxLines, taxNote, taxReviewed, taxSource });
      setEditorDraft({ ...resolvedInvoice, items, taxLines, taxNote, taxReviewed, taxSource });
      setEditorMode("edit");
    } else {
      setSelectedInvoice(invoices.find((invoice) => invoice.status === "Draft") || invoices[0]);
      setEditorDraft(null);
      setEditorMode("edit");
    }
    setEditorOpen(true);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const closeEditor = () => {
    setInvoiceEmailIntent(null);
    setEditorOpen(false);
    if (serverAccount && (!editorReturnContext?.activeView || editorReturnContext.activeView === activeView)) {
      void invoicePage.refresh();
    }
    if (editorReturnContext) {
      setWorkspaceMode(editorReturnContext.workspaceMode || "regular");
      setAdminSection(editorReturnContext.adminSection || "overview");
      setActiveView(editorReturnContext.activeView || "invoices");
    }
    setEditorReturnContext(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const startCreate = (request = {}) => {
    const requestedSeed = request?.clientId || request?.issuerId || request?.serviceId ? request : {};
    const seed = requestedSeed.issuerId || effectiveBusinessScopeId === ALL_BUSINESSES_SCOPE
      ? requestedSeed
      : { ...requestedSeed, issuerId: effectiveBusinessScopeId };
    setSearchOpen(false);
    setNotificationsOpen(false);
    setUserOpen(false);
    setWorkspaceMode("regular");
    if (!organizationBusinesses.length) {
      if (canManage) {
        openIssuerEditor();
        setToast("Add an issuing business before creating an invoice");
      } else {
        setToast("Ask an administrator to assign you an issuing business before creating an invoice");
      }
      return;
    }
    setCreateSeed(seed);
    setCreateOpen(true);
  };
  const addClientFromInvoice = (issuerId) => {
    setCreateOpen(false);
    setCreateSeed(current => ({ ...current, issuerId }));
    setClientEditor({ client: { issuerId }, mode: "profile", returnToCreate: true });
  };
  const resolveInvoiceAction = async (invoiceId, payment = false) => {
    if (!serverAccount) return { ok: true, record: allInvoices.find((item) => item.id === invoiceId) };
    const actions = await import(/* webpackChunkName: "account-invoice-actions" */ "./repositories/accountInvoiceActions");
    return actions.loadAccountInvoiceForAction({
      invoiceId, organizationId: organization.id, profile, payment,
      readDetail: accountRepositoryRuntime.reads.invoices.detail,
      mapDetail: accountPageAdapters?.mapAccountInvoiceDetail,
      isCurrent: () => accountScopeRef.current === accountScope,
    });
  };
  const updateInvoiceStatus = async (invoiceId, status, reason = null, reviewedInvoice = null, onRejected = null) => {
    const rejectStatus = message => { if (onRejected) onRejected(message); else setToast(message); return false; };
    const resolved = await resolveInvoiceAction(invoiceId, status === "Paid");
    if (!resolved.ok) return rejectStatus(resolved.error);
    const record = resolved.record;
    if (serverAccount && status !== 'Paid' && reviewedInvoice && (reviewedInvoice.revision !== record.revision || reviewedInvoice.accountStatus !== record.accountStatus || reviewedInvoice.updatedAt !== record.updatedAt)) {
      return rejectStatus('This invoice changed. Reload it and review the latest status before confirming.');
    }
    const result = (await loadInvoiceMutations()).changeStatus({ invoice: record, status, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      return rejectStatus(result.error);
    }
    if (result.noop) return true;
    if (serverAccount) {
      if (["Partially paid", "Overdue"].includes(status)) {
        setToast(status === "Overdue"
          ? "Overdue is calculated from the due date after an invoice is sent."
          : "Record a payment amount to create a partially paid balance.");
        return false;
      }
      if (status === "Paid" && resolved.balance === "0.0000") { setToast("This invoice has no outstanding balance."); return false; }
      const command = status === "Paid"
        ? () => accountRepositoryRuntime.commands.recordPayment(invoiceId, {
            amount: resolved.balance,
            paidOn: isoDate(new Date()),
            reference: "Marked paid in Invoice Studio",
          })
        : () => accountRepositoryRuntime.commands.changeInvoiceStatus(invoiceId, status, reason, reviewedInvoice || record);
      const accountResult = await runAccountCommand(command, {
        successMessage: status === "Paid" ? `${invoiceNumber(record)} payment recorded` : result.toast,
        onError: onRejected,
        onSuccess: async () => {
          const updated = await loadAccountInvoiceDetail(invoiceId, record);
          if (updated) setSelectedInvoice((current) => current?.id === invoiceId ? updated : current);
        },
      });
      if (accountResult.ok) {
        setResolvedNotifications((current) => status === "Paid" ? (current.includes(invoiceId) ? current : [...current, invoiceId]) : current.filter((item) => item !== invoiceId));
      }
      return accountResult.ok;
    }
    setResolvedNotifications((current) => status === "Paid" ? (current.includes(invoiceId) ? current : [...current, invoiceId]) : current.filter((item) => item !== invoiceId));
    setInvoiceStatuses((current) => ({ ...current, [invoiceId]: status }));
    if (isImportedPaymentPending(record)) {
      setInvoiceRecords((current) => current.map((invoice) => invoice.id === invoiceId ? {
        ...invoice,
        importedPaymentStatus: status === "Paid" ? IMPORTED_PAYMENT_REVIEW.paid : IMPORTED_PAYMENT_REVIEW.open,
        paymentStatusSource: "manual-import-review",
        paymentReviewedAt: new Date().toISOString(),
      } : invoice));
    }
    setSelectedInvoice((current) => current?.id === invoiceId ? { ...current, status } : current);
    setToast(result.toast);
    recordActivity({ ...result.audit, detail: reason?.trim() ? `${result.audit.detail}. Reason: ${reason.trim()}` : result.audit.detail });
    return true;
  };
  const reviewImportedPayment = (invoiceIds, decision) => {
    if (publicMode) {
      setToast("Turn off public mode to review imported payments");
      return false;
    }
    const requestedIds = new Set(Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds]);
    const reviewIds = allInvoices.filter((invoice) => requestedIds.has(invoice.id) && isImportedPaymentPending(invoice)).map((invoice) => invoice.id);
    if (!reviewIds.length) return false;
    const reviewedIds = new Set(reviewIds);
    const invoiceStatus = decision === "paid" ? "Paid" : "Sent";
    const importedPaymentStatus = decision === "paid" ? IMPORTED_PAYMENT_REVIEW.paid : IMPORTED_PAYMENT_REVIEW.open;
    const reviewedAt = new Date().toISOString();
    setInvoiceRecords((current) => current.map((invoice) => reviewedIds.has(invoice.id) ? {
      ...invoice,
      importedPaymentStatus,
      paymentStatusSource: "manual-import-review",
      paymentReviewedAt: reviewedAt,
    } : invoice));
    setInvoiceStatuses((current) => {
      const next = { ...current };
      reviewIds.forEach((invoiceId) => { next[invoiceId] = invoiceStatus; });
      return next;
    });
    setSelectedInvoice((current) => current && reviewedIds.has(current.id) ? { ...current, status: invoiceStatus, importedPaymentStatus } : current);
    recordActivity({
      action: decision === "paid" ? "Imported payment confirmed" : "Imported invoice marked unpaid",
      detail: `${reviewIds.length} imported invoice${reviewIds.length === 1 ? "" : "s"} reviewed`,
      entityType: reviewIds.length === 1 ? "invoice" : "data",
      entityId: reviewIds.length === 1 ? reviewIds[0] : organization.id,
    });
    setToast(decision === "paid"
      ? `${reviewIds.length} imported invoice${reviewIds.length === 1 ? "" : "s"} confirmed paid`
      : `${reviewIds.length} imported invoice${reviewIds.length === 1 ? "" : "s"} moved to sent`);
    return true;
  };
  const editInvoice = (invoice) => {
    setInvoiceEmailIntent(null);
    if (!canAccessInvoice(profile, invoice)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode to edit invoices");
      return;
    }
    setSelectedInvoice(invoice);
    setEditorDraft(null);
    setEditorMode("edit");
    setEditorOpen(true);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const saveInvoice = async (invoice, editorSignal) => {
    const result = (await loadInvoiceMutations()).save({ invoice, records: invoiceRecords, organizationId: organization.id, profile, publicMode });
    const isCurrent = () => accountScopeRef.current === accountScope && !editorSignal?.aborted;
    if (!isCurrent()) return { ok: false, code: "account_changed", error: "The invoice editor is no longer active." };
    if (!result.ok) {
      setToast(result.error);
      return result;
    }
    if (serverAccount) {
      const { saveAccountInvoiceForReview } = await import(/* webpackChunkName: "account-invoice-review" */ "./repositories/accountInvoiceReview");
      const accountResult = await saveAccountInvoiceForReview({
        invoice, organizationId: organization.id,
        save: accountRepositoryRuntime.commands.saveInvoice,
        runCommand: command => runAccountCommand(command, { successMessage: result.toast }),
        loadDetail: id => loadAccountInvoiceDetail(id, {}, true),
        isCurrent,
      });
      if (!isCurrent()) return accountResult;
      if (!accountResult.ok) { setToast(accountResult.error); return accountResult; }
      setSelectedInvoice(accountResult.record);
      setEditorDraft(null);
      setEditorMode("view");
      return true;
    }
    setInvoiceRecords(result.records);
    setSelectedInvoice(invoice);
    setEditorDraft(null);
    setEditorMode("view");
    setToast(result.toast);
    recordActivity(result.audit);
    return true;
  };
  const reloadInvoiceEditor = async (invoiceId, savedScope = null, editorSignal) => {
    let latest;
    try { latest = await loadAccountInvoiceDetail(invoiceId, {}, true); } catch { latest = null; }
    if (editorSignal?.aborted || accountScopeRef.current !== accountScope) return false;
    if (!latest?.id || (savedScope && (latest.issuerId !== savedScope.businessId || latest.revision < savedScope.expectedRevision))) {
      setToast("The latest invoice could not be loaded. Your edits are still open."); return false;
    }
    setSelectedInvoice(latest);
    setEditorDraft(null);
    setEditorMode("view");
    return true;
  };
  const reuseInvoice = async (source) => {
    if (!canAccessInvoice(profile, source)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (serverAccount) {
      const resolved = await resolveInvoiceAction(source.id);
      if (!resolved.ok) { setToast(resolved.error); return false; }
      const issuedDate = isoDate(new Date());
      const dueDate = addDays(issuedDate, paymentTermDays(resolved.record.paymentTerms));
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.reuseInvoice(source.id, issuedDate, dueDate, resolved.record.work),
        {
          successMessage: `${invoiceNumber(resolved.record)} reused as a fresh draft`,
          onSuccess: async ({ data }) => {
            const persisted = await loadAccountInvoiceDetail(data?.id, { client: source.client });
            setCreateOpen(false);
            setSearchOpen(false);
            setNotificationsOpen(false);
            setWorkspaceMode("regular");
            setActiveView("invoices");
            setSelectedInvoice(persisted?.id ? persisted : null);
            setEditorDraft(null);
            setEditorMode("edit");
            setEditorOpen(Boolean(persisted?.id));
            window.scrollTo({ top: 0, behavior: "auto" });
          },
        },
      );
      return accountResult.ok;
    }
    const client = resolveClient(organizationClients, { clientId: source.clientId, clientName: source.client, issuerId: source.issuerId });
    if (!client) {
      setToast("Assign this invoice's client to the issuer before reusing it");
      return;
    }
    const issuedDate = isoDate(new Date());
    const paymentTerms = source.paymentTerms || client.terms || "Net 14";
    const dueDate = addDays(issuedDate, paymentTermDays(paymentTerms));
    const number = getNextInvoiceNumber(source.issuerId, Number(issuedDate.slice(0, 4)));
    const taxLines = cloneTaxLines(source.taxLines || []);
    const sourceLineTotal = deriveLineTotalFromInvoiceTotal(parseCurrency(source.amount), { taxLines, adjustments: source.adjustments, currency: source.currency });
    const items = source.items?.length ? source.items.map((item, index) => ({ ...item, id: `reuse-line-${index + 1}-${Date.now()}` })) : [{ id: `reuse-line-1-${Date.now()}`, description: source.work || "Professional services", quantity: "1", rate: sourceLineTotal.toFixed(2) }];
    const draft = { ...source, id: `draft-reuse-${source.issuerId}-${number.replace("/", "-")}-${Date.now().toString(36)}`, invoiceNumber: number, clientId: client.id, client: client.name, issuedDate, issued: shortDateLabel(issuedDate), dueDate, due: shortDateLabel(dueDate), status: "Draft", archived: false, paymentTerms, items, taxLines, taxNote: source.taxNote || "", taxReviewed: Boolean(source.taxReviewed), taxSource: "reused-invoice", reusedFrom: source.id };
    setCreateOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setWorkspaceMode("regular");
    setActiveView("invoices");
    setSelectedInvoice(draft);
    setEditorDraft({ issuerId: source.issuerId, invoiceNumber: number, clientId: client.id, client: client.name, description: items[0].description, quantity: items[0].quantity, rate: items[0].rate, items, taxLines, taxNote: source.taxNote || "", taxReviewed: Boolean(source.taxReviewed), taxSource: "reused-invoice", reusedFrom: source.id });
    setEditorMode("edit");
    setEditorOpen(true);
    setToast(publicMode ? "Reusable draft created with refreshed dates and numbering" : `${invoiceNumber(source)} reused as draft ${number}`);
    window.scrollTo({ top: 0, behavior: "auto" });
    return true;
  };
  const changeInvoiceArchived = async (invoice, archived) => {
    const resolved = await resolveInvoiceAction(invoice);
    if (!resolved.ok) { setToast(resolved.error); return false; }
    const record = resolved.record;
    const currentArchivedIds = serverAccount ? (record.archived ? [record.id] : []) : archivedInvoiceIds;
    const result = (await loadInvoiceMutations()).changeArchive({ invoice: record, archived, archivedInvoiceIds: currentArchivedIds, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.archiveInvoice(invoice, archived),
        { successMessage: result.toast },
      );
      if (accountResult.ok && archived) {
        setEditorOpen(false);
        setActiveView("invoices");
      }
      return accountResult.ok;
    }
    setArchivedInvoiceIds(result.archivedInvoiceIds);
    if (archived) { setEditorOpen(false); setActiveView("invoices"); }
    setToast(result.toast);
    recordActivity(result.audit);
    return true;
  };
  const archiveInvoice = invoiceId => changeInvoiceArchived(invoiceId, true);
  const restoreInvoice = invoiceId => changeInvoiceArchived(invoiceId, false);

  const prepareInvoiceReminder = (invoice) => {
    if (!canAccessInvoice(profile, invoice)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode before preparing a private message");
      return;
    }
    const client = resolveClient(organizationClients, { clientId: invoice.clientId, clientName: invoice.client, issuerId: invoice.issuerId });
    const issuer = organizationBusinesses.find((business) => business.id === invoice.issuerId);
    if (!client) {
      setToast("Add or reconnect the invoice client before preparing a reminder");
      return;
    }
    setWhatsappRequest({ id: `receivable-${invoice.id}`, type: "receivable", audience: "client", contactPurpose: "receivables", templateId: invoice.status === "Overdue" ? "overdue-invoice" : "payment-reminder", record: client, invoice, issuerName: issuer?.name || organization.name });
  };
  const prepareDocumentRequest = (business, details = {}) => {
    if (!business || !canAccessBusiness(profile, business.id)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode before preparing a private message");
      return;
    }
    setWhatsappRequest({ id: `documents-${business.id}-${details.period || "current"}`, type: details.type || "missing-document", audience: "business", contactPurpose: details.contactPurpose || "document-request", templateId: details.templateId || "missing-document", record: business, businessName: business.name, period: details.period || "the current accounting period", documentName: details.documentName || "the outstanding invoices and supporting receipts", deadline: details.deadline || "the agreed deadline", subject: details.subject || "the outstanding accounting documents" });
  };
  const completeWhatsAppHandoff = async ({ request, contact, message, status, templateId }) => {
    const now = new Date().toISOString();
    const transaction = request.transactionId ? bankTransactions.find((item) => item.id === request.transactionId) : null;
    const entityType = request.transactionId ? "bank-transaction" : request.audience === "business" ? "business" : "invoice";
    const entityId = request.transactionId || request.invoice?.id || request.record?.id;
    const businessId = transaction?.businessId || request.invoice?.issuerId || (request.audience === "business" ? request.record?.id : request.record?.issuerId || "");
    const communicationId = request.communicationId || `communication-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`;
    const context = {
      type: request.type,
      audience: request.audience,
      contactPurpose: request.contactPurpose,
      transactionId: request.transactionId || "",
      invoiceId: request.invoice?.id || "",
      recordId: request.record?.id || "",
      issuerName: request.issuerName || "",
      businessName: request.businessName || "",
      period: request.period || "",
      documentName: request.documentName || "",
      deadline: request.deadline || "",
      transactionDate: request.transactionDate || "",
      subject: request.subject || "",
    };
    const communication = {
      id: communicationId,
      workspaceId: organization.id,
      businessId,
      clientId: request.invoice?.clientId || (request.audience === "client" ? request.record?.id || "" : ""),
      entityType,
      entityId,
      channel: "WhatsApp",
      status,
      templateId,
      contactId: contact?.id || "",
      contactName: contact?.name || "",
      message,
      context,
      createdAt: request.communicationId ? communicationRecords.find((item) => item.id === request.communicationId)?.createdAt || now : now,
      updatedAt: now,
      followUpAt: status === "follow-up" ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : "",
    };
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.saveCommunication(communication, organization.id),
        { successMessage: status === "sent" ? "Message recorded as sent" : status === "skipped" ? "Message recorded as skipped" : "Follow-up added to Notifications" },
      );
      if (!accountResult.ok) return false;
      if (request.transactionId && status === "sent") {
        const refreshedTransaction = accountResult.workspace.bankTransactions.find((item) => item.id === request.transactionId) || transaction;
        const evidenceResult = await runAccountCommand(
          () => accountRepositoryRuntime.commands.updateBankEvidence(request.transactionId, {
            status: "Requested",
            ownerId: refreshedTransaction?.evidenceOwnerId,
            deadline: refreshedTransaction?.evidenceDeadline,
          }),
          { successMessage: "Message sent state and evidence request recorded" },
        );
        return evidenceResult.ok;
      }
      return true;
    }
    setCommunicationRecords((current) => current.some((item) => item.id === communicationId) ? current.map((item) => item.id === communicationId ? communication : item) : [communication, ...current]);
    const action = status === "sent" ? "WhatsApp message marked sent" : status === "skipped" ? "WhatsApp message skipped" : "WhatsApp follow-up scheduled";
    const detail = status === "sent" ? `Recorded a message to ${contact?.name || "the contact"}` : status === "skipped" ? `Recorded that the ${templateId || "message"} handoff was skipped` : `Kept the ${templateId || "message"} request open for follow-up`;
    recordActivity({ action, detail, entityType, entityId });
    if (request.transactionId && status === "sent") {
      setBankTransactions((current) => current.map((transaction) => transaction.id === request.transactionId ? { ...transaction, evidenceStatus: "Requested", evidenceRequestedAt: new Date().toISOString(), evidenceContactId: contact?.id || "" } : transaction));
    }
    setToast(status === "sent" ? "Message recorded as sent" : status === "skipped" ? "Message recorded as skipped" : "Follow-up added to Notifications");
    return true;
  };
  const completeCommunicationFollowup = communication => runAccountCommand(
    () => accountRepositoryRuntime.commands.completeCommunication(communication, organization.id),
    { successMessage: 'Communication follow-up completed' },
  );
  const openCommunicationFollowUp = (communication) => {
    if (publicMode) {
      setToast("Turn off public mode before opening a private follow-up");
      return;
    }
    const context = communication.context || {};
    const invoice = context.invoiceId ? allInvoices.find((item) => item.id === context.invoiceId) : null;
    const transaction = context.transactionId ? bankTransactions.find((item) => item.id === context.transactionId) : null;
    const record = invoice
      ? resolveClient(organizationClients, { clientId: invoice.clientId, clientName: invoice.client, issuerId: invoice.issuerId })
      : organizationBusinesses.find((item) => item.id === (context.recordId || transaction?.businessId || communication.businessId));
    if (!record) {
      setToast("The contact record for this follow-up is no longer available");
      return;
    }
    setWhatsappRequest({
      ...context,
      id: `follow-up-${communication.id}-${Date.now()}`,
      communicationId: communication.id,
      record,
      invoice,
      templateId: communication.templateId,
      preferredContactId: communication.contactId,
      initialMessage: communication.message,
    });
  };

  const switchPortalBusiness = async (businessId) => {
    if (!portalUser || !serverAccount || !organization?.id || !businessId) {
      throw new Error("Business switching requires an active portal account.");
    }
    const data = await accountRepositoryRuntime.businessPortal.readBusiness(authSession, {
      organizationId: organization.id,
      businessId,
    });
    applyData(data);
  };
  const recordPortalUploads = (submissions, businessId) => {
    if (!portalUser || !profile.businessIds.includes(businessId)) return [];
    const completed = submissions.map((item) => ({
      id: item.submissionId, workspaceId: organization.id, businessId,
      documentRequestId: item.documentRequestId, filename: item.filename,
      contentType: item.contentType, byteSize: item.byteSize, documentType: "Document", period: "",
      status: item.status.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
      receivedAt: item.receivedAt, createdAt: item.receivedAt, updatedAt: item.updatedAt,
    }));
    if (completed.length) {
      setIntakeItems((current) => [...completed, ...current.filter((item) => !completed.some((entry) => entry.id === item.id))]);
      setPortalDocumentRequests((current) => current.map((item) => item.businessId === businessId
        && ["missing", "requested"].includes(String(item.status).toLowerCase())
        && completed.some((entry) => entry.documentRequestId === item.id) ? { ...item, status: "Received" } : item));
    }
    return completed.map((item) => item.id);
  };
  const uploadPortalDocuments = async (files, businessId) => {
    if (!portalUser || !profile.businessIds.includes(businessId)) {
      setToast(accessDeniedMessage);
      return [];
    }
    if (serverAccount) {
      const result = await accountRepositoryRuntime.businessPortal.uploadDocuments(files, businessId);
      const completed = recordPortalUploads(result.results.filter((item) => item?.ok && item.submission).map((item) => item.submission), businessId);
      if (result.failures.length) {
        const firstError = result.failures[0]?.error || "One document could not be sent.";
        setToast(`${result.uploadedCount} of ${result.results.length} documents sent. ${firstError}`);
      } else {
        setToast(`${result.uploadedCount} document${result.uploadedCount === 1 ? "" : "s"} sent to your accountant`);
      }
      return completed;
    }
    try {
      const known = intakeItems.filter((item) => item.workspaceId === organization.id && item.businessId === businessId);
      const { prepareBusinessPortalDocuments } = await import(/* webpackChunkName: "business-portal-upload" */ "./businessPortalUpload");
      const created = await prepareBusinessPortalDocuments({
        files,
        workspaceId: organization.id,
        businessId,
        existingItems: known,
      });
      setIntakeItems((current) => [...created, ...current]);
      setToast(`${created.length} document${created.length === 1 ? "" : "s"} sent to your accountant`);
      return created.map((item) => item.id);
    } catch (error) {
      if (!error.portalUploadError) console.error(error);
      setToast(error.portalUploadError ? error.message : "The selected documents could not be prepared for upload.");
      return [];
    }
  };
  const markPortalNotificationRead = async (notificationId) => {
    if (!portalUser || !serverAccount || !organization?.id || !portalBusiness?.id || !notificationId) {
      throw new Error("Notification updates require an active portal account.");
    }
    const result = await accountRepositoryRuntime.portalCommands.markNotificationRead({
      notificationId,
      organizationId: organization.id,
      businessId: portalBusiness.id,
    });
    if (!result?.ok) throw new Error(result?.error || "The notification could not be updated.");
    const readAt = result.data?.readAt || new Date().toISOString();
    setPortalNotifications((current) => current.map((item) => item.id === notificationId ? { ...item, readAt } : item));
    return result.data;
  };
  const uploadIntakeFiles = async (files, businessId, intent = {}) => {
    const business = businesses.find((item) => item.id === businessId);
    if (!business || !canAccessBusiness(profile, businessId) || !canUseAccountingOperation(profile, "intake")) {
      setToast(accessDeniedMessage);
      return [];
    }
    if (publicMode) {
      setToast("Document intake stays read-only in public mode");
      return [];
    }
    if (serverAccount) {
      if (!accountRepositoryRuntime.sourceUploads) {
        setToast("Private source storage is not available in this account environment.");
        return [];
      }
      const result = await accountRepositoryRuntime.sourceUploads.uploadBatch(files, businessId, { jobType: intent.statement ? "parse_bank_statement" : "extract" });
      if (result.results.some((item) => item.committed) && intakePage.loaded) await intakePage.reload();
      if (result.failures.length) {
        const firstError = result.failures[0].error || "One source file could not be uploaded.";
        setToast(`${result.uploadedCount} of ${result.results.length} files queued. ${firstError}`);
      } else {
        setToast(`${result.uploadedCount} private source file${result.uploadedCount === 1 ? "" : "s"} queued for review`);
      }
      return result.results.filter((item) => item.ok).map((item) => item.intakeId);
    }
    const expanded = await expandUploadFiles(files);
    const created = [];
    const localSources = new Map();
    const existingStatements = new Map();
    const known = intakeItems.filter((item) => item.workspaceId === organization.id && item.businessId === businessId);
    for (const entry of expanded) {
      const item = await createIntakeItem({ ...entry, workspaceId: organization.id, businessId, existingItems: known });
      if (item.status === 'Duplicate') {
        const { duplicateIntakeReview } = await import('./duplicateIntakeReview');
        const original = duplicateIntakeReview(item, known).original;
        // The hash and business are verified before repairing a missing local
        // attachment. Re-uploading never creates another accounting record.
        if (original) {
          localSources.set(original.id, entry.file);
          if (intent.statement && original.documentType === 'Bank statement') existingStatements.set(item.id, original.id);
        }
      }
      if (item.status !== "Duplicate") {
        const extension = entry.file.name.toLowerCase().split(".").pop();
        const couldContainBankStatement = ["pdf", "xlsx", "xls"].includes(extension);
        const shouldTryBankParser = couldContainBankStatement && bankingEnabled
          && (intent.statement || item.documentType === "Bank statement" || item.documentType === "Unknown document" || ["xlsx", "xls"].includes(extension));
        let parsed = false;
        if (shouldTryBankParser) {
          try {
            const { parseStatementFile } = await import(/* webpackChunkName: "bank-statement-parsers" */ "./banking/parsers");
            const extraction = await parseStatementFile(entry.file);
            item.documentType = "Bank statement";
            item.extraction = extraction;
            item.parserVersion = extraction.parserVersion;
            item.status = "Needs review";
            item.warning = [...(extraction.warnings || []), item.warning].filter(Boolean).join(" ");
            parsed = true;
          } catch (error) {
            item.bankParserError = error.message;
            if (item.documentType === "Bank statement") {
              item.parserError = error.message;
              item.status = /not supported|not recognized|no supported/i.test(error.message) ? "Unsupported" : "Failed";
              item.error = error.message;
            }
          }
        }
        const shouldTryInvoiceParser = !parsed && extension === "pdf"
          && (INVOICE_IMPORT_DOCUMENT_TYPES.includes(item.documentType) || item.documentType === "Unknown document");
        if (shouldTryInvoiceParser) {
          try {
            const { extractInvoiceDocument } = await import(/* webpackChunkName: "invoice-import" */ "./invoiceImport");
            const documentType = item.documentType.toLowerCase().replace(/\s+/g, "_");
            const invoice = await extractInvoiceDocument(entry.file, { documentType });
            const displayType = { purchase_invoice: "Purchase invoice", sales_invoice: "Sales invoice", receipt: "Receipt" }[invoice.documentType];
            if (displayType) item.documentType = displayType;
            item.extraction = { adapter: "pdf-invoice-text", parserVersion: invoice.parserVersion, invoice, warnings: invoice.warnings };
            item.parserVersion = invoice.parserVersion;
            item.status = "Needs review";
            item.warning = [...(invoice.warnings || []), "Confirm the mapped invoice fields before accepting this document."].filter(Boolean).join(" ");
            parsed = true;
          } catch (_error) {
            item.status = "Needs review";
            item.warning = "The PDF text layer could not be mapped automatically. Compare the source and enter the invoice fields manually.";
          }
        }
        if (!parsed && item.documentType === "Bank statement") {
          if (!bankingEnabled) {
            item.status = "Needs review";
            item.warning = "Banking is disabled. Enable it before parsing and accepting this statement.";
          }
        } else if (!parsed) {
          item.status = "Needs review";
          item.warning = item.warning || (item.documentType === "Unknown document" ? "Choose a document type before accepting this file." : INVOICE_IMPORT_DOCUMENT_TYPES.includes(item.documentType) ? "This source needs manual field mapping before it can be accepted." : "Confirm the classification before accepting this document.");
        }
      }
      if (item.statusHistory?.[item.statusHistory.length - 1]?.status !== item.status) {
        item.statusHistory = [...(item.statusHistory || []), { status: item.status, at: new Date().toISOString() }];
      }
      known.push(item);
      created.push(item);
      if (item.previewKind !== "metadata") localSources.set(item.id, entry.file);
    }
    const result = intakeMutationRepository.addBatch({ createdItems: created, records: intakeItems, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      created.forEach((item) => {
        if (item.previewUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.previewUrl);
      });
      setToast(result.error);
      return [];
    }
    const localSourceStore = localSources.size ? await import(/* webpackChunkName: "local-source-store" */ "./localSourceFileStore") : null;
    const retained = await Promise.all([...localSources].map(async ([itemId, source]) => localSourceStore.saveLocalSourceFile(itemId, source)));
    setIntakeItems(result.records);
    recordActivity(result.audit);
    setToast(retained.some(saved => !saved) ? 'Review saved, but the original file could not be retained in this browser. Attach it again when storage is available.' : result.toast);
    return [...new Set(result.itemIds.map(id => existingStatements.get(id) || id))];
  };
  const purchaseInvoiceSaveContextRef = useRef(null);
  purchaseInvoiceSaveContextRef.current = { accountScope, serverAccount, publicMode, profile, userId: authSession?.userId, sessionMode, localSpace, organizationId: organization?.id, effectiveBusinessScopeId };
  const saveIntakePurchaseInvoice = async (itemId, changes, originalReview, reviewContext) => {
    if (!serverAccount && sessionMode === 'local') {
      const captured = purchaseInvoiceSaveContextRef.current;
      const isCurrent = () => {
        const current = purchaseInvoiceSaveContextRef.current;
        return !current.serverAccount && !current.publicMode && current.sessionMode === 'local'
          && current.localSpace === captured.localSpace && current.organizationId === captured.organizationId
          && current.effectiveBusinessScopeId === captured.effectiveBusinessScopeId && current.profile.id === captured.profile.id
          && originalReview?.id === itemId && originalReview.workspaceId === current.organizationId
          && canAccessBusiness(current.profile, originalReview.businessId)
          && canUseAccountingOperation(current.profile, 'intake') && canUseAccountingOperation(current.profile, 'purchases')
          && reviewContext?.isCurrent?.() === true;
      };
      const { saveLocalPurchaseInvoice } = await import('./repositories/localPurchaseInvoiceRepository');
      const result = await saveLocalPurchaseInvoice({ item: originalReview, changes, organizationId: organization.id,
        businessId: originalReview?.businessId, userId: profile.id, space: localSpace, sourceMode: 'local', isCurrent,
        getSnapshot: () => localSnapshotRef.current, freshness: localPersistence.beginAtomic() });
      if (!result.ok) return result;
      const adopted = localPersistence.adopt(result, applyLocalCommit, 'state');
      if (adopted.ok) setToast('');
      return adopted;
    }
    const userId = authSession?.userId;
    const isCurrent = () => {
      const current = purchaseInvoiceSaveContextRef.current;
      return current?.serverAccount && !current.publicMode && current.accountScope === accountScope
        && accountScopeRef.current === accountScope && current.userId === userId
        && originalReview?.workspaceId === organization.id && originalReview?.id === itemId
        && canAccessBusiness(current.profile, originalReview.businessId)
        && canUseAccountingOperation(current.profile, 'intake') && canUseAccountingOperation(current.profile, 'purchases')
        && reviewContext?.isCurrent?.() === true;
    };
    if (!isCurrent()) return { ok: false, error: 'Private account purchase and document-intake access are required.' };
    const { saveAccountPurchaseInvoice } = await import(/* webpackChunkName: "account-purchase-invoice-save" */ './backend/accountPurchaseInvoiceSave');
    return saveAccountPurchaseInvoice({ item: originalReview, changes, organizationId: organization.id,
      businessId: originalReview.businessId, userId, vendors: accountPurchaseVendors, sourceMode: 'account', isCurrent,
      loadDetail: loadAccountIntakeDetail,
      saveReview: (id, patch, before) => runAccountCommand(() => accountRepositoryRuntime.accountApi.savePurchaseInvoiceReview(before, patch), { successMessage: 'Purchase review saved' }),
      markReady: (id, before) => runAccountCommand(() => accountRepositoryRuntime.accountApi.markPurchaseInvoiceReady(before), { successMessage: 'Purchase essentials reviewed' }),
      acceptPurchase: (id, document, source) => runAccountCommand(() => accountRepositoryRuntime.purchases.acceptIntake(id, document, source), { successMessage: 'Purchase invoice saved. No payment recorded.' }),
    });
  };
  const canReviewPurchaseEdi = scope => {
    const current = purchaseInvoiceSaveContextRef.current;
    return current.serverAccount && !current.publicMode && current.accountScope === accountScope
      && accountScopeRef.current === accountScope && current.userId === authSession?.userId
      && scope.organizationId === current.organizationId && scope.profile === 'edi-purchase'
      && canAccessBusiness(current.profile, scope.businessId)
      && canUseAccountingOperation(current.profile, 'intake') && canUseAccountingOperation(current.profile, 'purchases');
  };
  const invoiceEdiReviewApi = serverAccount && !publicMode ? {
    userId: authSession?.userId,
    recoveryController: accountRepositoryRuntime.billingRecovery,
    read: scope => canReviewPurchaseEdi(scope)
      ? accountRepositoryRuntime.accountApi.readInvoiceEdiReview(scope)
      : Promise.resolve({ ok: false, error: 'Private purchase and document-intake access are required for EDI configuration.' }),
    save: (snapshot, posting) => canReviewPurchaseEdi(snapshot)
      ? accountRepositoryRuntime.accountApi.saveInvoiceEdiReview(snapshot, posting)
      : Promise.resolve({ ok: false, error: 'Reopen the original private purchase before configuring EDI.' }),
    retry: record => canReviewPurchaseEdi(record.body) && accountRepositoryRuntime.billingRecovery.getSnapshot().pending?.key === record.key
      ? accountRepositoryRuntime.billingRecovery.retry()
      : Promise.resolve({ ok: false, error: 'The original EDI request is no longer active in this account.' }),
  } : null;
  const updateIntakeItem = async (itemId, changes, originalReview = null) => {
    if (serverAccount) {
      const item = accountIntakeItems.find((candidate) => candidate.id === itemId);
      if (publicMode || !originalReview || originalReview.id !== itemId || originalReview.workspaceId !== organization.id || originalReview.businessId !== item?.businessId || !Number.isInteger(originalReview.revision) || originalReview.revision < 1) {
        setToast("Reload the original document before saving its review");
        return false;
      }
      const next = { ...originalReview, ...changes };
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.saveIntakeReview(itemId, {
          documentType: next.documentType,
          period: next.period,
          expectedRevision: originalReview.revision,
          reviewFields: { ...(originalReview.reviewFields || {}), ...(changes.reviewFields || {}) },
          accountingEvidence: { ...(originalReview.accountingEvidence || {}), ...(changes.accountingEvidence || {}) },
        }),
        { successMessage: "Intake review saved" },
      );
      return accountResult.ok;
    }
    const item = intakeItems.find((candidate) => candidate.id === itemId);
    const business = businesses.find((candidate) => candidate.id === item?.businessId);
    const result = intakeMutationRepository.editReview({ item, records: intakeItems, changes, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) { setToast(result.error); return false; }
    if (!result.noop) setIntakeItems(result.records);
    return true;
  };
  const loadIntakePeriodProposal = item => accountRepositoryRuntime.reads.intake.periodProposal({ organizationId: organization.id, businessId: item.businessId, intakeRecordId: item.id });
  const confirmIntakeFilingPeriod = (item, input) => runAccountCommand(
    () => accountRepositoryRuntime.commands.confirmIntakePeriod(item.id, { ...input, organizationId: organization.id, businessId: item.businessId, filename: item.originalFilename }),
    { successMessage: "Filing period confirmed" },
  );
  const retryIntakeProcessing = async (itemId, item) => {
    const jobType = intakeProcessingType(item);
    if (!serverAccount || publicMode || item?.id !== itemId || item?.workspaceId !== organization.id || !jobType) {
      setToast("Reload the unreviewed account document before requesting extraction");
      return false;
    }
    const retryId = typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${item.processing?.attempt || 1}`;
    const result = await runAccountCommand(
      () => accountRepositoryRuntime.commands.queueIntake(itemId, {
        type: jobType,
        expectedRevision: item.revision,
        businessId: item.businessId,
        filename: item.originalFilename,
        parserKey: null,
        idempotencyKey: `intake:${itemId}:${jobType}:retry:${retryId}`,
      }),
      { successMessage: "Document extraction queued" },
    );
    if (result.ok && intakePage.loaded) await intakePage.reload();
    return result.ok;
  };
  const classifyIntakeItem = async (itemId, changes, originalReview, { reparse = false, draftContext = null } = {}) => {
    const statement = changes?.documentType === "Bank statement";
    const hasStatementRows = Array.isArray(originalReview?.extraction?.transactions) && originalReview.extraction.transactions.length > 0;
    if (statement && serverAccount && !hasStatementRows) {
      const processingActive = ["Queued", "Running"].includes(originalReview?.processing?.status);
      if (processingActive) return { ok: false, error: "This document is still being processed. Wait for the current attempt before choosing Bank statement." };
      const hasReviewValues = (value) => Object.values(value || {}).some((entry) => entry !== undefined && entry !== null);
      const unreviewed = originalReview?.reviewedAt === null && originalReview?.reviewedBy == null
        && !hasReviewValues(originalReview?.reviewFields) && !hasReviewValues(originalReview?.accountingEvidence);
      const eligibleStatus = ["New", "Needs review", "Failed", "Unsupported"].includes(originalReview?.status);
      if (publicMode || originalReview?.id !== itemId || originalReview?.workspaceId !== organization.id || originalReview?.sourceStatus !== "uploaded"
        || !Number.isInteger(originalReview?.revision) || originalReview.revision < 1 || originalReview?.archived || originalReview?.archivedAt || !unreviewed || !eligibleStatus) {
        return { ok: false, error: "Reload the unreviewed account document before requesting statement processing." };
      }
      const requestId = typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}-${originalReview.processing?.attempt || 1}`;
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.commands.queueIntake(itemId, {
          type: "parse_bank_statement",
          expectedRevision: originalReview.revision,
          businessId: originalReview.businessId,
          filename: originalReview.originalFilename,
          parserKey: null,
          idempotencyKey: `intake:${itemId}:parse_bank_statement:classification:${requestId}`,
        }),
        { successMessage: "Bank statement processing queued" },
      );
      if (result.ok && intakePage.loaded) await intakePage.reload();
      return { ok: result.ok, queued: result.ok, error: result.error };
    }
    if (statement && !serverAccount && (!hasStatementRows || reparse)) {
      try {
        const { commitLocalBankStatementReread } = await import(/* webpackChunkName: "bank-statement-reread" */ "./backend/localBankStatementReread");
        const currentItem = intakeItemsRef.current.find((item) => item.id === itemId);
        const business = businesses.find((candidate) => candidate.id === currentItem?.businessId);
        if(!business || originalReview?.id!==itemId)return {ok:false,error:'Reload the saved statement in its original business.'};
        const scope=`invoice-studio:bank-draft:v1:${sessionMode}/${localSpace}:${authSession?.userId || profile.id}:${organization.id}:${business.id}`;
        if(draftContext && (draftContext.scope!==scope || draftContext.storageKey!==scope+':'+itemId))return {ok:false,error:'The saved draft belongs to another statement context.'};
        const originalContext=JSON.stringify(bankDraftContext.current);
        // A same-file upload can restore bytes before the debounced intake
        // snapshot is saved. Finish that save before taking the atomic boundary.
        if (sessionMode === 'local') {
          const persisted = await localPersistence.flush();
          if (!persisted.ok) return { ok:false, error:'The workspace could not be saved before updating this statement. Resolve the browser save error and retry.' };
          if (JSON.stringify(bankDraftContext.current) !== originalContext) return { ok:false, error:'Reopen the statement in its original local workspace.' };
        }
        const result=await commitLocalBankStatementReread({freshness:localPersistence.beginAtomic(),space:localSpace,getSnapshot:()=>localSnapshotRef.current,userId:profile.id,item:originalReview,business,organizationId:organization.id,scope,
          expectedDraftRaw:draftContext?draftContext.savedRaw:localStorage.getItem(scope+':'+itemId),buildActivity:createActivity,
          isCurrent:()=>JSON.stringify(bankDraftContext.current)===originalContext&&!bankDraftContext.current.serverAccount&&!bankDraftContext.current.publicMode});
        if (!result.ok) return result;
        return {...localPersistence.adopt(result, applyLocalCommit),queued:false};
      } catch (error) {
        const parserError = error?.message || originalReview?.bankParserError || originalReview?.parserError || originalReview?.error;
        return { ok: false, error: `${parserError || "This bank statement format is not supported yet."} The original source is preserved and no review was saved.` };
      }
    }
    return { ok: await updateIntakeItem(itemId, changes, originalReview), queued: false };
  };
  const markIntakeReady = async (itemId, reviewedItem) => {
    const item = reviewedItem?.id === itemId ? reviewedItem : accountIntakeItems.find((candidate) => candidate.id === itemId);
    const business = businesses.find((candidate) => candidate.id === item?.businessId);
    const result = intakeMutationRepository.markReady({ item, records: intakeItems, business, organizationId: organization.id, profile, publicMode, bankingEnabled });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.markIntakeReady(itemId, item.revision),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    setIntakeItems(result.records);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const saveDocumentExpectation = async (expectation) => {
    const business = businesses.find((candidate) => candidate.id === expectation.businessId);
    const result = intakeMutationRepository.saveExpectation({ expectation, records: documentExpectations, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.saveDocumentExpectation(organization.id, expectation.businessId, expectation),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    setDocumentExpectations(result.records);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const savePurchaseVendor = vendor => {
    if (!serverAccount || publicMode) return Promise.resolve({ ok: false, error: "A connected, private account workspace is required." });
    return runAccountCommand(() => accountRepositoryRuntime.purchases.saveVendor({ ...vendor, organizationId: organization.id }), {
      successMessage: "Supplier saved",
    });
  };
  const archivePurchaseVendor = (vendor, archived) => {
    if (!serverAccount || publicMode) return Promise.resolve({ ok: false, error: "A connected, private account workspace is required." });
    return runAccountCommand(() => accountRepositoryRuntime.purchases.archiveVendor({ ...vendor, organizationId: organization.id }, archived), {
      successMessage: archived ? "Supplier archived" : "Supplier restored",
    });
  };
  const archivePurchaseDocument = (source, archived) => {
    if (!serverAccount || publicMode || source?.organizationId !== organization.id) return Promise.resolve({ ok: false, error: "A connected, private account workspace is required." });
    return runAccountCommand(() => accountRepositoryRuntime.purchases.archiveDocument(source, archived), {
      successMessage: archived ? "Purchase archived" : "Purchase restored",
      onSuccess: result => { if (result.data?.purchaseDocument?.id) openPurchaseRecord(result.data.purchaseDocument.id); },
    });
  };
  const savePurchaseDocument = async (document, original = null) => {
    if (publicMode) {
      setToast("Turn off public mode before changing purchase records");
      return { ok: false, error: "Public mode is read-only." };
    }
    if (!serverAccount) {
      setToast("Purchase records become durable after the account workspace is connected.");
      return { ok: false, error: "An account workspace is required." };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.purchases.saveDocument({
        ...document,
        organizationId: organization.id,
        sourceKind: document.sourceKind || "manual",
      }, original),
      { successMessage: document.id ? "Purchase record updated" : "Purchase record created",
        onSuccess: result => { if (result.data?.purchaseDocument?.id) openPurchaseRecord(result.data.purchaseDocument.id); } },
    );
  };
  const reviewPurchaseDocument = async (documentId, review, source) => {
    if (publicMode) {
      setToast("Turn off public mode before reviewing purchase records");
      return { ok: false, error: "Public mode is read-only." };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.purchases.reviewDocument(documentId, review, source),
      { successMessage: review.reviewStatus === "reviewed" ? "Purchase review confirmed" : "Purchase review updated" },
    );
  };
  const recordPurchasePayment = async (documentId, payment, source) => {
    if (publicMode) {
      setToast("Turn off public mode before recording vendor payments");
      return { ok: false, error: "Public mode is read-only." };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.purchases.savePayment(documentId, payment, source),
      { successMessage: "Vendor payment recorded" },
    );
  };
  const reversePurchasePayment = async (paymentId, reason, expectedRevision, source, payment) => {
    if (publicMode) {
      setToast("Turn off public mode before reversing vendor payments");
      return { ok: false, error: "Public mode is read-only." };
    }
    return runAccountCommand(
      () => accountRepositoryRuntime.purchases.reversePayment(paymentId, reason, expectedRevision, source, payment),
      { successMessage: "Vendor payment reversed" },
    );
  };
  const openImportedBankStatement = (receipt) => {
    if (accountBusinessId && accountBusinessId !== receipt.business_id) changeBusinessScope(receipt.business_id);
    setBankingBusinessId(receipt.business_id);
    setBankIntakeFocusId("");
    setBankStatementFilterId(receipt.id);
    navigate("banking");
  };
  const acceptIntakeItem = async (itemId, reviewedItem, statementPage) => {
    const item = reviewedItem?.id === itemId ? reviewedItem : accountIntakeItems.find((candidate) => candidate.id === itemId);
    const business = businesses.find((candidate) => candidate.id === item?.businessId);
    if (serverAccount && item?.documentType === "Bank statement") {
      if (publicMode || !bankingEnabled || !canUseAccountingOperation(profile, "intake") || !canUseAccountingOperation(profile, "banking") || !business || !canAccessBusiness(profile, item.businessId)) return { ok: false, error: "Document intake and Banking access are required." };
      try {
        const { reviewedBankStatement } = await import(/* webpackChunkName: "account-banking-commands" */ "./backend/accountBankStatementReview");
        const snapshot = reviewedBankStatement(item, statementPage);
        const current = await loadAccountIntakeDetail(itemId);
        const review = await accountRepositoryRuntime.reads.intake.statementReview({ organizationId: organization.id, businessId: item.businessId,
          intakeId: itemId, revision: item.revision, extractionRunId: item.extraction.id, offset: 0 });
        if (!review?.ok) return review;
        if (JSON.stringify(snapshot) !== JSON.stringify(reviewedBankStatement(current, review.data))) return { ok: false, error: "The statement changed. Refresh its review before importing." };
        return await runAccountCommand(() => accountRepositoryRuntime.commands.acceptBankStatement(itemId, item.revision, item.extraction.id, snapshot), {
          successMessage: "Statement imported. Review its transactions in Banking.", onSuccess: result => openImportedBankStatement(result.data),
        });
      } catch (error) { return { ok: false, error: error.message || "The statement could not be verified." }; }
    }
    const result = intakeMutationRepository.accept({ item, records: accountIntakeItems, bankTransactions, business, organizationId: organization.id, profile, publicMode, bankingEnabled });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (serverAccount) {
      const purchaseSource = ["Purchase invoice", "Receipt"].includes(item.documentType);
      if (purchaseSource && !canUseAccountingOperation(profile, "purchases")) {
        setToast("Purchase access is required before this source can become an accounting record.");
        return false;
      }
      const purchaseDocument = purchaseSource ? purchaseDocumentFromIntake({
        item,
        vendors: accountPurchaseVendors,
        organizationId: organization.id,
      }) : null;
      if (purchaseSource && (!purchaseDocument || !purchaseDocument.vendorName || !purchaseDocument.issuedOn)) {
        setToast("Confirm the supplier, issue date, currency, and gross amount before accepting this purchase.");
        return false;
      }
      let purchaseSnapshot;
      if (purchaseSource) {
        try {
          const { reviewedPurchaseIntake, purchaseIntakeOptions } = await import(/* webpackChunkName: "account-purchase-commands" */ "./backend/accountPurchaseIntakeRecovery");
          purchaseSnapshot = reviewedPurchaseIntake(item, purchaseDocument);
          const current = await loadAccountIntakeDetail(itemId);
          if (accountScopeRef.current !== accountScope) throw new Error("The active account changed. Reopen the purchase in its original workspace.");
          const currentDocument = purchaseDocumentFromIntake({ item: current, vendors: accountPurchaseVendors, organizationId: organization.id });
          const currentSnapshot = reviewedPurchaseIntake(current, currentDocument);
          if (JSON.stringify(purchaseIntakeOptions(itemId, purchaseDocument, purchaseSnapshot)) !== JSON.stringify(purchaseIntakeOptions(itemId, currentDocument, currentSnapshot))) {
            throw new Error("The purchase changed. Refresh its saved mapping before accepting.");
          }
        } catch (error) { setToast(error.message || "The purchase could not be verified."); return false; }
      }
      const command = purchaseSource
          ? () => accountRepositoryRuntime.purchases.acceptIntake(itemId, purchaseDocument, purchaseSnapshot)
          : () => accountRepositoryRuntime.commands.acceptIntake(itemId, item.revision);
      const accountResult = await runAccountCommand(command, {
        successMessage: purchaseSource ? "Source accepted and added to Purchases" : result.toast,
      });
      return accountResult.ok;
    }
    setIntakeItems(result.records);
    setBankTransactions(result.bankTransactions);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const removeIntakeItem = async (itemId, reviewedItem) => {
    const item = reviewedItem?.id === itemId ? reviewedItem : accountIntakeItems.find((candidate) => candidate.id === itemId);
    const business = businesses.find((candidate) => candidate.id === item?.businessId);
    const result = intakeMutationRepository.remove({ item, records: intakeItems, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.archiveIntake(itemId, true, item.revision),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    if (result.previewUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(result.previewUrl);
    if (sessionMode === "local") {
      const { removeLocalSourceFile } = await import(/* webpackChunkName: "local-source-store" */ "./localSourceFileStore");
      await removeLocalSourceFile(itemId);
    }
    setIntakeItems(result.records);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const exportIntakeDiagnostic = (itemId) => {
    const item = intakeItems.find((candidate) => candidate.id === itemId);
    if (!item || !canAccessBusiness(profile, item.businessId) || !canUseAccountingOperation(profile, "intake")) {
      setToast(accessDeniedMessage);
      return;
    }
    const blob = new Blob([JSON.stringify(createIntakeDiagnostic(item), null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `intake-diagnostic-${item.id}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    recordActivity({ action: "Intake diagnostic exported", detail: `Exported privacy-filtered parser diagnostics for ${item.originalFilename}`, entityType: "intake", entityId: item.id });
    setToast("Privacy-filtered diagnostic downloaded");
  };
  const confirmBankReconciliation = async (transactionId, allocation, original) => {
    if (serverAccount) {
      if (publicMode || original?.id !== transactionId || !allocation?.invoices?.length) {
        setToast("Review the original bank transaction and invoices before confirming");
        return false;
      }
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.banking.confirmInvoiceReconciliation(original, allocation.invoices, allocation.bankFeeAmount),
        { successMessage: "Invoice payment match confirmed" },
      );
      return result.ok;
    }
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const currentInvoices = serverAccount ? accountInvoices : allInvoices;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const result = bankingMutationRepository.confirmReconciliation({
      transaction,
      transactions: currentTransactions,
      invoices: currentInvoices,
      invoiceStatuses,
      invoiceIds: allocation?.invoiceIds || [],
      bankFeeAmount: allocation?.bankFeeAmount || 0,
      business,
      organizationId: organization.id,
      profile,
      publicMode,
    });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    setBankTransactions(result.transactions);
    setInvoiceStatuses(result.invoiceStatuses);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const confirmBankPurchaseReconciliation = async (transactionId, allocation, original) => {
    if (publicMode) {
      setToast("Turn off public mode before confirming bank matches");
      return false;
    }
    if (!serverAccount) {
      setToast("Purchase reconciliation requires an account workspace.");
      return false;
    }
    const purchaseDocuments = allocation?.purchaseDocuments || [];
    if (!purchaseDocuments.length || original?.id !== transactionId) {
      setToast("Choose at least one reviewed purchase first.");
      return false;
    }
    const accountResult = await runAccountCommand(
      () => accountRepositoryRuntime.banking.confirmPurchaseReconciliation(original, purchaseDocuments, allocation.bankFeeAmount),
      { successMessage: `${allocation.classification || "Vendor payment"} confirmed` },
    );
    return accountResult.ok;
  };
  const markBankTransactionNoMatch = async (transactionId) => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const result = bankingMutationRepository.markNoMatch({ transaction, transactions: currentTransactions, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.markBankNoMatch(transactionId),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    setBankTransactions(result.transactions);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const saveBankAccountantReview = async (transactionId, decision) => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find(item => item.id === transactionId);
    const business = businesses.find(item => item.id === transaction?.businessId);
    if (!transaction || !business || decision?.expectedRevision !== (transaction.revision ?? 1)) {
      return { ok: false, code: "resource_revision_conflict", error: "This bank transaction changed. Reload before saving the review." };
    }
    if (serverAccount) return runAccountCommand(
      () => accountRepositoryRuntime.banking.reviewTransaction(transactionId, {
        organizationId: organization.id, businessId: transaction.businessId, state: decision.state,
        reason: decision.reason, expectedRevision: decision.expectedRevision,
      }, { businessId: transaction.businessId, description: String(transaction.description || transaction.reference || "Bank transaction review").slice(0, 240) }),
      { successMessage: decision.state === "reviewed_without_invoice" ? "Accountant review completed" : decision.state === "waiting_for_evidence" ? "Waiting-for-evidence review saved" : "Accountant review reopened" },
    );
    const result = bankingMutationRepository.review({ transaction, transactions: currentTransactions, business,
      organizationId: organization.id, profile, publicMode, state: decision.state, reason: decision.reason,
      expectedRevision: decision.expectedRevision, idempotencyKey: `local-bank-review:${transactionId}:${Date.now()}` });
    if (!result.ok) { setToast(result.error); return result; }
    if (!result.noop) { setBankTransactions(result.transactions); recordActivity(result.audit); }
    setToast(decision.state === "reviewed_without_invoice" ? "Accountant review completed" : decision.state === "waiting_for_evidence" ? "Waiting-for-evidence review saved" : "Accountant review reopened");
    return { ok: true, data: result.receipt };
  };
  const reviewAccountBankDocument = async (...args) => {
    if (!serverAccount || publicMode) return { ok: false, error: "Open your account workspace and turn off public mode to review this payment." };
    return runAccountCommand(() => accountRepositoryRuntime.banking.reviewDocument(...args), { successMessage: "Bank document review saved" });
  };
  const reopenBankReconciliation = async (transactionId, original, reason) => {
    if (serverAccount) {
      if (publicMode || original?.accountReconciliationSnapshot?.id !== transactionId || !original.reconciliation?.id) {
        setToast("Reload the bank match and turn off public mode before reopening it.");
        return false;
      }
      const result = await runAccountCommand(
        () => accountRepositoryRuntime.banking.reopenReconciliation(original.accountReconciliationSnapshot, original.reconciliation.id, reason),
        { successMessage: "Bank match reopened for review" },
      );
      return result.ok;
    }
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const currentInvoices = serverAccount ? accountInvoices : allInvoices;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const result = bankingMutationRepository.reopenReconciliation({ transaction, transactions: currentTransactions, invoices: currentInvoices, invoiceStatuses, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    setBankTransactions(result.transactions);
    setInvoiceStatuses(result.invoiceStatuses);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const updateBankTransactionCategory = async (transactionId, categoryCode, expectedRevision) => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    if (serverAccount && (!Number.isInteger(expectedRevision) || expectedRevision !== transaction?.revision)) {
      setToast("This bank transaction changed. Reload its latest details before saving.");
      return { ok: false, code: "resource_revision_conflict" };
    }
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const result = bankingMutationRepository.updateCategory({ transaction, transactions: currentTransactions, categoryCode, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.setBankTransactionCategory(transactionId, categoryCode, expectedRevision, {
          businessId: transaction.businessId, description: String(transaction.description || transaction.reference || "Bank transaction").slice(0, 240),
        }),
        { successMessage: result.toast },
      );
      return accountResult;
    }
    setBankTransactions(result.transactions);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const updateTransactionEvidence = async (transactionId, changes, expectedRevision) => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    if (serverAccount && (!Number.isInteger(expectedRevision) || expectedRevision !== transaction?.revision)) {
      setToast("This bank transaction changed. Reload its latest details before saving.");
      return { ok: false, code: "resource_revision_conflict" };
    }
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const options = { transaction, transactions: currentTransactions, changes, members: teamMembers, business, organizationId: organization.id, profile, publicMode };
    const result = serverAccount ? prepareAccountBankEvidenceUpdate(options) : bankingMutationRepository.updateEvidence(options);
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.updateBankEvidence(transactionId, { ...result.followup, expectedRevision }, {
          businessId: transaction.businessId, description: String(transaction.description || transaction.reference || "Bank transaction").slice(0, 240),
        }),
        { successMessage: "Evidence follow-up updated" },
      );
      return accountResult;
    }
    setBankTransactions(result.transactions);
    recordActivity(result.audit);
    return true;
  };
  const confirmOutgoingEvidenceMatch = async (transactionId, intakeId) => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    const evidence = (serverAccount ? accountIntakeItems : intakeItems).find((item) => item.id === intakeId);
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const result = bankingMutationRepository.linkEvidence({ transaction, evidence, transactions: currentTransactions, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (serverAccount) {
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.linkBankEvidence(transactionId, intakeId),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    setBankTransactions(result.transactions);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const unlinkOutgoingEvidenceMatch = async (transactionId, activeLinkId = "") => {
    const currentTransactions = serverAccount ? accountBankTransactions : bankTransactions;
    const transaction = currentTransactions.find((item) => item.id === transactionId);
    const business = businesses.find((item) => item.id === transaction?.businessId);
    const transactionWithLink = activeLinkId ? { ...transaction, evidenceMatch: { ...(transaction?.evidenceMatch || {}), id: activeLinkId, confirmedAt: transaction?.evidenceMatch?.confirmedAt || new Date().toISOString() } } : transaction;
    const result = bankingMutationRepository.unlinkEvidence({ transaction: transactionWithLink, transactions: currentTransactions, business, organizationId: organization.id, profile, publicMode });
    if (!result.ok) {
      setToast(result.error);
      return false;
    }
    if (result.noop) return true;
    if (serverAccount) {
      const linkId = activeLinkId || transaction?.evidenceMatch?.id;
      if (!linkId) {
        setToast("The active evidence link could not be found.");
        return false;
      }
      const accountResult = await runAccountCommand(
        () => accountRepositoryRuntime.commands.unlinkBankEvidence(linkId, "Reopened for evidence review in Invoice Studio"),
        { successMessage: result.toast },
      );
      return accountResult.ok;
    }
    setBankTransactions(result.transactions);
    recordActivity(result.audit);
    setToast(result.toast);
    return true;
  };
  const prepareTransactionEvidenceRequest = (transaction) => {
    if (!transaction || !canAccessBusiness(profile, transaction.businessId)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode before preparing a private message");
      return;
    }
    const business = organizationBusinesses.find((item) => item.id === transaction.businessId);
    if (!business) {
      setToast("Assign this transaction to a managed business first");
      return;
    }
    setWhatsappRequest({ id: `evidence-${transaction.id}`, transactionId: transaction.id, type: "missing-receipt", audience: "business", contactPurpose: "document-request", templateId: "missing-receipt", record: business, businessName: business.name, period: transaction.statementPeriod || "the current accounting period", transactionDate: transaction.bookedAt || "the statement date", documentName: "the supporting invoice or receipt", deadline: transaction.evidenceDeadline || "the agreed deadline" });
  };
  const exportQuickBooksBatch = async (batch, business, { format = "workbook" } = {}) => {
    if (!business || !canAccessBusiness(profile, business.id)) {
      setToast(accessDeniedMessage);
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode before exporting accounting records");
      return;
    }
    if (!batch.rows.length || !batch.validation.valid) {
      setToast("Resolve the batch validation errors before export");
      return;
    }
    try {
      const {
        downloadQuickBooksWorkbook,
        quickBooksFilename,
      } = await import(/* webpackChunkName: "quickbooks-workbook" */ "./quickbooks/bridge");
      const businessName = business?.name || "Issuing business";
      const filename = quickBooksFilename(businessName);
      const directIif = format === "iif";
      if (serverAccount) {
        const uniqueSources = [...new Map(batch.rows.map((row) => [
          `${row.sourceType}:${row.sourceEntityId}`,
          { type: row.sourceType, id: row.sourceEntityId },
        ])).values()];
        const prepared = await runAccountCommand(
          () => accountRepositoryRuntime.commands.prepareQuickBooksExport({
            businessId: business.id,
            filename,
            version: batch.version,
            profileId: batch.profile.id,
            sources: uniqueSources,
          }, organization.id),
          { successMessage: "QuickBooks batch prepared" },
        );
        if (!prepared.ok) return false;
        const exportRecord = prepared.data;
        const reader = directIif
          ? accountRepositoryRuntime.reads.quickBooksExports.iif
          : accountRepositoryRuntime.reads.quickBooksExports.file;
        const file = await reader({
          organizationId: organization.id,
          exportId: exportRecord.id,
        });
        if (!file?.ok) {
          setToast(file?.error || `The prepared QuickBooks ${directIif ? "IIF preview" : "workbook"} could not be loaded.`);
          return false;
        }
        if (Number(file.data?.recordCount) !== Number(exportRecord.rowCount || 0)
          || file.data?.manifestSha256 !== exportRecord.manifestSha256) {
          setToast(`The prepared QuickBooks ${directIif ? "IIF preview" : "workbook"} did not match its immutable manifest.`);
          return false;
        }
        const savedFilename = saveAccountFile(file.data);
        const downloaded = await runAccountCommand(
          () => accountRepositoryRuntime.commands.markQuickBooksExportDownloaded(exportRecord.id, organization.id),
          { successMessage: `Downloaded ${savedFilename}` },
        );
        if (!downloaded.ok) setToast(`Downloaded ${savedFilename}, but its download audit could not be recorded.`);
        return downloaded.ok;
      }
      if (directIif) {
        setToast("Sign in to an account workspace before preparing a server-validated QuickBooks IIF preview.");
        return false;
      }
      await downloadQuickBooksWorkbook(batch, businessName, filename);
      const record = { id: `quickbooks-${Date.now()}`, businessId: business?.id || "", filename, rowCount: batch.rows.length, version: batch.version, profileId: batch.profile.id, warningCount: batch.validation.warnings.length, createdAt: new Date().toISOString(), createdBy: profile.name };
      setQuickBooksExports((current) => [record, ...current]);
      recordActivity({ action: "QuickBooks staging exported", detail: `Exported ${batch.rows.length} reviewed invoice row${batch.rows.length === 1 ? "" : "s"} for ${business?.name || "the issuing business"}`, entityType: "quickbooks-export", entityId: record.id });
      setToast(`Downloaded ${filename}`);
      return true;
    } catch (error) {
      console.error(error);
      setToast(`The QuickBooks ${format === "iif" ? "IIF preview" : "staging workbook"} could not be created`);
      return false;
    }
  };
  const prepareEdiExportBatch = async (request) => {
    if (!serverAccount || !organization?.id) {
      return { ok: false, code: "not_account_mode", error: "An account workspace is required for a server-owned EDI export." };
    }
    if (publicMode) {
      return { ok: false, code: "public_mode", error: "Turn off public mode before exporting financial records." };
    }
    const { expectedEdiReview, ...command } = request;
    const prepared = await accountRepositoryRuntime.commands.prepareEdiExport(command, organization.id);
    if (!prepared?.ok) return prepared;
    const issueResult = await collectAccountExportPages({
      reader: accountRepositoryRuntime.reads.ediExports.issues,
      organizationId: organization.id,
      exportId: prepared.data.id,
      noun: "EDI review issues",
      expectedCount: Number(prepared.data.errorCount || 0) + Number(prepared.data.warningCount || 0),
      maxPages: 50,
      maxItems: 5_000,
      unavailableCode: "edi_issues_unavailable",
      countMismatchCode: "edi_issue_count_mismatch",
    });
    if (!issueResult.ok) return issueResult;
    if (prepared.data.status === "needs_review" || issueResult.items.some((issue) => issue.severity === "error")) {
      return { ...prepared, issues: issueResult.items, rows: [] };
    }
    if (expectedEdiReview) {
      const sources = await collectAccountExportPages({ reader: accountRepositoryRuntime.reads.ediExports.sources,
        organizationId: organization.id, exportId: prepared.data.id, noun: 'EDI source revision', expectedCount: 1,
        maxPages: 1, maxItems: 1, unavailableCode: 'edi_source_unavailable', countMismatchCode: 'edi_source_mismatch' });
      if (!sources.ok) return sources;
      try {
        const { verifyPreparedInvoiceEdiReview } = await import('./backend/accountInvoiceEdiReview');
        verifyPreparedInvoiceEdiReview(expectedEdiReview, prepared.data, sources.items);
      } catch (error) { return { ok: false, code: 'edi_review_changed', error: error.message }; }
    }
    const file = await accountRepositoryRuntime.reads.ediExports.file({
      organizationId: organization.id,
      exportId: prepared.data.id,
    });
    if (!file?.ok) return file;
    if (Number(file.data?.recordCount) !== Number(prepared.data.rowCount || 0)
      || file.data?.manifestSha256 !== prepared.data.manifestSha256) {
      return { ok: false, code: "edi_file_manifest_mismatch", error: "The prepared EDI file did not match its immutable manifest." };
    }
    return { ...prepared, data: { ...prepared.data, file: file.data }, issues: issueResult.items };
  };
  const markEdiExportDownloaded = async (exportId) => {
    if (!serverAccount || !organization?.id) {
      return { ok: false, code: "not_account_mode", error: "An account workspace is required to record this EDI download." };
    }
    return accountRepositoryRuntime.commands.markEdiExportDownloaded(exportId, organization.id);
  };
  const loadVatFilingClose = useCallback(async (request) => {
    if (!serverAccount || !organization?.id) {
      return { ok: false, code: "not_account_mode", error: "An account workspace is required to load a server-owned VAT filing package." };
    }
    if (publicMode) return { ok: true, data: null };
    return accountRepositoryRuntime.reads.vatFilings.latest({
      organizationId: organization.id,
      businessId: request.businessId,
      accountingPeriod: request.accountingPeriod,
    });
  }, [organization?.id, publicMode, serverAccount]);
  const prepareVatFilingClose = async (request) => {
    if (!serverAccount || !organization?.id) {
      return { ok: false, code: "not_account_mode", error: "An account workspace is required for a server-owned VAT filing package." };
    }
    if (publicMode) {
      return { ok: false, code: "public_mode", error: "Turn off public mode before preparing financial records." };
    }
    return accountRepositoryRuntime.commands.prepareVatFiling(request, organization.id);
  };
  const downloadVatFilingClose = async (close) => {
    if (!serverAccount || !organization?.id || !close?.id) {
      return { ok: false, code: "not_account_mode", error: "An account workspace is required to download a VAT filing package." };
    }
    if (publicMode) {
      return { ok: false, code: "public_mode", error: "Turn off public mode before downloading financial records." };
    }
    const file = await accountRepositoryRuntime.reads.vatFilings.file({
      organizationId: organization.id,
      closeId: close.id,
    });
    if (!file?.ok) return file;
    if (file.data?.manifestSha256 !== close.manifestSha256) {
      return { ok: false, code: "vat_file_manifest_mismatch", error: "The VAT filing package did not match its immutable close manifest." };
    }
    const filename = saveAccountFile(file.data);
    const downloaded = await accountRepositoryRuntime.commands.markVatFilingDownloaded(close.id, organization.id);
    if (!downloaded?.ok) {
      return { ...downloaded, error: `Downloaded ${filename}, but its download audit could not be recorded.` };
    }
    return { ok: true, filename, data: downloaded.data };
  };
  const openDocumentIntake = (itemId = "", mode = "summary") => {
    navigate("intake");
    if (itemId) setBusinessScopeId(ALL_BUSINESSES_SCOPE);
    setFocusedIntakeItemId(itemId);
    setFocusedIntakeMode(itemId ? mode : "summary");
  };
  const openBankingFromIntake = (intakeRecordId = "") => {
    setBankStatementFilterId(serverAccount ? "" : intakeRecordId);
    setBankIntakeFocusId(serverAccount ? intakeRecordId : "");
    navigate("banking");
  };
  const renderBankingArchive = ({ businessId, openIntake }) => {
    const scopedBusinesses = dailyBusinesses.filter((business) => business.id === businessId);
    const scopedItems = (serverAccount ? accountIntakeItems : dailyIntakeItems).filter((item) => item.businessId === businessId);
    return <CompanyArchiveView businesses={scopedBusinesses} sourceSummaries={organizationAccountingSources.filter((item) => !item.businessId || item.businessId === businessId)} intakeItems={scopedItems} openIntake={openIntake} accountArchive={serverAccount ? { organizationId: organization.id, ...accountRepositoryRuntime?.reads?.archive } : null} />;
  };
  const bankDraftContext = useRef(null);
  const renderBankDocumentRequests = (businessId, ids) => {
    const business = dailyBusinesses.find(item => item.id === businessId);
    if (!business || !ids.length) return null;
    const mode = serverAccount ? 'account' : `${sessionMode}/${localSpace}`, ownerId = authSession?.userId || profile.id;
    return <BankDocumentRequests key={`${accountScope}/${businessId}`} mode={mode} ownerId={ownerId} organizationId={organization.id} business={business}
      scope={`invoice-studio:bank-draft:v1:${mode}:${ownerId}:${organization.id}:${businessId}`} ids={ids} items={serverAccount ? accountIntakeItems : dailyIntakeItems}
      transactions={accountBankTransactions} confirmedOnly disabled={publicMode} loadDetail={serverAccount ? loadAccountIntakeDetail : null}
      loadPage={serverAccount ? (item, offset) => accountRepositoryRuntime.accountApi.bankingDraftCommand('readBankStatementDraft', {
        organizationId: organization.id, businessId, intakeId: item.id, revision: item.revision, extractionRunId: item.extraction?.id, offset }) : null} />;
  };
  bankDraftContext.current = { organizationId: organization?.id, userId: authSession?.userId || profile.id, serverAccount, publicMode, sessionMode, localSpace };
  const renderBankingIntake = ({ context, businessId, close, onFlowStateChange, openArchive, openMatching }) => {
    const scopedBusinesses = dailyBusinesses.filter((business) => business.id === businessId);
    const businessReviewItems = (serverAccount ? accountIntakeItems : dailyIntakeItems).filter((item) => item.businessId === businessId);
    const scopedItems = businessReviewItems.filter((item) => context.kind !== "support" || item.id === context.itemId);
    if (context.kind === 'statement' && scopedBusinesses[0]) return <BankStatementDraftReview key={`bank-draft/${accountScope || sessionMode}/${businessId}`}
      items={scopedItems} initialItemId={context.itemId || ''} business={scopedBusinesses[0]} organizationId={organization.id}
      mode={serverAccount ? 'account' : `${sessionMode}/${localSpace}`} ownerId={authSession?.userId || profile.id} readOnly={publicMode}
      transactions={bankTransactions} invoices={serverAccount ? [] : localInvoiceTargets(localSnapshotRef.current,organization.id,businessId)}
      invoiceRepository={serverAccount ? accountRepositoryRuntime.reads.invoices : null} purchaseRepository={serverAccount ? accountRepositoryRuntime.reads.purchaseDocuments : null}
      documentRepository={serverAccount ? accountRepositoryRuntime.reads.intake : null}
      uploadFiles={async (...args) => { const ids = await uploadIntakeFiles(...args); return Array.isArray(ids) ? { ok: ids.length > 0, itemIds: ids } : ids; }}
      classifyItem={classifyIntakeItem} loadDetail={serverAccount ? loadAccountIntakeDetail : null}
      loadPage={serverAccount ? (item,offset) => accountRepositoryRuntime.accountApi.bankingDraftCommand('readBankStatementDraft', {
        organizationId: organization.id, businessId, intakeId: item.id, revision: item.revision, extractionRunId: item.extraction?.id, offset }) : null}
      loadSource={serverAccount ? loadAccountIntakeSource : null} onFlowStateChange={onFlowStateChange} onDone={close}
      recoveryController={serverAccount ? accountRepositoryRuntime.billingRecovery : null}
      renderSupportReview={serverAccount || sessionMode === 'local' ? ({sourceId,onSaved,onReturn,onOpenOriginal,onFlowStateChange:flow}) => renderBankingIntake({context:{kind:'support',itemId:sourceId,archiveMode:'review',reviewItemIds:[sourceId],onInvoiceSaved:onSaved,onOpenOriginal},businessId,close:onReturn,onFlowStateChange:flow,openArchive:onReturn,openMatching:onReturn}) : null}
      retryConfirmation={serverAccount ? async itemId => {
        const pending = accountRepositoryRuntime.billingRecovery.getSnapshot()?.pending;
        if (publicMode || pending?.path!==`/v1/banking/intake-records/${itemId}/final-confirm` || pending.organizationId!==organization.id || pending.body.businessId!==businessId)
          return {ok:false,error:'Reopen the original statement confirmation before retrying.'};
        return runAccountCommand(()=>accountRepositoryRuntime.billingRecovery.retry(),{successMessage:'Original statement confirmation verified.'});
      } : null}
      onManualAction={async (item,action,payload,key) => {
        if(publicMode)return {ok:false,error:'Banking is read-only in public mode.'};
        if(serverAccount){
          const result=await runAccountCommand(()=>accountRepositoryRuntime.accountApi.bankingDraftCommand('bankStatementManualCommand',item,action,payload),{successMessage:action==='draft'?'Manual statement draft saved. No rows imported.':'Manual statement confirmed; rows remain unmatched.'});
          if(!result.ok)return result;
          const updated=await loadAccountIntakeDetail(item.id,item);
          return updated?{...result,item:updated}:{ok:false,error:'The manual request was saved, but its refreshed source could not be loaded. Reload the original review before another change.'};
        }
        const originalContext=JSON.stringify(bankDraftContext.current);
        const result=await commitLocalBankStatementManual({freshness:localPersistence.beginAtomic(),space:localSpace,getSnapshot:()=>localSnapshotRef.current,userId:profile.id,
          isCurrent:()=>JSON.stringify(bankDraftContext.current)===originalContext&&!bankDraftContext.current.serverAccount&&!bankDraftContext.current.publicMode,
          buildActivity:createActivity,item,action,payload,key,business:businesses.find(candidate=>candidate.id===businessId),organizationId:organization.id,bankingEnabled});
        if(!result.ok)return result;
        return localPersistence.adopt(result, applyLocalCommit);
      }}
      onManualRetry={serverAccount?async item=>{
        const pending=accountRepositoryRuntime.billingRecovery.getSnapshot()?.pending;
        if(publicMode||!pending?.path?.startsWith(`/v1/banking/intake-records/${item.id}/manual-review/`)||pending.organizationId!==organization.id||pending.body.businessId!==businessId)return {ok:false,error:'Reopen the exact saved manual request in its original business.'};
        const result=await runAccountCommand(()=>accountRepositoryRuntime.billingRecovery.retry());
        if(!result.ok)return result;
        return {...result,item:await loadAccountIntakeDetail(item.id,item)};
      }:null}
      onConfirm={async (review,draft) => {
        if (publicMode) return { ok:false,error:'Banking is read-only in public mode.' };
        const choices = Object.values(draft.choices).sort((left,right)=>left.row.position-right.row.position);
        if (serverAccount) return runAccountCommand(() => accountRepositoryRuntime.accountApi.bankingDraftCommand('finalizeBankStatement',review.item,review.page,choices),
          { successMessage:'Statement and selected matches confirmed; unmatched rows remain pending.' });
        const originalContext = JSON.stringify(bankDraftContext.current);
        const result = await commitLocalBankStatementFinalize({ freshness:localPersistence.beginAtomic(),space:localSpace,getSnapshot:()=>localSnapshotRef.current,userId:profile.id,
          isCurrent:()=>JSON.stringify(bankDraftContext.current)===originalContext && !bankDraftContext.current.serverAccount && !bankDraftContext.current.publicMode,
          buildActivity:createActivity,itemId:review.item.id,version:draft.version,choices,key:draft.key,
          business:businesses.find(item=>item.id===businessId),organizationId:organization.id,profile,publicMode,bankingEnabled });
        if (!result.ok) return result;
        return localPersistence.adopt(result, applyLocalCommit);
      }} />;
    return <DocumentIntakeView onOpenOriginal={context.onOpenOriginal} onReviewBack={context.kind === 'support' ? close : null} onInvoiceSaved={context.onInvoiceSaved} purchaseSaveRecoveryController={serverAccount ? accountRepositoryRuntime.billingRecovery : null} retryInvoiceSave={serverAccount ? record => {
      const pending=accountRepositoryRuntime.billingRecovery.getSnapshot()?.pending;
      if(publicMode || pending?.key!==record.key || pending.organizationId!==organization.id || (pending.purchaseReview?.businessId || pending.body?.businessId)!==businessId) return Promise.resolve({ok:false,error:'Reopen the original supporting invoice in its business before retrying.'});
      return runAccountCommand(()=>accountRepositoryRuntime.billingRecovery.retry());
    } : null} key={`banking/${businessId}/${context.kind}/${context.itemId || "new"}`} explicitImportFlow={context.kind === "statement"} onFlowStateChange={onFlowStateChange} businesses={scopedBusinesses} scopeLabel={scopedBusinesses[0]?.name || "Issuing business required"} clients={accountClients} purchaseVendors={accountPurchaseVendors} items={scopedItems} relatedReviewItems={businessReviewItems} expectations={scopedWorkspace.documentExpectations} sourceSummaries={dailyAccountingSources.filter((item) => !item.businessId || item.businessId === businessId)} initialItemId={context.itemId || ""} initialMode={context.archiveMode || "summary"} reviewItemIds={context.reviewItemIds || []} uploadFiles={uploadIntakeFiles} uploadRepository={serverAccount ? accountRepositoryRuntime.sourceUploads : null} onUploadReceived={serverAccount ? async () => { if (intakePage.loaded) await intakePage.reload(); } : null} sourceMode={serverAccount ? "account" : sessionMode === "local" ? "local" : ""} loadDetail={serverAccount ? loadAccountIntakeDetail : null} loadStatementPage={serverAccount ? (item, offset) => accountRepositoryRuntime.reads.intake.statementReview({ organizationId: organization.id, businessId: item.businessId, intakeId: item.id, revision: item.revision, extractionRunId: item.extraction?.id, offset }) : null} loadHistory={serverAccount ? loadAccountIntakeHistory : null} loadEvidence={serverAccount ? loadAccountIntakeEvidence : null} loadSourcePreview={serverAccount ? loadAccountIntakeSource : null} loadExpectation={serverAccount ? loadAccountMonthlyCloseChecklist : null} loadPeriodProposal={serverAccount && !publicMode ? loadIntakePeriodProposal : null} confirmFilingPeriod={serverAccount && !publicMode ? confirmIntakeFilingPeriod : null} updateItem={updateIntakeItem} saveInvoice={serverAccount || sessionMode === 'local' ? saveIntakePurchaseInvoice : null} ediReviewApi={invoiceEdiReviewApi} classifyItem={classifyIntakeItem} organizationId={organization.id} prepareEdiExport={serverAccount ? prepareEdiExportBatch : null} markEdiExportDownloaded={serverAccount ? markEdiExportDownloaded : null} retryItem={serverAccount ? retryIntakeProcessing : null} markReady={markIntakeReady} acceptItem={acceptIntakeItem} removeItem={removeIntakeItem} exportDiagnostic={exportIntakeDiagnostic} saveExpectation={saveDocumentExpectation} openArchive={openArchive} openBanking={(intakeId) => { if (context.kind === "statement") { if (openMatching) openMatching(intakeId); else close(); openBankingFromIntake(intakeId); } else close(); }} bankingEnabled={bankingEnabled} readOnly={publicMode} />;
  };

  const handleExportData = async (format) => {
    if (serverAccount) { setToast('Local backup files do not contain the complete account. Export invoices or reports from their records.'); return; }
    if (!canManage) {
      setToast("Only an owner or administrator can export a complete workspace backup");
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode to export backups containing original records");
      return;
    }
    try {
      const entry = createActivity({ action: "Portable backup exported", detail: `Exported complete ${format.toUpperCase()} backup`, entityType: "data", entityId: organization.id });
      const data = { ...localSnapshot, activityLog: [entry, ...activityLog] };
      setActivityLog(data.activityLog);
      const filename = await exportBackup(data, format);
      setToast(`Downloaded ${filename}`);
    } catch (error) {
      console.error(error);
      setToast("The backup could not be created. Please try again.");
    }
  };

  const handleInvoiceRegisterExport = async (client = null, year = null) => {
    if (publicMode) {
      setToast("Turn off public mode before exporting original financial records");
      return;
    }
    try {
      if (serverAccount) {
        const prepared = await accountRepositoryRuntime.commands.prepareInvoiceRegisterExport({
          businessId: workspaceMode === "admin" ? null : accountBusinessId,
          clientId: client?.id || null,
          year,
          includeArchived: false,
        }, organization.id);
        if (!prepared?.ok) {
          setToast(prepared?.error || "The server could not prepare a complete invoice export.");
          return;
        }
        const exportRecord = prepared.data;
        const file = await accountRepositoryRuntime.reads.invoiceRegisterExports.file({
          organizationId: organization.id,
          exportId: exportRecord.id,
        });
        if (!file?.ok) throw new Error(file?.error || "The prepared invoice export file could not be loaded.");
        if (Number(file.data?.recordCount) !== Number(exportRecord.rowCount || 0)
          || file.data?.manifestSha256 !== exportRecord.manifestSha256) {
          throw new Error("The prepared invoice export file did not match its manifest.");
        }
        const filename = saveAccountFile(file.data);
        const downloaded = await accountRepositoryRuntime.commands.markInvoiceRegisterExportDownloaded(
          exportRecord.id,
          organization.id,
        );
        if (!downloaded?.ok) {
          setToast(`Downloaded ${filename}, but its download audit could not be recorded.`);
          return;
        }
        setToast(`Downloaded ${filename}`);
        return;
      }
      const scopedExportInvoices = workspaceMode === "admin" ? organizationInvoices : dailyInvoices;
      const exportInvoices = year ? scopedExportInvoices.filter((invoice) => invoiceYear(invoice) === String(year)) : scopedExportInvoices;
      const exportBusinesses = workspaceMode === "admin" ? organizationBusinesses : dailyBusinesses;
      const result = await downloadInvoiceRegister({
        invoices: exportInvoices,
        businesses: exportBusinesses,
        client,
      });
      recordActivity({
        action: client ? "Client statement exported" : "Invoice register exported",
        detail: client ? `Exported the invoice statement for ${client.name}` : `Exported ${result.recordCount} invoice records`,
        entityType: client ? "client" : "data",
        entityId: client?.id || organization.id,
      });
      setToast(`Downloaded ${result.filename}`);
    } catch (error) {
      console.error(error);
      setToast(client ? "The client statement could not be created." : "The invoice register could not be created.");
    }
  };

  const handleImportData = async (file) => {
    if (!file) return;
    if (serverAccount) { setToast('Local workspace backups cannot replace a connected account.'); return; }
    if (!canManage) {
      setToast("Only an owner or administrator can restore a complete workspace backup");
      return;
    }
    if (publicMode) {
      setToast("Turn off public mode before restoring original records");
      return;
    }
    const importOwner = sessionMode === "local" ? localPersistence.beginAtomic() : null;
    try {
      const data = await importBackup(file);
      const organizationId = data.workspaces[0]?.id;
      if (!organizationId) throw new Error("Backup does not contain an organization.");
      const entry = createActivity({ action: "Portable backup restored", detail: `Imported ${file.name}`, entityType: "data", entityId: organizationId, workspaceId: organizationId });
      const nextData = { ...data, activityLog: [entry, ...data.activityLog] };
      if (sessionMode === "local") {
        const result = await importOwner.withLock(() => importOwner.write(nextData));
        if (!result.ok) throw new Error("The backup was valid, but this browser could not safely save it.");
        const adopted = localPersistence.adopt(result, applyLocalCommit);
        if (!adopted.ok) throw new Error(adopted.error);
      } else applyData(nextData);
      if (authSession.organizationId !== organizationId) {
        const result = prototypeIdentityRepository.replaceOrganization(organizationId);
        if (!result.ok) throw new Error(result.error);
        setAuthSession(result.session);
      }
      setToast(`Restored ${file.name}`);
      setActiveView("history");
    } catch (error) {
      console.error(error);
      setToast(error.message || "The backup could not be restored.");
    }
  };

  if (!authReady || accountWorkspaceSelection?.loading) return <div className="is2-login" aria-busy="true"><header className="is2-login__topbar"><Brand compact /><span><ShieldIcon size={15} /> Verifying account</span></header><main className="is2-login__main"><section className="is2-login__panel"><header><p className="is2-eyebrow">Invoice Studio</p><h2>Opening your workspace.</h2><span>Verifying your organization and managed-business access...</span></header></section></main></div>;
  if (accountWorkspaceSelection) return <React.Suspense fallback={<p role="status">Loading workspaces...</p>}><AccountWorkspaceChooser key={accountWorkspaceSelection.userId} identity={accountWorkspaceSelection} directory={accountRepositoryRuntime.workspaceDirectory} notificationDevices={accountRepositoryRuntime.notificationDevices} onSelected={openSelectedAccountWorkspace} onSignOut={signOut} /></React.Suspense>;
  if (portalUser && !portalWorkspaceView) return <div className="is2-login" aria-busy="true"><header className="is2-login__topbar"><Brand compact /><span><ShieldIcon size={15} /> Business portal</span></header><main className="is2-login__main"><section className="is2-login__panel"><header><p className="is2-eyebrow">Your business</p><h2>Opening the records shared with you.</h2><span>Loading published invoices, deadlines, and reports...</span></header></section></main></div>;
  if (!organization) return <>
    <LoginPage accountAvailable={Boolean(accountRepositoryRuntime)} invitationIntent={accountInvitation} onDismissInvitation={() => { clearAccountInvitationIntent(); setAccountInvitation(null); }} onSignIn={signIn} onRecover={recoverAccount} onUseLocal={useLocal} onUsePrototypeRole={usePrototypeRole} onUsePersonalLocal={usePersonalLocal} onUseEmptyLocal={useEmptyLocal} hasPersonalWorkspace={Boolean(readLocalData("custom"))} hasEmptyWorkspace={Boolean(readLocalData("empty"))} publicGuestMode={PUBLIC_GUEST_MODE} />
    {localOnboardingOpen ? <LocalWorkspaceOnboarding mode={localOnboardingOpen} close={() => setLocalOnboardingOpen(false)} complete={localOnboardingOpen === "empty" ? completeEmptyLocalOnboarding : completeLocalOnboarding} skip={localOnboardingOpen === "empty" ? () => completeEmptyLocalOnboarding({}) : undefined} /> : null}
  </>;

  if (portalUser) return <>
    <React.Suspense fallback={<div className="is2-login" aria-busy="true"><header className="is2-login__topbar"><Brand compact /><span><ShieldIcon size={15} /> Business portal</span></header><main className="is2-login__main"><section className="is2-login__panel"><header><p className="is2-eyebrow">Your business</p><h2>Opening the records shared with you.</h2><span>Loading published invoices, deadlines, and reports...</span></header></section></main></div>}>
    <BusinessPortalView
      notificationDevices={serverAccount ? accountRepositoryRuntime.notificationDevices : null}
      accountUserId={authSession?.userId}
      organization={organization}
      business={portalBusiness}
      businesses={scopedWorkspace.businesses}
      profile={profile}
      invoices={scopedWorkspace.invoices}
      bankTransactions={scopedWorkspace.bankTransactions}
      publications={scopedWorkspace.portalPublications}
      documentRequests={scopedWorkspace.portalDocumentRequests}
      notifications={scopedWorkspace.portalNotifications}
      financialSnapshot={portalFinancialSnapshot}
      pagination={portalPagination}
      intakeItems={scopedWorkspace.intakeItems}
      signOut={signOut}
      switchAccountWorkspace={switchAccountWorkspace}
      uploadDocuments={uploadPortalDocuments}
      uploadRecovery={serverAccount ? accountRepositoryRuntime.businessPortal : null}
      onUploadsReceived={recordPortalUploads}
      publicMode={publicMode}
      loadPage={serverAccount ? accountRepositoryRuntime.businessPortal.loadPage : null}
      loadInvoice={serverAccount ? accountRepositoryRuntime.businessPortal.loadInvoice : null}
      loadInvoicePayments={serverAccount ? accountRepositoryRuntime.businessPortal.loadInvoicePayments : null}
      loadFinancialSnapshot={serverAccount ? accountRepositoryRuntime.businessPortal.loadOverview : null}
      downloadArtifact={serverAccount ? accountRepositoryRuntime.businessPortal.downloadArtifact : null}
      switchBusiness={serverAccount ? switchPortalBusiness : null}
      markNotificationRead={serverAccount ? markPortalNotificationRead : null}
      notify={setToast}
    />
    </React.Suspense>
    {toast ? <div className="is2-toast" role="status" aria-live="polite">{toast}</div> : null}
  </>;

  const organizationType = PUBLIC_GUEST_MODE && sampleWorkspace ? "Guest preview" : sessionMode === "local" ? "Stored on this device" : "Business organization";
  const organizationMembers = serverAccount ? accountAdminMembers : teamMembers.filter((member) => member.workspaceId === organization.id);
  const operationMembers = organizationMembers.filter((member) => member.id === profile.id || member.businessIds?.includes("*") || dailyBusinesses.some((business) => member.businessIds?.includes(business.id)));
  const accountAdministrationSummary = serverAccount ? accountAdministrationOverview : {
    businessCount: organizationBusinesses.filter((business) => !business.archived).length,
    activeMemberCount: organizationMembers.filter((member) => member.status === "Active").length,
    pendingInvitationCount: organizationMembers.filter((member) => member.status === "Invited").length,
    managerCount: organizationMembers.filter((member) => member.role === "Manager" && member.status === "Active").length,
  };
  const administrationContent = (() => {
    if (adminSection === "history" && canManage) return <AccountPagedView page={serverAccount ? auditPage : null} noun="changes"><History entries={accountAuditEntries} openInvoice={openEditor} invoices={organizationInvoices} /></AccountPagedView>;
    if (canManage) return <>
      <React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Owner workspace" title="Administration" description="Loading managed businesses and access controls..." /></section>}><AdminView businessContactManagement={businessContactManagement} teamManagement={teamManagement} businesses={serverAccount ? accountAdminBusinessesForView : organizationBusinesses} clients={serverAccount ? accountClients : organizationClients} members={serverAccount ? accountAdminMembersForView : organizationMembers} summary={accountAdministrationSummary} profile={profile} saveMember={saveTeamMember} removeMember={removeTeamMember} openIssuer={(business) => openIssuerEditor(business, "managed")} openClient={openClientEditor} saveBusiness={saveBusiness} removeBusiness={removeBusiness} activeSection={adminSection} onSectionChange={setAdminSection} showTabs={false} requestDocuments={prepareDocumentRequest} initialBusinessId={focusedBusinessId} selectedBusinessId={selectedAdminBusinessId} onSelectBusiness={setFocusedBusinessId} selectedMemberId={selectedAdminMemberId} onSelectMember={setFocusedAdminMemberId} serverManaged={serverAccount} businessDetailState={serverAccount ? { ...accountAdminBusinessDetailState, reload: () => setAccountAdminBusinessDetailRefreshKey((current) => current + 1) } : null} memberDetailState={serverAccount ? { ...accountAdminMemberDetailState, reload: () => setAccountAdminMemberDetailRefreshKey((current) => current + 1) } : null} loadBusinessDetailCollection={serverAccount ? loadAdminBusinessDetailCollection : null} loadMemberDetailCollection={serverAccount ? loadAdminMemberDetailCollection : null} returnToSearch={recordReturnContext && adminSection === "businesses" ? returnToSearch : null} loadPortalMembers={serverAccount ? loadBusinessPortalMembers : null} revokePortalMember={serverAccount ? revokeBusinessPortalMember : null} loadPortalSubjects={serverAccount ? loadBusinessPortalSubjects : null} publishPortalArtifact={serverAccount ? publishBusinessPortalArtifact : null} /></React.Suspense>
      {serverAccount ? <div className="is2-admin-page-statuses">
        <AccountPageStatus page={adminBusinessPage} noun="managed businesses" />
        <AccountPageStatus page={clientPage} noun="invoice customers" />
        <AccountPageStatus page={adminMemberPage} noun="workspace members" />
        <AccountPageStatus page={adminInvitationPage} noun="pending invitations" />
        {accountAdministrationOverviewError ? <div className="is2-account-page-status is-error" role="alert"><span>{accountAdministrationOverviewError}</span><button type="button" className="is2-button is2-button--quiet" onClick={loadAccountAdministrationOverview}>Try again</button></div> : null}
      </div> : null}
    </>;
    return null;
  })();
  const mobileLeadingIds = mobileExperience === "minimal"
    ? ["overview", "invoices"]
    : ["overview", enabledWorkNavItems.length ? "work" : "invoices"];
  const mobileTrailingId = mobileExperience === "minimal"
    ? navigationPreferences.clients ? "clients" : null
    : enabledWorkNavItems.length ? "invoices" : navigationPreferences.clients ? "clients" : null;
  const content = editorOpen ? <PrivacyProvider enabled={publicMode && editorMode !== "edit"} clients={accountClients} invoices={accountInvoices}><InvoiceWorkspace key={`${selectedInvoice?.id || "new-invoice"}:${editorMode}:${selectedInvoice?.revision || 0}`} closeEditor={closeEditor} notify={setToast} initialDraft={editorDraft} invoice={selectedInvoice} mode={editorMode} emailRequested={!publicMode && invoiceEmailIntent?.workspaceId === organization.id && invoiceEmailIntent?.issuerId === selectedInvoice?.issuerId && invoiceEmailIntent?.invoiceId === selectedInvoice?.id} editInvoice={editInvoice} saveInvoice={saveInvoice} reloadInvoice={serverAccount ? reloadInvoiceEditor : null} updateInvoiceStatus={updateInvoiceStatus} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} reuseInvoice={reuseInvoice} businesses={organizationBusinesses} clients={accountClients} loadBusinesses={serverAccount ? loadAccountBusinesses : null} loadBusiness={serverAccount ? loadAccountBusiness : null} loadClients={serverAccount ? loadInvoiceClientOptions : null} loadClient={serverAccount ? loadInvoiceClientOption : null} getNextInvoiceNumber={getNextInvoiceNumber} activityLog={accessibleActivityLog} documentPreferences={organization} accountingMappings={organizationAccountingMappings} historicalInvoices={accountInvoices} ledgerPostingEnabled={ledgerAccessEnabled} postLedgerSource={postSalesInvoiceLedger} postLedgerPayment={postInvoicePaymentLedger} loadPaymentReview={loadInvoicePaymentReview} loadCashAccounts={loadPaymentCashAccounts} invoiceEmail={serverAccount ? { organizationId: organization.id, api: accountRepositoryRuntime.invoiceEmail } : null} invoicePortal={serverAccount ? { organizationId: organization.id, commands: accountRepositoryRuntime.portalCommands, artifacts: accountRepositoryRuntime.portalArtifacts } : null} downloadAccountDocument={serverAccount ? getAccountInvoiceDocument : null} /></PrivacyProvider> : {
    overview: <Overview downloadAccountDocument={serverAccount ? getAccountInvoiceDocument : null} navigate={navigate} openInvoice={openEditor} reuseInvoice={reuseInvoice} notify={setToast} invoices={accountInvoices} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} updateInvoiceStatus={updateInvoiceStatus} businesses={dailyBusinesses} clients={accountClients} showIssuer={showIssuer} profile={profile} scopeLabel={dailyScopeLabel} collectionHistory={dailyCollectionHistory} reportingCurrency={organization.currency || "EUR"} loadOverview={serverAccount ? accountRepositoryRuntime?.reads?.overview?.get : null} organizationId={organization.id} businessId={accountBusinessId} />,
    work: workSection === "calendar" ? <React.Suspense fallback={<section className="is2-view" aria-busy="true">{t("Loading...")}</section>}><WorkCalendar key={JSON.stringify([serverAccount, accountScope, organization?.id, accountBusinessId])} workspaceKey={JSON.stringify([serverAccount, accountScope, organization?.id, accountBusinessId])} organization={organization} businesses={dailyBusinesses} members={operationMembers} profile={profile} tasks={dailyWorkTasks} reminders={dailyWorkReminders} accountMode={serverAccount} loadPage={serverAccount ? accountRepositoryRuntime?.reads?.workCalendar?.list : null} loadBusinesses={serverAccount ? loadAccountBusinesses : null} initialBusinessId={accountBusinessId} refreshKey={workCalendarRefreshKey} viewState={workCalendarState} onViewStateChange={setWorkCalendarState} onNavigate={navigateWork} enabledSections={enabledWorkNavItems.map(item => item.id)} onOpenRecord={openWorkFromSearch} saveTask={saveWorkTask} saveReminder={saveWorkReminder} publicMode={publicMode} /></React.Suspense> : <AccountPagedView page={serverAccount ? workSection === "tasks" ? taskPage : workSection === "reminders" ? reminderPage : routinePage : null} noun={workSection}>{enabledWorkNavItems.some(item => item.id === "calendar") ? <button type="button" className="is2-button is2-button--quiet" data-cy="work-back-calendar" onClick={() => { setSearchWorkTarget(null); setWorkSection("calendar"); }}>{t("Back to Work calendar")}</button> : null}<React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Daily work" title="Work" description="Preparing tasks, reminders, and recurring work..." /></section>}><WorkView initialWorkItem={searchWorkTarget} organization={organization} businesses={dailyBusinesses} routineBusinesses={organizationBusinesses} members={operationMembers} routineMembers={organizationMembers} profile={profile} tasks={accountTasks} sharedTaskOrder={serverAccount && sharedTaskOrder} onTaskOrderChange={serverAccount ? setSharedTaskOrder : null} moveTask={serverAccount ? moveWorkTask : null} orderBusy={serverAccount && (taskPage.loading || Boolean(taskPage.error))} reminders={accountReminders} routines={accountRoutines} routineOccurrences={accountRoutineOccurrences} routineOccurrencesPage={serverAccount ? routineOccurrencePage : null} activities={dailyWorkActivities} saveTask={saveWorkTask} saveReminder={saveWorkReminder} addNote={addWorkItemNote} loadAssignees={serverAccount ? loadWorkAssignees : null} loadRoutineAssignees={serverAccount ? loadRoutineAssignees : null} loadRoutineBusinesses={serverAccount ? loadAccountBusinesses : null} loadTaskDetail={serverAccount ? loadWorkTaskDetail : null} loadReminderDetail={serverAccount ? loadWorkReminderDetail : null} loadActivityHistory={loadWorkActivityHistory} setChecklistItemStatus={changeWorkChecklistItem} loadRoutine={loadWorkRoutine} loadRoutineCollection={loadWorkRoutineCollection} saveRoutine={saveWorkRoutine} setRoutineStatus={changeWorkRoutineStatus} generateRoutinePeriod={generateWorkRoutinePeriod} skipRoutinePeriod={skipWorkRoutinePeriod} canManageRoutines={canManage} section={workSection} enabledSections={enabledWorkNavItems.map((item) => item.id)} onSectionChange={setWorkSection} readOnly={publicMode} publicMode={publicMode} /></React.Suspense></AccountPagedView>,
    invoices: <AccountPagedView page={serverAccount ? invoicePage : null} noun="invoices"><Invoices downloadAccountDocument={serverAccount ? getAccountInvoiceDocument : null} openInvoice={openEditor} reuseInvoice={reuseInvoice} openCreate={startCreate} openPurchases={() => navigate("purchases")} openScope={() => navigate("more")} notify={setToast} invoices={accountInvoices} archiveInvoice={archiveInvoice} restoreInvoice={restoreInvoice} updateInvoiceStatus={updateInvoiceStatus} reviewImportedPayment={reviewImportedPayment} businesses={dailyBusinesses} clients={accountClients} showIssuer={showIssuer} showMobileScope={organizationBusinesses.length > 1} scopeLabel={dailyScopeLabel} /></AccountPagedView>,
    clients: <AccountPagedView page={serverAccount ? clientPage : null} noun="clients"><Clients openCreate={startCreate} openInvoice={openEditor} openClient={openClientEditor} exportStatement={handleInvoiceRegisterExport} invoices={serverAccount ? accountClientInvoices : accountInvoices} clients={serverAccount ? accountClientsForClientView : accountClients} businesses={dailyBusinesses} updateInvoiceStatus={updateInvoiceStatus} prepareInvoiceReminder={prepareInvoiceReminder} initialClientId={focusedClientId} selectedClientId={selectedClientWorkspaceId} onSelectClient={setFocusedClientId} invoicePage={serverAccount ? clientInvoicePage : null} detailState={serverAccount ? { ...accountClientDetailState, reload: () => setAccountClientDetailRefreshKey((current) => current + 1) } : null} loadDetailCollection={serverAccount ? loadClientDetailCollection : null} serverManaged={serverAccount} returnToSearch={recordReturnContext ? returnToSearch : null} /></AccountPagedView>,
    services: <AccountPagedView page={serverAccount ? servicePage : null} noun="services"><Services openCreate={startCreate} editService={openServiceEditor} duplicateService={service => openServiceEditor(service, true)} archiveService={archiveServiceRecord} services={accountServices.filter(service => (includeArchivedServices || !service.archived) && (serverAccount || `${service.name} ${service.detail}`.toLowerCase().includes(serviceQuery.toLowerCase())))} readOnly={publicMode} query={serviceQuery} onQuery={setServiceQuery} includeArchived={includeArchivedServices} onIncludeArchived={setIncludeArchivedServices} /></AccountPagedView>,
    history: <AccountPagedView page={serverAccount ? auditPage : null} noun="changes"><History entries={accountAuditEntries} openInvoice={openEditor} invoices={accountInvoices} /></AccountPagedView>,
    reports: <React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Performance" title="Reports" description="Preparing authoritative financial signals..." /></section>}><ReportsView portal={serverAccount ? { businesses: organizationBusinesses, loadBusinesses: loadAccountBusinesses, loadBusiness: loadAccountBusiness, commands: accountRepositoryRuntime.portalCommands, artifacts: accountRepositoryRuntime.portalArtifacts, recoveryController: accountRepositoryRuntime.billingRecovery, save: savePortalPublication, target: portalPublicationTarget } : null} invoices={accountInvoices} exportReport={(year) => handleInvoiceRegisterExport(null, year)} loadReport={serverAccount ? accountRepositoryRuntime?.reads?.financialReport?.get : null} organizationId={organization.id} businessId={accountBusinessId} reportingCurrency={organization.currency || "EUR"} /></React.Suspense>,
    archive: <React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Accounting records" title="Company archive" description="Preparing the company evidence index..." /></section>}><CompanyArchiveView businesses={organizationBusinesses} sourceSummaries={organizationAccountingSources} intakeItems={accountIntakeItems} openIntake={openDocumentIntake} accountArchive={serverAccount ? { organizationId: organization.id, ...accountRepositoryRuntime?.reads?.archive } : null} /></React.Suspense>,
    intake: <><React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Accounting tools" title="Document intake" description="Preparing source review tools..." /></section>}><DocumentIntakeView queueFilter={intakeQueueView} onQueueFilterChange={view => setIntakeQueueFilter({ scope: intakeQueueScope, view })} queuePage={serverAccount ? intakePage : null} key={`${accountScope || sessionMode}/${effectiveBusinessScopeId}`} businesses={dailyBusinesses} businessPage={serverAccount && !accountBusinessId ? businessPage : null} scopeLabel={dailyScopeLabel} clients={accountClients} purchaseVendors={accountPurchaseVendors} items={serverAccount ? accountIntakeItems : dailyIntakeItems} expectations={scopedWorkspace.documentExpectations} sourceSummaries={dailyAccountingSources} initialItemId={focusedIntakeItemId} initialMode={focusedIntakeMode} uploadFiles={uploadIntakeFiles} uploadRepository={serverAccount ? accountRepositoryRuntime.sourceUploads : null} onUploadReceived={serverAccount ? async () => { if (intakePage.loaded) await intakePage.reload(); } : null} sourceMode={serverAccount ? "account" : sessionMode === "local" ? "local" : ""} loadDetail={serverAccount ? loadAccountIntakeDetail : null} loadStatementPage={serverAccount ? (item, offset) => accountRepositoryRuntime.reads.intake.statementReview({ organizationId: organization.id, businessId: item.businessId, intakeId: item.id, revision: item.revision, extractionRunId: item.extraction?.id, offset }) : null} loadHistory={serverAccount ? loadAccountIntakeHistory : null} loadEvidence={serverAccount ? loadAccountIntakeEvidence : null} loadSourcePreview={serverAccount ? loadAccountIntakeSource : null} loadExpectation={serverAccount ? loadAccountMonthlyCloseChecklist : null} loadPeriodProposal={serverAccount && !publicMode ? loadIntakePeriodProposal : null} confirmFilingPeriod={serverAccount && !publicMode ? confirmIntakeFilingPeriod : null} updateItem={updateIntakeItem} saveInvoice={serverAccount || sessionMode === 'local' ? saveIntakePurchaseInvoice : null} ediReviewApi={invoiceEdiReviewApi} classifyItem={classifyIntakeItem} organizationId={organization.id} ediRepository={serverAccount ? accountRepositoryRuntime.reads.ediExports : null} prepareMonthlyEdi={serverAccount ? accountRepositoryRuntime.commands.prepareEdiExport : null} prepareEdiExport={serverAccount ? prepareEdiExportBatch : null} markEdiExportDownloaded={serverAccount ? markEdiExportDownloaded : null} retryItem={serverAccount ? retryIntakeProcessing : null} markReady={markIntakeReady} acceptItem={acceptIntakeItem} removeItem={removeIntakeItem} exportDiagnostic={exportIntakeDiagnostic} saveExpectation={saveDocumentExpectation} requestDocuments={prepareDocumentRequest} openArchive={() => navigate("archive")} openDeclarations={() => navigate("declarations")} openBanking={openBankingFromIntake} bankingEnabled={bankingEnabled} readOnly={publicMode} /></React.Suspense><AccountPageStatus page={serverAccount ? purchaseVendorPage : null} noun="purchase vendors" /></>,
    purchases: <React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Payables" title="Purchase invoices" description="Preparing the vendor ledger..." /></section>}><PurchasesView showLedgerSwitch={false} localWorkspace={!serverAccount} openSource={id => openDocumentIntake(id, 'review')} renderMonthlyEdi={({ businessId, close }) => serverAccount
      ? <PurchaseAccountMonthEdi key={accountScope} organizationId={organization.id} businesses={dailyBusinesses} initialBusinessId={businessId}
        repository={accountRepositoryRuntime.reads.ediExports} purchaseRepository={accountRepositoryRuntime.reads.purchaseDocuments}
        loadSource={loadAccountIntakeDetail}
        prepare={accountRepositoryRuntime.commands.prepareEdiExport} markDownloaded={markEdiExportDownloaded} close={close}
        openSource={id => openDocumentIntake(id, 'review')} readOnly={publicMode} />
      : <PurchaseMonthEdi key={`${localSpace}/${organization.id}/${effectiveBusinessScopeId}`} organizationId={organization.id} businesses={dailyBusinesses}
        initialBusinessId={businessId} documents={localPurchases} sources={dailyIntakeItems} close={close} openSource={id => openDocumentIntake(id, 'review')}
        readOnly={publicMode || sessionMode !== 'local'} download={async options => {
          const captured = purchaseInvoiceSaveContextRef.current;
          const { readLocalDataStrict } = await import('./dataPortability');
          const { purchaseMonthEdi } = await import('./purchaseMonthEdi');
          const { downloadPurchaseMonthEdi } = await import('./invoiceImportWorkbook');
          const activeBusinessId = localPurchases.find(doc => doc.id === options.plan.entries[0]?.id)?.businessId;
          const canDownload = () => {
            try {
              const current = purchaseInvoiceSaveContextRef.current;
              if (!options.canDownload() || current.serverAccount || current.publicMode || current.localSpace !== captured.localSpace
                || current.organizationId !== captured.organizationId || current.profile.id !== captured.profile.id) return false;
              const { data } = readLocalDataStrict(localSpace);
              if (data.profile?.id !== captured.profile.id || data.profile.publicMode || !canAccessBusiness(data.profile, activeBusinessId)
                || !canUseAccountingOperation(data.profile, 'purchases')) return false;
              const plan = purchaseMonthEdi({ organizationId: organization.id, businessId: activeBusinessId, period: options.period,
                documents: data.purchaseDocuments, sources: data.intakeItems, postings: Object.fromEntries(options.plan.entries.map(row => [row.id, row.posting])) });
              return plan.canExport && JSON.stringify(plan.entries.map(row => [row.id, row.values, row.posting])) === JSON.stringify(options.plan.entries.map(row => [row.id, row.values, row.posting]));
            } catch { return false; }
          };
          return downloadPurchaseMonthEdi({ ...options, canDownload });
        }} />
    } downloadWorkbook={serverAccount ? async (detail, stillSelected) => {
      const scope=accountScope, user=authSession?.userId;
      const isCurrent=()=>stillSelected() && purchaseInvoiceSaveContextRef.current.accountScope===scope && purchaseInvoiceSaveContextRef.current.userId===user && !purchaseInvoiceSaveContextRef.current.publicMode;
      const {downloadAccountPurchaseWorkbook}=await import('./backend/accountPurchaseWorkbook');
      const {downloadInvoiceImportWorkbook}=await import('./invoiceImportWorkbook');
      return downloadAccountPurchaseWorkbook({document:detail.accountEditSnapshot?.document,loadDocument:(org,id)=>accountRepositoryRuntime.accountApi.getPurchaseDocument(org,id),loadIntake:loadAccountIntakeDetail,isCurrent,download:downloadInvoiceImportWorkbook});
    } : sessionMode === 'local' ? async (detail, stillSelected) => {
      const captured=purchaseInvoiceSaveContextRef.current;
      const isCurrent=()=>stillSelected() && !purchaseInvoiceSaveContextRef.current.serverAccount && !purchaseInvoiceSaveContextRef.current.publicMode
        && purchaseInvoiceSaveContextRef.current.localSpace===captured.localSpace && purchaseInvoiceSaveContextRef.current.organizationId===captured.organizationId
        && purchaseInvoiceSaveContextRef.current.effectiveBusinessScopeId===captured.effectiveBusinessScopeId && purchaseInvoiceSaveContextRef.current.profile.id===captured.profile.id;
      const {downloadLocalPurchaseWorkbook}=await import('./repositories/localPurchaseInvoiceRepository');
      const {downloadInvoiceImportWorkbook}=await import('./invoiceImportWorkbook');
      return downloadLocalPurchaseWorkbook({document:detail.localSnapshot,space:localSpace,userId:captured.profile.id,isCurrent,download:downloadInvoiceImportWorkbook});
    } : null} includeArchived={includeArchivedPurchases} onIncludeArchivedChange={enabled => setPurchaseArchiveFilter({ scope: purchaseFilterScope, enabled })} archiveDocument={serverAccount ? archivePurchaseDocument : null} pageStatus={<><AccountPageStatus page={serverAccount ? purchaseDocumentPage : null} noun="purchase records" /><AccountPageStatus page={serverAccount ? purchaseVendorPage : null} noun="purchase vendors" /></>} supplierRepository={serverAccount ? accountRepositoryRuntime.reads.purchaseVendors : null} selectedBusinessId={accountBusinessId} initialVendor={focusedPurchaseVendor} saveVendor={savePurchaseVendor} archiveVendor={archivePurchaseVendor} loadBusiness={loadAccountBusiness} loadBusinesses={loadAccountBusinesses} organization={organization} businesses={dailyBusinesses} vendors={accountPurchaseVendors} documents={serverAccount ? accountPurchaseDocuments : localPurchases} initialDocumentId={focusedPurchaseId} loadDetail={serverAccount ? loadAccountPurchaseDetail : null} loadHistory={serverAccount ? loadAccountPurchaseHistory : null} saveDocument={savePurchaseDocument} recoveryController={serverAccount ? accountRepositoryRuntime.billingRecovery : null} reviewDocument={reviewPurchaseDocument} recordPayment={recordPurchasePayment} reversePayment={reversePurchasePayment} openSales={() => navigate("invoices")} openIntake={() => openDocumentIntake()} readOnly={publicMode || !serverAccount} exportReadOnly={publicMode || (!serverAccount && sessionMode !== 'local')} intakeReadOnly={publicMode} ledgerPostingEnabled={ledgerAccessEnabled} postLedgerSource={postPurchaseDocumentLedger} postLedgerPayment={postPurchasePaymentLedger} loadPaymentReview={loadPurchasePaymentReview} loadCashAccounts={loadPaymentCashAccounts} notify={setToast} /></React.Suspense>,
    banking: <AccountPagedView page={serverAccount ? bankTransactionPage : null} noun="transactions"><React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Accounting tools" title="Banking" description="Preparing statement review and reconciliation..." /></section>}><BankingView key={accountScope} notice={toast} renderIntake={renderBankingIntake} renderDocumentRequests={renderBankDocumentRequests} renderArchive={renderBankingArchive} selectedBusinessId={serverAccount ? selectedBankingBusinessId : undefined} onSelectedBusinessIdChange={serverAccount ? changeBankingBusiness : undefined} transactionPage={serverAccount ? bankTransactionPage : null} organization={organization} businesses={dailyBusinesses} enabled={bankingEnabled} readOnly={publicMode} statements={accountBankStatements} statementPage={serverAccount ? bankStatementPage : null} selectedStatementId={bankStatementFilterId} setSelectedStatementId={setBankStatementFilterId} transactions={accountBankTransactions} invoices={accountInvoices} purchaseDocuments={accountPurchaseDocuments} reviewDocument={serverAccount ? reviewAccountBankDocument : null} documentRepository={serverAccount ? accountRepositoryRuntime.reads.intake : null} purchaseRepository={serverAccount ? accountRepositoryRuntime.reads.purchaseDocuments : null} invoiceRepository={serverAccount ? accountRepositoryRuntime.reads.invoices : null} intakeItems={accountIntakeItems} members={operationMembers} loadEvidenceOwners={serverAccount ? loadBankEvidenceOwners : null} openIntake={openDocumentIntake} openInvoice={openEditor} openPurchase={openPurchaseRecord} reloadTransactions={serverAccount ? bankTransactionPage.reload : null} loadTransactionDetail={serverAccount ? loadAccountBankTransactionDetail : null} loadTransactionHistory={serverAccount ? loadAccountBankTransactionHistory : null} loadTransactionReview={serverAccount ? loadAccountBankTransactionReview : null} saveReviewDecision={saveBankAccountantReview} confirmReconciliation={confirmBankReconciliation} confirmPurchaseReconciliation={serverAccount ? confirmBankPurchaseReconciliation : null} markNoMatch={markBankTransactionNoMatch} reopenReconciliation={reopenBankReconciliation} updateCategory={updateBankTransactionCategory} updateEvidence={updateTransactionEvidence} confirmEvidenceMatch={confirmOutgoingEvidenceMatch} unlinkEvidenceMatch={unlinkOutgoingEvidenceMatch} requestEvidence={prepareTransactionEvidenceRequest} ledgerPostingEnabled={ledgerAccessEnabled && serverAccount} loadLedgerAccounts={loadBankLedgerAccounts} postLedgerTransaction={postBankTransactionLedger} saveLedgerAccountMapping={saveBankLedgerAccountMapping} /></React.Suspense></AccountPagedView>,
    quickbooks: <AccountPagedView page={serverAccount ? quickBooksExportPage : null} noun="exports"><React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Accounting tools" title="QuickBooks export" description="Preparing reviewed mappings and export tools..." /></section>}><QuickBooksView businesses={dailyBusinesses} invoices={accountInvoices} purchaseDocuments={accountPurchaseDocuments} exports={accountQuickBooksExports} accountingMappings={dailyAccountingMappings} mappingPage={serverAccount ? accountingMappingPage : null} sourceSummaries={dailyAccountingSources} saveMapping={saveAccountingMapping} exportBatch={exportQuickBooksBatch} exportCandidates={serverAccount ? quickBooksCandidatePage.items : null} candidatePage={serverAccount ? quickBooksCandidatePage : null} candidateSourceType={serverAccount ? quickBooksCandidateSourceType : undefined} onCandidateSourceTypeChange={serverAccount ? setQuickBooksCandidateSourceType : undefined} selectedBusinessId={serverAccount ? selectedQuickBooksBusinessId : undefined} onSelectedBusinessIdChange={serverAccount ? setQuickBooksBusinessId : undefined} loadLineAssignments={serverAccount ? loadAccountingLineAssignments : null} loadLineRules={serverAccount ? loadLineAccountingRules : null} saveLineAssignment={serverAccount ? saveAccountingLineAssignment : null} clearLineAssignment={serverAccount ? clearAccountingLineAssignment : null} readOnly={publicMode} /></React.Suspense></AccountPagedView>,
    ledger: <><React.Suspense fallback={<section className="is2-view"><ViewHeading eyebrow="Accounting books" title="Financials" description="Loading the authoritative ledger..." /></section>}><FinancialsView organization={organization} businesses={organizationBusinesses} businessId={accountBusinessId} selectBusiness={changeBusinessScope} serverAccount={serverAccount} accountsPage={ledgerAccountPage} journalsPage={journalEntryPage} fixedAssetsPage={fixedAssetPage} reads={accountRepositoryRuntime?.reads?.ledger} commands={accountRepositoryRuntime?.ledger} readOnly={publicMode} notify={setToast} /></React.Suspense><AccountPageStatus page={serverAccount && accountBusinessId ? ledgerAccountPage : null} noun="ledger accounts" showContinuation={false} /><AccountPageStatus page={serverAccount && accountBusinessId ? journalEntryPage : null} noun="journal entries" showContinuation={false} /><AccountPageStatus page={serverAccount && accountBusinessId ? fixedAssetPage : null} noun="fixed assets" showContinuation={false} /></>,
    declarations: <AccountPagedView page={serverAccount ? declarationPage : null} noun="declaration drafts"><React.Suspense fallback={<section className="is2-view is2-declarations-loading" data-cy="declarations-loading"><ViewHeading eyebrow="Accounting tools" title="Declarations" description="Preparing calculation tools..." /></section>}>{serverAccount && !payrollEmployeePage.loaded && !payrollEmployeePage.error ? <section className="is2-view is2-declarations-loading" data-cy="payroll-roster-loading"><ViewHeading eyebrow="Accounting tools" title="Declarations" description="Loading the employee roster before preparing payroll..." /></section> : <DeclarationsView payrollLedgerTarget={payrollLedgerTarget} payrollLedgerEpoch={payrollLedgerEpoch} organization={organization} businesses={dailyBusinesses} drafts={accountDeclarationDrafts} employeeProfiles={accountPayrollProfiles} loadDraft={serverAccount ? loadDeclarationDraft : null} loadPayrollPreparation={serverAccount ? loadPayrollPreparation : null} onDraftSaved={saveDeclarationDraft} onSaveDraft={serverAccount ? saveDeclarationDraftNow : null} onExportDraft={serverAccount ? exportDeclarationDraft : null} onEmployeeProfileSaved={savePayrollEmployeeProfile} onPayrollReviewed={serverAccount ? reviewPayrollDeclaration : null} onPayrollPosted={serverAccount ? postPayrollDeclaration : null} loadPayrollEvidence={serverAccount ? loadPayrollLedgerEvidence : null} onLoadVatFiling={serverAccount ? loadVatFilingClose : null} onPrepareVatFiling={serverAccount ? prepareVatFilingClose : null} onDownloadVatFiling={serverAccount ? downloadVatFilingClose : null} persistLocally={!serverAccount} readOnly={publicMode} notify={setToast} />}</React.Suspense><AccountPageStatus page={serverAccount ? payrollEmployeePage : null} noun="employee roster records" /></AccountPagedView>,
    admin: administrationContent,
    settings: organizationSettingsManagement ? <React.Suspense fallback={<p role="status">Loading settings...</p>}><AccountSettingsView key={accountScope} management={organizationSettingsManagement} organization={organization} initialSection={settingsSection} editTarget={settingsEditTarget} openIssuers={() => navigateAdmin('businesses')} openClients={() => navigate('clients')} openReports={() => navigate('reports')} openInvoices={() => navigate('invoices')} interfaceLanguage={interfaceLanguage} setInterfaceLanguage={setInterfaceLanguage} /></React.Suspense> : canManage ? <Settings organization={organization} businesses={organizationBusinesses} saveOrganization={saveOrganization} openOrganization={() => setOrganizationOpen(true)} openIssuer={openIssuerEditor} initialSection={settingsSection} getNextInvoiceNumber={getNextInvoiceNumber} sessionMode={sessionMode} exportData={handleExportData} importData={handleImportData} interfaceLanguage={interfaceLanguage} setInterfaceLanguage={setInterfaceLanguage} guestSampleWorkspace={PUBLIC_GUEST_MODE && sampleWorkspace} resetGuestWorkspace={resetGuestWorkspace} /> : <AccountView notificationDevices={serverAccount ? accountRepositoryRuntime.notificationDevices : null} accountUserId={authSession?.userId} profile={profile} saveProfile={saveProfile} setPublicMode={setPublicMode} sessionMode={sessionMode} signOut={signOut} notify={setToast} accountManaged={serverAccount} accountSecurity={accountRepositoryRuntime?.identity} assuranceLevel={authSession?.assuranceLevel || "aal1"} onAssuranceChanged={(level) => setAuthSession((current) => current ? { ...current, assuranceLevel: level } : current)} interfaceLanguage={interfaceLanguage} setInterfaceLanguage={setInterfaceLanguage} navigationPreferences={navigationPreferences} saveNavigationPreferences={saveNavigationSettings} />,
    account: <AccountView notificationDevices={serverAccount ? accountRepositoryRuntime.notificationDevices : null} accountUserId={authSession?.userId} profile={profile} saveProfile={saveProfile} setPublicMode={setPublicMode} sessionMode={sessionMode} signOut={signOut} notify={setToast} accountManaged={serverAccount} accountSecurity={accountRepositoryRuntime?.identity} assuranceLevel={authSession?.assuranceLevel || "aal1"} onAssuranceChanged={(level) => setAuthSession((current) => current ? { ...current, assuranceLevel: level } : current)} interfaceLanguage={interfaceLanguage} setInterfaceLanguage={setInterfaceLanguage} navigationPreferences={navigationPreferences} saveNavigationPreferences={saveNavigationSettings} />,
    search: <SearchView close={() => navigate(workspaceMode === "admin" ? "admin" : "overview")} navigate={navigate} openCreate={startCreate} openInvoice={openEditor} openClient={openClientFromSearch} openBusiness={serverAccount || canManage ? openBusinessFromSearch : null} openDocument={availableOperationItems.some((item) => item.id === "intake") ? openDocumentFromSearch : null} openService={openServiceFromSearch} exportReport={publicMode ? null : () => handleInvoiceRegisterExport()} invoices={surfaceInvoices} businesses={surfaceBusinesses} clients={surfaceClients} documents={surfaceIntakeItems} services={surfaceServices} searchPage={serverAccount ? searchAccountPage : null} openWork={openWorkFromSearch} searchState={searchState} setSearchState={setSearchState} />,
    notifications: <NotificationsView close={() => navigate(workspaceMode === "admin" ? "admin" : "overview")} navigate={navigate} openInvoice={openEditor} resolved={resolvedNotifications} markPaid={(invoiceId) => updateInvoiceStatus(invoiceId, "Paid")} prepareReminder={prepareInvoiceReminder} notify={setToast} invoices={surfaceInvoices} accountActions={serverAccount ? accountNotificationActions : null} actionPage={serverAccount ? actionPage : null} communications={pendingCommunications} workReminders={surfaceWorkReminders} businesses={surfaceBusinesses} openCommunication={openCommunicationFollowUp} completeCommunication={serverAccount ? completeCommunicationFollowup : null} />,
    more: <MobileMoreView navigate={navigate} openBusinessContacts={serverAccount ? () => setBusinessContactsOpen(true) : null} navigateWork={navigateWork} navigateAdmin={navigateAdmin} profile={profile} sessionMode={sessionMode} signOut={signOut} switchAccountWorkspace={switchAccountWorkspace} workspaceMode={workspaceMode} switchWorkspaceMode={switchWorkspaceMode} canManage={canManage} businesses={organizationBusinesses} businessPage={serverAccount ? businessPage : null} businessScopeId={businessScopeId} setBusinessScopeId={changeBusinessScope} operationItems={availableOperationItems.map((item) => ({ ...item, detail: item.id === "archive" ? "Search company folders and records" : item.id === "intake" ? "Upload and review source documents" : item.id === "purchases" ? "Vendor invoices, receipts, tax, and payments" : item.id === "banking" ? "Statements, matches, and evidence" : item.id === "ledger" ? "Statements, journals, periods, and assets" : item.id === "declarations" ? "CD, WR, WM / CM, and TM / QL" : "Reviewed accounting export batches" }))} mobileExperience={mobileExperience} setMobileExperience={changeMobileExperience} navigationPreferences={navigationPreferences} routineWorkEnabled={serverAccount} />,
  }[activeView];

  return (
    <UiLanguageProvider language={interfaceLanguage}>
    <PrivacyProvider enabled={publicMode} clients={surfaceClients} invoices={surfaceInvoices}>
    <div className={`is2-app is-${workspaceMode}-workspace${publicMode ? " is-public-mode" : ""}${mobileViewport && mobileExperience === "minimal" ? " is-mobile-minimal" : ""}`} data-cy="invoice-studio-app" data-session-mode={sessionMode} data-workspace-mode={workspaceMode} data-mobile-experience={mobileViewport ? mobileExperience : "desktop"}>
      <aside className="is2-sidebar">
        <button className="is2-sidebar-brand-home" data-cy="brand-home" aria-label="Go to overview" onClick={() => workspaceMode === "admin" ? navigateAdmin("overview") : navigate("overview")}><Brand /></button>
        <button className="is2-workspace-switcher" data-cy="workspace-switcher" onClick={() => canManage ? setOrganizationOpen(true) : navigate("account")}>{organization.logo ? <img src={organization.logo} alt="" className="is2-workspace-mark" /> : <span className="is2-workspace-mark is2-workspace-mark--fallback">{organization.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2)}</span>}<span><strong>{organization.name}</strong><small>{organizationType}</small></span><ChevronRightIcon size={14} /></button>
        {workspaceMode === "regular" && organizationBusinesses.length > 1 ? <><BusinessScopeSelect businesses={organizationBusinesses} value={businessScopeId} onChange={changeBusinessScope} disabled={editorOpen} /><BusinessDirectoryMore page={serverAccount ? businessPage : null} /></> : null}
        {serverAccount && workspaceMode === "regular" ? <button className="is2-business-contacts-entry" disabled={editorOpen} onClick={() => setBusinessContactsOpen(true)}><PeopleIcon size={16} />Business contacts</button> : null}
        <nav className="is2-nav" aria-label="Main navigation">
          {(workspaceMode === "admin" ? ADMIN_NAV_ITEMS : regularNavigationItems).map((item) => {
            const Icon = item.icon;
            const operationalDestination = Boolean(item.section);
            const accountingToolsDestination = item.id === ACCOUNTING_TOOLS_NAV_ITEM.id;
            const destinationGroup = item.destinationGroup ? availableDestinationGroups.find((group) => group.id === item.destinationGroup) : null;
            const directWorkDestination = workspaceMode === "regular" && Boolean(item.workSection);
            const workDestination = workspaceMode === "regular" && item.id === WORK_NAV_ITEM.id && !directWorkDestination;
            const workspaceDestination = workspaceMode === "regular" && item.id === WORKSPACE_NAV_ITEM.id;
            const groupedDestination = accountingToolsDestination || Boolean(destinationGroup) || workDestination || workspaceDestination;
            const active = operationalDestination
              ? activeView === "admin" && adminSection === item.section
              : accountingToolsDestination
                ? Boolean(activeOperationItem) && !editorOpen
                : destinationGroup
                  ? destinationGroup.items.some((child) => child.id === activeView) && !editorOpen
                : workDestination
                  ? activeView === "work" && !editorOpen
                  : directWorkDestination
                    ? activeView === "work" && workSection === item.workSection && !editorOpen
                  : workspaceDestination
                    ? WORKSPACE_NAV_ITEMS.some((child) => child.id === activeView) && !editorOpen
                : activeView === item.id && !editorOpen;
            const destination = accountingToolsDestination
              ? activeOperationItem?.id || availableOperationItems[0]?.id
              : destinationGroup
                ? destinationGroup.items.find((child) => child.id === activeView)?.id || destinationGroup.items[0]?.id
                : item.id;
            if (groupedDestination) {
              const groupKey = destinationGroup ? `destination-${destinationGroup.id}` : accountingToolsDestination ? "accounting" : workDestination ? "work" : "workspace";
              const groupItems = destinationGroup?.items || (accountingToolsDestination ? availableOperationItems : workDestination ? enabledWorkNavItems : enabledWorkspaceNavItems);
              const expanded = expandedNavGroups[groupKey];
              const openDefault = () => {
                if (workDestination) navigateWork(workSection);
                else if (workspaceDestination) navigate(WORKSPACE_NAV_ITEMS.some((child) => child.id === activeView) ? activeView : WORKSPACE_NAV_ITEMS[0].id);
                else if (destination) navigate(destination);
                setExpandedNavGroups((current) => ({ ...current, [groupKey]: true }));
              };
              return <div className="is2-nav-group" key={item.id}>
                <div className={`is2-nav-group-row${active ? " is-active" : ""}${expanded ? " is-expanded" : ""}`}>
                  <button data-cy={`nav-${item.id}`} className={`is2-nav-parent${active ? " is-active" : ""}`} onClick={openDefault}><Icon size={17} /><span>{t(item.label)}</span></button>
                  <button type="button" className={`is2-nav-group-toggle${expanded ? " is-expanded" : ""}${active ? " is-active" : ""}`} data-cy={`nav-${item.id}-toggle`} aria-label={`${expanded ? "Collapse" : "Expand"} ${item.label}`} aria-expanded={expanded} onClick={() => toggleNavGroup(groupKey)}><ChevronDownIcon size={13} /></button>
                </div>
                {expanded ? <div className="is2-nav-submenu" aria-label={item.label}>
                  {groupItems.map((child) => { const ChildIcon = child.icon; const childActive = workDestination ? activeView === "work" && workSection === child.id && !editorOpen : activeView === child.id && !editorOpen; return <button key={child.id} data-cy={`nav-${workDestination ? `work-${child.id}` : child.id}`} className={childActive ? "is-active" : ""} onClick={() => workDestination ? navigateWork(child.id) : navigate(child.id)}><ChildIcon size={14} /><span>{t(child.tabLabel || child.label)}</span></button>; })}
                </div> : null}
              </div>;
            }
            return <button key={`${item.id}-${item.workSection || "destination"}`} data-cy={`nav-${item.id}`} className={active ? "is-active" : ""} onClick={() => operationalDestination ? navigateAdmin(item.section) : directWorkDestination ? navigateWork(item.workSection) : destination && navigate(destination)}><Icon size={17} /><span>{t(item.label)}</span>{item.id === "invoices" && !serverAccount ? <b>{publicMode ? "••" : dailyInvoices.length}</b> : null}</button>;
          })}
        </nav>
        <div className="is2-sidebar__bottom">{canManage ? <button className={`is2-workspace-mode-switch is-${workspaceMode}`} onClick={() => switchWorkspaceMode(workspaceMode === "admin" ? "regular" : "admin")} data-cy="workspace-mode-switch" aria-label={workspaceMode === "admin" ? "Return to daily work" : "Switch to administration"} title={workspaceMode === "admin" ? "Return to daily work" : "Switch to administration"}><span><SwapHorizontalIcon size={16} /></span><strong>{t(workspaceMode === "admin" ? "Daily work" : "Administration")}</strong></button> : null}<button data-cy="nav-settings" className={activeView === "settings" ? "is-active" : ""} onClick={() => { setSettingsSection(SETTINGS_ITEMS[0][0]); navigate("settings"); }}><SettingsIcon size={17} /><span>{t("Settings")}</span></button><button data-cy="nav-account" className={`is2-user-trigger${activeView === "account" ? " is-active" : ""}`} onClick={() => navigate("account")}><ProfileAvatar profile={profile} /><span><strong>{profile.name}</strong><small>{PUBLIC_GUEST_MODE && sampleWorkspace ? t("Guest reviewer") : profile.role}</small></span><ChevronRightIcon size={13} /></button>{PUBLIC_GUEST_MODE ? null : <a href="?legacy=1">{t("Open current version")}</a>}<span className="is2-product-credit">Invoice Studio by <a href="https://coetic-x.com" target="_blank" rel="noreferrer">Coetic X</a></span></div>
      </aside>

      <main className="is2-main">
        <header className="is2-topbar">
          <button className="is2-topbar__mobile-brand is2-topbar__mobile-workspace" data-cy="mobile-brand-home" aria-label="Go to overview" onClick={() => workspaceMode === "admin" ? navigateAdmin("overview") : navigate("overview")}><Brand compact /></button>
          <span className="is2-topbar__context">{workspaceMode === "admin" ? <em data-cy="admin-context"><ShieldIcon size={11} /> Admin</em> : null}<span>{t(editorOpen ? selectedDocument?.status === "Draft" ? "Invoice editor" : "Invoice record" : activeLabel)}</span></span>
          <div className="is2-topbar__actions">
            {publicMode ? <button className="is2-public-mode-pill" onClick={() => setPublicMode(false)} title="Turn off public mode"><EyeOffIcon size={14} /><span>{t("Public mode")}</span></button> : null}
            <IconButton label="Search" data-cy="topbar-search" onClick={openSearchControl}><SearchIcon size={17} /></IconButton>
            <IconButton label="Notifications" className="is2-notification" data-cy="topbar-notifications" onClick={openNotificationsControl}><NotificationsIcon size={17} />{hasNotificationActions ? <i /> : null}</IconButton>
            <button className="is2-topbar-profile" data-cy="topbar-account" aria-label={`Open ${profile.name || "account"} profile`} title={profile.name || "Open account"} onClick={openAccountControl}><ProfileAvatar profile={profile} className="is2-topbar-profile__avatar" /></button>
            <button className="is2-button is2-button--primary is2-topbar__create" data-cy="new-invoice" aria-disabled={!canManage && organizationBusinesses.length === 0 ? "true" : undefined} aria-label={t(!canManage && organizationBusinesses.length === 0 ? "New invoice unavailable. Ask an administrator to assign you an issuing business." : "New invoice")} title={t(!canManage && organizationBusinesses.length === 0 ? "Ask an administrator to assign you an issuing business" : "New invoice")} onClick={startCreate}><PlusIcon size={16} /><span>{t("New invoice")}</span></button>
          </div>
        </header>
        {searchOpen ? <SearchPanel close={() => setSearchOpen(false)} navigate={navigate} openCreate={startCreate} openInvoice={openEditor} openClient={openClientFromSearch} openBusiness={serverAccount || canManage ? openBusinessFromSearch : null} openDocument={availableOperationItems.some((item) => item.id === "intake") ? openDocumentFromSearch : null} openService={openServiceFromSearch} exportReport={publicMode ? null : () => handleInvoiceRegisterExport()} invoices={surfaceInvoices} businesses={surfaceBusinesses} clients={surfaceClients} documents={surfaceIntakeItems} services={surfaceServices} searchPage={serverAccount ? searchAccountPage : null} openWork={openWorkFromSearch} searchState={searchState} setSearchState={setSearchState} /> : null}
        {notificationsOpen ? <NotificationPanel close={() => setNotificationsOpen(false)} navigate={navigate} openInvoice={openEditor} resolved={resolvedNotifications} markPaid={(invoiceId) => updateInvoiceStatus(invoiceId, "Paid")} prepareReminder={prepareInvoiceReminder} notify={setToast} invoices={surfaceInvoices} accountActions={serverAccount ? accountNotificationActions : null} actionPage={serverAccount ? actionPage : null} communications={pendingCommunications} workReminders={surfaceWorkReminders} businesses={surfaceBusinesses} openCommunication={openCommunicationFollowUp} completeCommunication={serverAccount ? completeCommunicationFollowup : null} /> : null}
        <div className="is2-content">
          {sessionMode === "local" ? <LocalSnapshotSaveStatus status={localPersistence.status} retry={localPersistence.flush} exportBackup={() => handleExportData("json")} canExport={canManage && !publicMode} masked={publicMode} reload={localPersistence.discardAndReload} /> : null}
{serverAccount && accountRepositoryRuntime?.billingRecovery ? <React.Suspense fallback={null}><AccountBillingRecovery key={accountScope} controller={accountRepositoryRuntime.billingRecovery} publicMode={publicMode} businesses={[...accountAdminBusinesses, ...organizationBusinesses]} invoices={accountInvoices} runCommand={(command, original) => runAccountCommand(command, {
  successMessage: "Original change confirmed",
  onSuccess: result => {
    if (/^\/v1\/organizations\/[^/]+\/(?:identity|document-defaults)$/.test(original?.path || '')) {
      setOrganizationOpen(false);
      setSettingsEditTarget({ organizationId: organization.id, section: original.path.split('/')[4], epoch: Date.now() });
      navigate('settings');
    } else if (/^\/v1\/organizations\/[^/]+\/invitations(?:\/|$)/.test(original?.path || '')) {
      openAccountInvitation(result.data.invitationId);
    } else if (original?.memberSnapshot) {
      setFocusedAdminInvitation(null);
      setFocusedAdminMemberId(original.memberSnapshot.userId);
      setAccountAdminMemberDetailRefreshKey(current => current + 1);
      navigateAdmin('team');
    } else if (original?.businessContactSnapshot) {
      setIssuerEditor(null);
      setFocusedBusinessId(original.businessContactSnapshot.businessId);
      setAccountAdminBusinessDetailRefreshKey(current => current + 1);
      navigateAdmin('businesses');
    } else if (/^\/v1\/organizations\/[^/]+\/businesses(?:\/|$)/.test(original?.path || "")) {
      setIssuerEditor(null);
      navigateAdmin("businesses");
    } else if (original?.path.startsWith('/v1/business-portal/publications')) {
      setEditorOpen(false);
      setPortalPublicationTarget({ businessId: original.body.businessId, id: result.data.id, epoch: Date.now() });
      navigate('reports');
    } else if (original?.payrollPosting) {
      setEditorOpen(false);
      if (accountBusinessId !== original.payrollPosting.businessId) changeBusinessScope(original.payrollPosting.businessId);
      setPayrollLedgerEpoch(value => value + 1);
      setPayrollLedgerTarget({ businessId: original.payrollPosting.businessId, period: original.payrollPosting.period });
      navigate("declarations");
    } else if (original?.path.startsWith("/v1/payroll/employees")) {
      setEditorOpen(false);
      if (accountBusinessId && accountBusinessId !== original.body.businessId) changeBusinessScope(original.body.businessId);
      navigate("declarations");
      void payrollEmployeePage.reload();
    } else if (original?.path.startsWith("/v1/declarations")) {
      const mapped = accountPageAdapters.mapAccountDeclarationDraft(result.data);
      const key = declarationScopeKey(mapped.scope);
      const timer = declarationSaveTimersRef.current.get(key);
      if (timer) window.clearTimeout(timer);
      declarationSaveTimersRef.current.delete(key);
      // Keep newer unsaved inputs; attach the confirmed server identity and revision.
      const records = declarationDraftsRef.current;
      const existing = records.find(item => declarationScopeKey(item.scope) === key);
      const next = existing && JSON.stringify(existing.inputs) !== JSON.stringify(mapped.inputs)
        ? { ...existing, id: mapped.id, revision: mapped.revision, updatedAt: mapped.updatedAt, status: "draft" } : mapped;
      declarationDraftsRef.current = [...records.filter(item => declarationScopeKey(item.scope) !== key), next];
      setDeclarationDrafts(declarationDraftsRef.current);
      navigate("declarations");
    } else if (original?.path.startsWith("/v1/accounting-line-assignments/")) {
      setEditorOpen(false);
      setQuickBooksBusinessId(original.body.businessId);
      navigate("quickbooks");
      void quickBooksCandidatePage.reload();
    } else if (original?.path.startsWith("/v1/purchases/vendors")) {
      setEditorOpen(false);
      if (accountBusinessId && accountBusinessId !== result.data.vendor.business_id) changeBusinessScope(result.data.vendor.business_id);
      setFocusedPurchaseVendor({ id: result.data.vendor.id, revision: result.data.vendor.revision });
      setActiveView("purchases");
    } else if (original?.intakeProcessing) {
      setEditorOpen(false);
      openDocumentIntake(original.intakeProcessing.id);
      void intakePage.reload();
    } else if (original?.path.startsWith("/v1/services")) {
      setServiceEditor(null); setActiveView("services");
    } else if (original?.path.startsWith("/v1/clients")) {
      setClientEditor(null); setActiveView("clients");
    } else if (original?.path.startsWith("/v1/banking/")) {
      setEditorOpen(false);
      if (original.bankStatement) openImportedBankStatement(result.data);
      else navigate("banking");
    } else if (original?.purchaseIntake || original?.purchase || original?.purchaseSave || original?.purchaseArchive) {
      setEditorOpen(false);
      if (accountBusinessId && accountBusinessId !== result.data.purchaseDocument.business_id) changeBusinessScope(result.data.purchaseDocument.business_id);
      openPurchaseRecord(result.data.purchaseDocument.id);
    } else if (!original?.path.startsWith("/v1/work/")) {
      setEditorOpen(false); setActiveView("invoices");
    }
  },
})} /></React.Suspense> : null}
          {PUBLIC_GUEST_MODE && sampleWorkspace && activeView === "overview" && !editorOpen ? <section className="is2-sample-workspace is-guest" role="status"><span><strong>Sample workspace</strong><small>These records are fictional. Your personal local workspace remains separate.</small></span></section> : null}
          {activeView === "work" && workSection !== "calendar" && availableOperationItems.length && !editorOpen ? <nav className="is2-operation-tabs is2-operation-tabs--daily" aria-label={t("Accounting tools")} data-cy="work-accounting-tools"><span><ArchiveIcon size={14} /> {t("Accounting tools")}</span><div>{availableOperationItems.map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`work-accounting-${item.id}`} onClick={() => navigate(item.id)}><Icon size={14} /><span>{t(item.tabLabel)}</span></button>; })}</div></nav> : null}
          {activeOperationItem && activeDestinationGroup?.items.length > 1 && !editorOpen ? <nav className="is2-operation-tabs" aria-label={t(activeDestinationGroup.label)} data-cy="accounting-tools-tabs"><span><ArchiveIcon size={14} /> {t(activeDestinationGroup.label)}</span><div ref={operationTabsScrollerRef}>{activeDestinationGroup.items.map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`accounting-tab-${item.id}`} className={activeView === item.id ? "is-active" : ""} aria-current={activeView === item.id ? "page" : undefined} onClick={() => navigate(item.id)}><Icon size={14} /><span>{t(item.tabLabel || item.label)}</span></button>; })}</div></nav> : null}
          {content}
        </div>
      </main>

      <nav className="is2-mobile-nav" aria-label="Mobile navigation">
        {workspaceMode === "admin" ? <>
          {ADMIN_NAV_ITEMS.filter((item) => ["overview", "businesses", "team", "history"].includes(item.section)).map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`mobile-nav-${item.id}`} className={activeView === "admin" && adminSection === item.section && !editorOpen ? "is-active" : ""} onClick={() => navigateAdmin(item.section)}><Icon size={19} /><span>{t(item.mobileLabel)}</span></button>; })}
          <button data-cy="mobile-nav-more" className={activeView === "more" && !editorOpen ? "is-active" : ""} onClick={() => navigate("more")}><MoreIcon size={20} /><span>{t("More")}</span></button>
        </> : <>
          {NAV_ITEMS.filter((item) => mobileLeadingIds.includes(item.id)).map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`mobile-nav-${item.id}`} className={activeView === item.id && !editorOpen ? "is-active" : ""} onClick={() => item.id === "work" ? navigateWork(workSection) : navigate(item.id)}><Icon size={19} /><span>{t(item.label)}</span></button>; })}
          <button className="is2-mobile-nav__create" data-cy="mobile-new-invoice" onClick={startCreate} aria-disabled={!canManage && organizationBusinesses.length === 0 ? "true" : undefined} aria-label={t(!canManage && organizationBusinesses.length === 0 ? "New invoice unavailable. Ask an administrator to assign you an issuing business." : "Create invoice")} title={t(!canManage && organizationBusinesses.length === 0 ? "Ask an administrator to assign you an issuing business" : "Create invoice")}><PlusIcon size={22} /></button>
          {NAV_ITEMS.filter((item) => item.id === mobileTrailingId).map((item) => { const Icon = item.icon; return <button key={item.id} data-cy={`mobile-nav-${item.id}`} className={activeView === item.id && !editorOpen ? "is-active" : ""} onClick={() => navigate(item.id)}><Icon size={19} /><span>{t(item.label)}</span></button>; })}
          <button data-cy="mobile-nav-more" className={activeView === "more" && !editorOpen ? "is-active" : ""} onClick={() => navigate("more")}><MoreIcon size={20} /><span>{t("More")}</span></button>
        </>}
      </nav>

      {createOpen ? <React.Suspense fallback={<LazyOverlayFallback title="Preparing invoice assistant" />}><InvoiceAssistant close={() => { setCreateOpen(false); setCreateSeed({}); }} continueToEditor={openEditor} onAddClient={addClientFromInvoice} businesses={dailyBusinesses} clients={accountClients} services={accountServices.filter((service) => !service.archived)} loadBusinesses={serverAccount ? loadAccountBusinesses : null} loadBusiness={serverAccount ? loadAccountBusiness : null} loadClients={serverAccount ? loadInvoiceClientOptions : null} loadClient={serverAccount ? loadInvoiceClientOption : null} loadServices={serverAccount ? loadInvoiceServices : null} loadService={serverAccount ? loadAccountService : null} getNextInvoiceNumber={getNextInvoiceNumber} initialClientId={createSeed.clientId} initialIssuerId={createSeed.issuerId} initialServiceId={createSeed.serviceId} /></React.Suspense> : null}
      {organizationOpen && canManage ? <OrganizationPanel key={serverAccount ? accountScope : organization.id} management={organizationSettingsManagement} close={() => setOrganizationOpen(false)} openIssuers={() => { setSettingsSection("Issuing businesses"); navigate("settings"); }} organization={organization} saveOrganization={saveOrganization} notify={setToast} /> : null}
      {businessContactsOpen && serverAccount && workspaceMode === "regular" ? <React.Suspense fallback={<LazyOverlayFallback title="Loading business contacts" />}><BusinessContacts key={`${organization.id}:${businessContactTarget}`} close={() => { setBusinessContactsOpen(false); setBusinessContactTarget(null); }} organizationId={organization.id} initialBusinessId={businessContactTarget || accountBusinessId} businesses={organizationBusinesses} loadBusinesses={loadAccountBusinesses} loadBusiness={loadAccountBusiness} loadContacts={accountRepositoryRuntime.reads.businesses.contacts} /></React.Suspense> : null}
      {issuerEditor?.loading ? <LazyOverlayFallback title="Loading complete business details" /> : issuerEditor ? <IssuerPanel brandRepository={serverAccount ? accountRepositoryRuntime.brandUploads : null} onLogoUpdated={syncReviewedLogo} close={() => setIssuerEditor(null)} business={issuerEditor.business} organization={organization} saveBusiness={saveBusiness} reloadBusiness={serverAccount ? loadBusinessEditor : null} getNextInvoiceNumber={getNextInvoiceNumber} notify={setToast} context={issuerEditor.context} /> : null}
      {clientEditor?.loading ? <LazyOverlayFallback title="Loading complete client details" /> : clientEditor ? <React.Suspense fallback={<LazyOverlayFallback title="Opening client details" />}><ClientPanel close={() => { const returnToCreate = clientEditor.returnToCreate; setClientEditor(null); if (returnToCreate) setCreateOpen(true); }} client={clientEditor.client} mode={clientEditor.mode} businesses={organizationBusinesses} loadBusinesses={serverAccount ? loadAccountBusinesses : null} loadBusiness={serverAccount ? loadAccountBusiness : null} saveClient={saveClient} /></React.Suspense> : null}
      {serviceEditor !== null ? <React.Suspense fallback={<LazyOverlayFallback title="Loading service" />}><ServicePanel readOnly={publicMode || Boolean(serverAccount && serviceEditor.id && !serviceEditor.capabilities?.canEdit)} key={`${serviceEditor.id || "new"}:${serviceEditor.revision || 0}`} close={() => setServiceEditor(null)} service={serviceEditor || null} businesses={organizationBusinesses} loadBusinesses={serverAccount ? loadAccountBusinesses : null} loadBusiness={serverAccount ? loadAccountBusiness : null} saveService={saveServiceRecord} reload={serverAccount ? openServiceEditor : null} canChangeScope={!serverAccount || (serviceEditor.id ? serviceEditor.capabilities?.canChangeScope === true : canManage)} /></React.Suspense> : null}
      {whatsappRequest ? <WhatsAppComposer key={whatsappRequest.id} request={whatsappRequest} close={() => setWhatsappRequest(null)} onComplete={completeWhatsAppHandoff} /> : null}
      {userOpen ? <UserMenu close={() => setUserOpen(false)} navigate={navigate} profile={profile} setPublicMode={setPublicMode} sessionMode={sessionMode} signOut={signOut} switchAccountWorkspace={switchAccountWorkspace} /> : null}
      {toast ? <div className="is2-toast" role="status"><TickCircleIcon size={17} /> {toast}</div> : null}
    </div>
    </PrivacyProvider>
    </UiLanguageProvider>
  );
};

export default InvoiceStudioRevamp;
